// strings.js — Единый центр всех текстов приложения INMONEX
// ══════════════════════════════════════════════════════════
// Все UI-тексты, подсказки, ошибки и лейблы собраны здесь.
// Для мультиязычности: добавь kz и en объекты по аналогии с ru.
// Использование в компонентах: import { S } from './strings';
//   затем в JSX: S().home.title, S().ops.income, S().help.contractor и т.д.
// ══════════════════════════════════════════════════════════

// --- Текущий язык (по умолчанию русский) ---
let currentLang = 'ru';

// --- Переключение языка ---
export const setLang = (lang) => { currentLang = lang; };
export const getLang = () => currentLang;

// --- Главная функция доступа к строкам ---
export const S = () => STRINGS[currentLang] || STRINGS.ru;

// ══════════════════════════════════════════════════════════
// РУССКИЙ ЯЗЫК
// ══════════════════════════════════════════════════════════
const ru = {

  // ─── Общие (используются в нескольких файлах) ───
  common: {
    save: 'Сохранить',
    cancel: 'Отмена',
    confirm: 'Подтвердить',
    delete: 'Удалить',
    edit: 'Редактировать',
    close: 'Закрыть',
    toArchive: 'В архив',
    add: 'Добавить',
    back: 'Назад',
    next: 'Далее',
    search: 'Поиск...',
    noData: 'Нет данных',
    loading: 'Загрузка...',
    yes: 'Да',
    no: 'Нет',
    all: 'Всё',
    archive: 'Архив',
    noRights: 'Нет прав',
    check: 'Проверить',
    show: 'Показать',
    hide: 'Скрыть',
    reset: 'Сбросить',
    download: 'Скачать',
    noCategoryShort: 'Без категории',
    noComment: 'Без комментария',
    noDescription: 'Без описания',
    unknown: 'Неизвестный',
    notSpecified: 'Не указан',
    notSpecifiedFem: 'Не указана',
    defaultUser: 'Пользователь',
    system: 'Система',
    saving: 'Сохранение...',
    genericError: 'Ошибка',
    deleteError: 'Ошибка при удалении',
    pdfUploadError: 'Ошибка загрузки PDF',
    unknownError: 'неизвестная ошибка',
  },

  // ─── Названия фондов по умолчанию (SetupWizard) ───
  defaultFunds: {
    salary: 'Зарплата',
    taxes: 'Налоги',
    profit: 'Прибыль',
  },

  // ─── Роли пользователей ───
  roles: {
    owner: 'Владелец',
    admin: 'Администратор',
    accountant: 'Бухгалтер',
    viewer: 'Просмотр',
  },

  // ─── Названия месяцев ───
  months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  monthsShort: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],

  // ─── Периоды фильтрации ───
  periods: {
    day: 'День',
    week: 'Неделя',
    month: 'Месяц',
    year: 'Год',
    twoYears: '2 года',
    all: 'Всё',
  },

  // ─── Дни недели ───
  weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],

  // ─── Валюты (лейблы) ───
  currencies: {
    KZT: 'Тенге (KZT)',
    RUB: 'Рубли (RUB)',
    USD: 'Доллары США (USD)',
    EUR: 'Евро (EUR)',
    CNY: 'Китайские юани (CNY)',
    AED: 'Дирхамы ОАЭ (AED)',
    GBP: 'Британские фунты (GBP)',
    TRY: 'Турецкие лиры (TRY)',
    UZS: 'Узбекский сум (UZS)',
    KGS: 'Киргизский сом (KGS)',
    TJS: 'Таджикский сомони (TJS)',
    BYN: 'Белорусский рубль (BYN)',
    UAH: 'Украинская гривна (UAH)',
    GEL: 'Грузинский лари (GEL)',
    AZN: 'Азербайджанский манат (AZN)',
    AMD: 'Армянский драм (AMD)',
    MDL: 'Молдавский лей (MDL)',
    TMT: 'Туркменский манат (TMT)',
  },

  // ─── Типы транзакций ───
  txTypes: {
    income: 'Поступление',
    expense: 'Расход',
    transfer: 'Перевод',
    transferFund: 'Перевод фонда',
    transferAccount: 'Перевод между счетами',
    transferFundFull: 'Перевод между фондами',
  },

  // ─── HomeScreen (App.js) ───
  home: {
    brand: 'INMONEX',
    subtitle: 'Контроль денег. Свобода решений',
    yourOrgs: 'Ваши организации:',
    createOrg: 'Создать организацию',
    light: 'Светлая',
    dark: 'Тёмная',
    deleteOrgConfirm: 'Удалить организацию и все данные?',
    loginTitle: 'Вход в систему',
    forgotPassword: 'Забыли пароль?',
    registerLink: 'Регистрация',
    addOrg: 'Создать организацию',
    passwordHint: 'Подсказка по аккаунтам:',
    contactOwner: 'обратитесь к владельцу организации',
    noUsersYet: 'Нет зарегистрированных пользователей.',
    createFirstOrg: 'Создайте первую организацию для начала работы',
    loginHint: (login, len) => `Логин: ${login} · Подсказка: пароль из ${len} символов`,
    cantRememberPassword: 'Если вы не можете вспомнить пароль, обратитесь к владельцу организации.',
  },

  // ─── Экран выбора организации ───
  orgSelect: {
    welcome: (name) => `Добро пожаловать, ${name}`,
    choose: 'Выберите организацию',
    create: 'Создать организацию',
    noOrgs: 'У вас пока нет доступа к организациям. Обратитесь к владельцу организации для получения доступа.',
  },

  // ─── LoginScreen (App.js) ───
  login: {
    loginPlaceholder: 'Логин или email',
    passwordPlaceholder: 'Пароль',
    enter: 'Войти',
    wrongCredentials: 'Неверный логин или пароль',
    // Stage 11: второй экран логина для юзеров с включённой 2FA
    twoFA: {
      title: 'Введите код из приложения',
      desc: 'Откройте Google Authenticator (или другое TOTP-приложение) и введите 6-значный код. Если вы потеряли доступ к приложению — используйте резервный код вида XXXX-XXXX.',
      codePlaceholder: '6 цифр или XXXX-XXXX',
      verifyBtn: 'Войти',
      verifying: 'Проверка...',
      backToLogin: '← Назад к логину',
      expired: 'Сессия 2FA истекла. Войдите заново.',
      invalidCode: 'Неверный код',
      backupCodesLow: (n) => `Внимание: осталось всего ${n} резервных кодов. Сгенерируйте новые в настройках безопасности.`,
      usedBackup: 'Вы вошли с помощью резервного кода. Один код потрачен.',
    },
  },

  // ─── Модалка "Забыли пароль" ───
  forgotPwd: {
    title: 'Забыли пароль?',
    desc: 'Введите email, указанный при регистрации. Мы отправим на него ссылку для сброса пароля.',
    noEmailHint: 'Если у вас нет email или нет к нему доступа — обратитесь к владельцу компании. Он может сбросить ваш пароль вручную.',
    emailPh: 'email@example.com',
    sendBtn: 'Отправить ссылку',
    sentMsg: 'Если этот email зарегистрирован, мы отправили на него ссылку для сброса пароля. Проверьте почту (в том числе папку Спам). Ссылка действительна 1 час.',
    close: 'Закрыть',
    failed: 'Не удалось отправить запрос. Попробуйте снова.',
  },

  // ─── Страница сброса пароля (ResetPassword.js) ───
  resetPwd: {
    logoSub: 'Контроль денег. Свобода решений',
    title: 'Создание нового пароля',
    subtitle: 'Придумайте надёжный пароль для вашего аккаунта',
    newPwd: 'Новый пароль',
    newPwdPh: 'Минимум 8 символов',
    confirmPwd: 'Повторите новый пароль',
    confirmPwdPh: 'Введите пароль ещё раз',
    pwdTooShort: 'Пароль должен быть не менее 8 символов',
    pwdMismatch: 'Пароли не совпадают',
    save: 'Сохранить',
    saving: 'Сохранение...',
    successMsg: 'Пароль успешно изменён! Сейчас откроется страница входа...',
    noToken: 'Токен сброса пароля не найден. Запросите новую ссылку.',
    failed: 'Не удалось сменить пароль. Попробуйте снова.',
  },

  // ─── Смена email с OTP (в настройках профиля) ───
  emailChange: {
    title: 'Смена email',
    step1Desc: 'Введите новый email. Мы отправим на него код подтверждения.',
    newEmailLabel: 'Новый email',
    newEmailPh: 'new@example.com',
    sendCode: 'Отправить код',
    step2Desc: 'Мы отправили 6-значный код на новый адрес. Введите его ниже.',
    codeLabel: 'Код из письма',
    codePh: '123456',
    confirm: 'Подтвердить',
    successMsg: 'Email успешно изменён',
    rateLimit: (s) => `Подождите ${s} сек.`,
    invalidCode: 'Неверный код',
    expired: 'Код истёк, запросите новый',
    changeBtn: 'Изменить',
    sameEmail: 'Новый email совпадает с текущим',
    sendError: 'Не удалось отправить код',
  },

  // ─── Phase 10: Optimistic locking (App.js) ───
  sync: {
    conflictTitle: 'Данные обновлены',
    conflictBody: 'Другой пользователь сохранил изменения раньше. Применили свежую версию — повторите ваше действие, если нужно.',
  },

  // ─── SetupWizard (App.js) ───
  setup: {
    title: 'НАСТРОЙКА INMONEX',
    stepOf: (step, total) => `Шаг ${step} из ${total}`,
    welcomeText: 'Добро пожаловать в INMONEX! Заполните основные данные о вашей компании и создайте аккаунт Владельца. Эти данные используются для безопасного входа и в отчетах.',
    orgColorLabel: 'Цвет организации',
    orgColorTaken: (label) => `${label} (занят)`,
    companyName: 'Название компании или проекта',
    companyPlaceholder: 'Например: Рога и Копыта ТОО',
    yourName: 'Ваше Имя',
    namePlaceholder: 'Иван Иванов',
    loginLabel: 'Логин для входа',
    loginPlaceholder: 'admin',
    passwordLabel: 'Придумайте надежный пароль',
    passwordMinLength: 'Минимум 8 символов',
    passwordsDoNotMatch: 'Пароли не совпадают',
    confirmPasswordLabel: 'Подтвердите пароль',
    confirmPasswordPlaceholder: 'Повторите пароль',
    emailLabel: 'Email (для чеков и восстановления)',
    emailPlaceholder: 'director@company.com',
    emailConfirmed: 'Подтверждена',
    confirmEmail: 'Подтвердить',
    codePlaceholder: 'Код из письма',
    wrongCode: 'Неверный код. Попробуйте ещё раз.',
    codeSent: 'Код отправлен на вашу почту',
    codeSendError: 'Не удалось отправить код',
    codeSending: 'Отправка...',
    verificationError: 'Ошибка проверки кода',
    phoneLabel: 'Телефон',
    phonePlaceholder: '77770001122',
    phoneMinDigits: 'Минимум 7 цифр',
    phoneHint: 'Только цифры, без пробелов и скобок. Пробелы система расставит сама.',
    accountLabel: (i) => `Счёт ${i}`,
    ibanLabel: 'IBAN счёта',
    ibanPlaceholder: 'KZ00 0000 0000 0000 0000',
    balanceLabel: 'Текущий баланс (необязательно)',
    balancePlaceholder: '0',
    balanceHint: 'Если укажете баланс — он автоматически распределится по фондам',
    addAccount: 'Добавить счёт',
    fundsWhy: 'Зачем нужны фонды?',
    fundsExplain: 'Фонды автоматически распределяют выручку: Зарплаты, Налоги, Дивиденды.',
    fundsSkipHint: 'Вы можете настроить их сейчас или удалить и пропустить этот шаг. INMONEX сам предложит включить их позже.',
    percentHint: (pct) => `Сумма процентов должна быть = 100%. Сейчас: ${pct}%`,
    fundsPctError: 'Сумма процентов всех фондов должна быть ровно 100%. Исправьте значения, чтобы продолжить.',
    duplicateAccountName: 'Названия счетов не должны повторяться',
    duplicateIban: 'IBAN счетов не должны повторяться',
    duplicateFundName: 'Названия фондов не должны повторяться',
    fundDistribution: (amount) => `Начальный баланс: ${amount} — распределение по фондам:`,
    fundNamePlaceholder: 'Название фонда',
    addFund: 'Добавить фонд',
    removeAllFunds: 'Удалить все и пропустить',
    skipFunds: 'Пропустить фонды',
    noFundsConfirm: 'Вы не добавили ни одного фонда. Списания будут идти напрямую со счетов. Продолжить?',
    ownerLabel: 'Владелец:',
    emailLabelShort: 'Email:',
    accountsCount: 'Счетов:',
    fundsCount: 'Фондов:',
    agreementText: 'Я прочитал(а) и принимаю условия',
    tosLink: 'Пользовательского соглашения',
    privacyLink: 'Политики конфиденциальности',
    consentLink: 'Согласие на сбор и обработку персональных данных',
    finishSetup: 'Завершить регистрацию',
    // ─── Расширенные описания шагов ───
    existingUserInfo: 'Вы уже авторизованы',
    step1Title: 'Ваша компания и аккаунт',
    step1Desc: 'Давайте начнем! Введите данные компании и создайте себе аккаунт руководителя. Позже вы сможете добавить других сотрудников с разными правами доступа.',
    step2Title: 'Ваши банковские счета',
    step2Desc: 'Счёт — это ваш реальный банковский счёт или касса. Например, Kaspi Бизнес, Halyk или наличные в кассе. Через них вы принимаете оплату от клиентов и оплачиваете расходы.',
    step2Examples: 'Kaspi Бизнес · Halyk · Forte · Наличные · Jýsan',
    step2Hint: 'Добавьте хотя бы один счёт. Позже в настройках вы сможете добавить ещё.',
    step3Title: 'Фонды — ваша финансовая подушка',
    step3Desc: 'Фонды — это «копилки» внутри системы. Каждое поступление автоматически делится между ними по заданным процентам. Это помогает заранее откладывать деньги на налоги, зарплаты и прибыль.',
    step3Example: 'Пример: клиент заплатил 1 000 000 ₸ → 500 000 ₸ уходит в фонд «Зарплата», 200 000 ₸ в «Налоги», 300 000 ₸ в «Прибыль».',
    step3FlowTitle: 'Как это работает?',
    flowClientPays: 'Клиент платит',
    flowYourAccount: 'Ваш счёт',
    flowFundsNotConfigured: 'Фонды не настроены',
    examplesTitle: 'Примеры названий:',
    accountExample: 'Например: Kaspi Бизнес',
    step4Title: 'Всё готово! Проверьте данные',
    step4Desc: 'После создания организации вы попадете в рабочую область, где сможете вести полный учёт финансов.',
    step4Next1: 'Добавите категории доходов и расходов',
    step4Next2: 'Создадите контрагентов (клиентов и поставщиков)',
    step4Next3: 'Начнёте фиксировать операции и получать отчёты',
    step4WhatsNext: 'Что дальше?',
    switchTo: (name) => `Переключить на: ${name}`,
  },

  // ─── Welcome Tour ───
  tour: {
    welcome: 'Добро пожаловать в INMONEX!',
    welcomeDesc: 'Давайте за 30 секунд покажем вам основные инструменты. Вы можете пропустить тур в любой момент.',
    step1Title: '💳 Счета и фонды',
    step1Desc: 'Здесь вы видите все свои счета и фонды. Нажмите «+» на счёте, чтобы быстро зафиксировать поступление денег. Деньги автоматически распределятся по фондам.',
    step2Title: '📚 Справочники',
    step2Desc: 'Тут живут ваши контрагенты (клиенты и поставщики) и категории расходов/доходов. Чем точнее вы их настроите, тем полезнее будут отчёты.',
    step3Title: '⚡ Операции',
    step3Desc: 'Главный инструмент! Фиксируйте поступления, расходы и переводы. Каждая операция автоматически отражается в отчётах и балансах.',
    step4Title: '📊 Отчёты',
    step4Desc: 'Здесь формируются ОПиУ и ДДС — два ключевых финансовых отчёта. Вы увидите, сколько зарабатываете и куда уходят деньги.',
    skipBtn: 'Пропустить',
    nextBtn: 'Далее',
    prevBtn: 'Назад',
    finishBtn: 'Начать работу!',
    stepOf: (step, total) => `${step} / ${total}`,
  },

  // ─── Sidebar / MainApp (App.js) ───
  sidebar: {
    tabs: {
      af: 'Главная',
      data: 'Структура',
      ops: 'Операции',
      debts: 'Обязательства',
      tx: 'Транзакции',
      reports: 'Отчеты',
      history: 'История',
      admin: 'Настройки',
      myaccount: 'Мой аккаунт',
      multiorg: 'Сводка',
      support: 'Поддержка',
      referral: 'Рефералы',
      subscription: 'Подписка',
    },
    cooperation: 'Сотрудничество',
    logout: 'Выйти',
    backToOrgs: '← Орг.',
    allOrgs: 'Все орг.',
    addOrg: 'Добавить организацию',
    menu: '☰ Меню',
    copyright: (year) => `© ${year} INMONEX`,
    allRightsReserved: 'Все права защищены.',
    tosLink: 'Пользовательское соглашение',
    privacyLink: 'Политика конфиденциальности',
    dataProcessingLink: 'Обработка данных',
    sync: {
      updated: 'Обновлено',
      justNow: 'только что',
      secondsAgo: 'N сек назад',
      minutesAgo: 'N мин назад',
      hoursAgo: 'N ч назад',
    },
  },

  // ─── Реферальная программа (ReferralTab.js) ───
  referral: {
    title: 'Реферальная программа',
    subtitle: 'Приглашайте друзей и получайте бонусы',
    myLink: 'Моя реферальная ссылка',
    copyLink: 'Копировать ссылку',
    copied: 'Скопировано!',
    myCode: 'Мой код',
    balance: 'Бонусный баланс',
    balanceUnit: '₸',
    howItWorks: 'Как это работает',
    rule1: 'Поделитесь своей ссылкой с друзьями',
    rule2: 'Друг регистрируется по ссылке и оплачивает подписку на 6+ месяцев',
    rule3: 'Вы получаете 10% от суммы его платежа на бонусный баланс',
    rule4: 'Бонусы автоматически применяются при вашей следующей оплате подписки',
    subUserNoticeTitle: 'Памятка сотруднику организации',
    subUserNoticeText: 'Если подписку INMONEX оплачивает ваш работодатель, вы можете передать накопленные бонусы владельцу организации. Он использует их при оплате подписки и сэкономит средства компании. По внутренней договорённости с работодателем вы вправе получить денежное вознаграждение за переданные бонусы — размер, форма и сроки такого вознаграждения определяются исключительно между вами и работодателем. INMONEX не является стороной подобного соглашения и не участвует в расчётах между работником и работодателем.',
    invitedTitle: 'Приглашённые друзья',
    invitedEmpty: 'Пока никто не зарегистрировался по вашей ссылке',
    invitedTotal: (n) => `Всего приглашено: ${n}`,
    invitedRewarded: (n) => `Получено бонусов: ${n}`,
    colDate: 'Дата',
    colLogin: 'Логин',
    colEmail: 'Email',
    colStatus: 'Статус',
    statusRewarded: '✓ Бонус начислен',
    statusPending: 'Ожидает оплаты',
    historyTitle: 'История бонусов',
    historyEmpty: 'История пуста',
    typeReferralReward: 'Реферальный бонус',
    typeTransferIn: 'Перевод получен',
    typeTransferOut: 'Перевод отправлен',
    typeSpend: 'Использован при оплате',
    typeAdminAdjust: 'Корректировка администратора',
    transferButton: 'Перевести бонусы',
    transferTitle: 'Перевод бонусов',
    transferHint: 'Найдите получателя по логину или email',
    transferSearch: 'Начните вводить логин или email (мин. 3 символа)',
    transferAmount: 'Сумма перевода',
    transferNote: 'Комментарий (необязательно)',
    transferSend: 'Отправить',
    transferCancel: 'Отмена',
    transferSuccess: (amount, login) => `Переведено ${amount} ₸ пользователю ${login}`,
    transferSelectUser: 'Выберите получателя',
    transferNoResults: 'Пользователи не найдены',
    transferConfirm: (amount, login) => `Перевести ${amount} ₸ пользователю ${login}?`,
    errorInvalidAmount: 'Введите корректную сумму',
    errorNoRecipient: 'Выберите получателя',
    errorTransferFailed: 'Ошибка перевода',
  },

  // ─── Модалка контактов (shared.js) ───
  contacts: {
    title: 'Сотрудничество',
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
  },

  // ─── Компонент подтверждения (shared.js) ───
  confirmDialog: {
    cancel: 'Отмена',
    confirm: 'Подтвердить',
  },

  // ─── Компонент Empty (shared.js) ───
  empty: 'Нет данных',

  // ─── Компонент NumInput (shared.js) ───
  numInputPlaceholder: 'Введите сумму',

  // ─── Компонент GlobalStats (shared.js) ───
  globalStats: {
    totalOnAccounts: 'Итого на счетах:',
  },

  // ─── Audit (shared.js) ───
  audit: {
    headers: 'Дата\tТип\tСумма\tСчёт\tФонд\tКатегория\tКомментарий\tПользователь',
  },

  // ─── Категории по умолчанию (shared.js) ───
  defaultCategories: ['Аванс', 'Зарплата', 'Циркон', 'Материалы', 'Реклама', 'Логистика', 'Прочее'],

  // ─── OpsTab ───
  ops: {
    title: 'Операции',
    subtitle: 'Поступления, расходы и переводы',
    income: 'Поступление',
    expense: 'Расход',
    transfer: 'Перевод',
    betweenAccounts: 'Между счетами',
    betweenFunds: 'Между фондами',
    fromAccount: 'Со счёта',
    toAccount: 'На счёт',
    account: 'Счёт',
    fromFund: 'Из фонда',
    toFund: 'В фонд',
    fundDebit: 'Фонд списания',
    expenseCategory: 'Категория расхода',
    amount: 'Сумма',
    comment: 'Комментарий',
    incomeDescPlaceholder: 'Описание поступления',
    expenseDescPlaceholder: 'Описание расхода',
    transferDescPlaceholder: 'Описание перевода',
    submitIncome: 'Провести поступление',
    submitExpense: 'Провести расход',
    submitTransfer: 'Провести перевод',
    undoLast: 'Отменить последнюю',
    undoConfirm: 'Отменить последнюю операцию?',
    undoSuccess: 'Операция отменена',
    enterAmount: 'Введите сумму',
    differentAccounts: 'Выберите разные счета',
    differentFunds: 'Выберите разные фонды',
    fundInsufficient: (fundName, balance, deficit) =>
      `В фонде "${fundName}" только ${balance}. Не хватает: ${deficit}`,
    accountInsufficient: (balance, deficit) =>
      `На счёте только ${balance}. Не хватает: ${deficit}`,
    fundOnlyInsufficient: (balance, deficit) =>
      `В фонде только ${balance}. Не хватает: ${deficit}`,
    distributionTitle: 'Распределение по фондам:',
    lastOps: 'Последние операции',
    noOpsYet: 'Пока нет операций',
    transferBetweenAccounts: 'Перевод между счетами',
    transferBetweenFunds: 'Перевод между фондами',
    // Перевод в заначку (скрытый счёт)
    transferToHidden: 'В заначку',
    toHiddenAccount: 'В заначку (скрытый счёт)',
    transferToHiddenComment: 'Перевод в заначку',
    transferToHiddenHint: 'Для фонда это списание (расход), сумма не попадёт в отчёты о доходах.',
    // Связь с долгами
    closeDebtLabel: 'Закрыть долг (Нам оплатили)',
    simpleIncome: '-- Простое поступление --',
    payDebtLabel: 'Оплатить наш долг / регулярный платеж',
    simpleExpense: '-- Простой расход --',
    debtPaymentComment: (desc) => `Оплата по обязательству: ${desc}`,
    // Категории
    expenseCategories: 'Категории расходов',
    categoriesLinkedToFund: 'Категории привязываются к выбранному фонду.',
    categoriesForFund: 'Категории для фонда:',
    newCategory: 'Новая категория',
    noCategories: 'Нет категорий',
    deleteCategoryConfirm: 'Удалить категорию расхода?',
    freeOperation: 'Свободная операция',
    debtPaymentIncoming: 'Оплата по обязательству (Нам)',
    debtPaymentOutgoing: 'Оплата по обязательству (Мы)',
    noReceivables: 'У вас нет ожидаемых поступлений по обязательствам.',
    selectDebtReceivable: 'Выберите долг (Нам должны)',
    selectDebtPlaceholder: '-- Выберите долг --',
    remainLabel: (amount) => `Остаток: ${amount}`,
    noPayables: 'У вас нет активных исходящих долгов или регулярных платежей.',
    selectDebtPayable: 'Выберите наш долг / регулярный платеж',
    selectPaymentPlaceholder: '-- Выберите платеж --',
    contractorOptional: 'Контрагент (опционально)',
    noContractor: 'Без контрагента',
    addContractorQuick: 'Быстрое добавление контрагента',
    addCategoryQuick: 'Быстрое добавление категории',
    incomeCategoryRequired: 'Категория дохода (обязательно)',
    expenseCategoryRequired: 'Категория расхода (обязательно)',
    descriptionPlaceholder: 'Описание',
    undoBtn: 'Отмена',
    accessRestricted: 'Доступ ограничен',
    noOperationRights: 'У вас нет прав для совершения операций.',
    selectDebtRequired: 'Выберите обязательство',
    paymentComment: (desc) => `Оплата: ${desc}`,
    changeCategories: 'Изменить',
    hideCategories: 'Скрыть',
  },

  // ─── DebtsTab ───
  debts: {
    title: 'Обязательства',
    subtitle: 'Управление дебиторской и кредиторской задолженностью',
    receivableTitle: 'Нам должны (Дебиторка)',
    receivableSubtitle: 'Выставленные счета, долги клиентов',
    payableTitle: 'Мы должны (Кредиторка)',
    payableSubtitle: 'Счета поставщиков, регулярные платежи',
    totalExpected: 'Итого ждем:',
    totalOwed: 'Итого должны:',
    addDebtor: 'Добавить должника',
    addOurDebt: 'Добавить наш долг',
    noActiveDebts: 'Нет активных долгов',
    whoOwesUs: 'Кто должен нам?',
    weOweTo: 'Кому мы должны?',
    // Модалка просмотра
    viewReceivable: 'Детальнее: Нам должны',
    viewPayable: 'Детальнее: Мы должны',
    contractor: 'Контрагент:',
    debtAmount: 'Сумма долга:',
    date: 'Дата:',
    commentLabel: 'Комментарий:',
    paymentBindings: 'Привязки для оплаты:',
    accountCredit: 'Счет зачисления: ',
    fundDebit: 'Фонд списания: ',
    category: 'Категория: ',
    paid: 'Оплатил',
    wePaid: 'Оплатили',
    paidAlert: 'Кнопка работает! Следующим шагом мы привяжем сюда модалку транзакции.',
    // Модалка создания
    contractorLabel: 'Контрагент*',
    newContractor: '+ Новый',
    selectPlaceholder: '-- Выберите --',
    accountCreditLabel: 'Счет зачисления',
    incomeCategoryLabel: 'Категория дохода',
    fundDebitLabel: 'Фонд списания',
    expenseCategoryLabel: 'Категория расхода',
    noBinding: 'Без привязки',
    noCategoryOption: 'Без категории',
    amountLabel: 'Сумма*',
    issueDateLabel: 'Дата выставления',
    expectedPaymentLabel: 'Ожидаемая оплата',
    descriptionLabel: 'Описание (За что?)',
    descriptionPlaceholder: 'Например: За поставку товара',
    regularPayment: 'Регулярный платеж (Аренда и т.д.)',
    repeatLabel: 'Повторять:',
    frequencyMonth: 'Раз в месяц',
    frequencyQuarter: 'Раз в квартал',
    frequencyYear: 'Раз в год',
    saveDebt: 'Сохранить обязательство',
    fillContractorAndAmount: 'Заполните контрагента и сумму',
    deleteConfirm: 'Точно удалить эту запись?',
    daysPassed: (days) => `прошло ${days} дн.`,
    // Контрагент из DebtsTab
    newContractorTitle: 'Новый контрагент',
    saveContractor: 'Сохранить контрагента',
    enterContractorName: 'Введите название контрагента',
    selectContractorType: 'Выберите хотя бы один тип контрагента',
    clientLabel: 'Клиент (Платит нам)',
    supplierLabel: 'Поставщик (Платим мы)',
    namePlaceholder: 'ИП Иванов',
    legalNameLabel: 'Юр. название',
    legalNamePlaceholder: 'ТОО Ромашка',
    binLabel: 'БИН / ИИН',
    binPlaceholder: '123456789012',
    phoneLabel: 'Телефон',
    phonePlaceholder: '+7 700 000 00 00',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Адрес',
    addressPlaceholder: 'г. Астана, ул. Примерная 1',
    availableAccounts: 'Доступные счета (для Клиента)',
    accountsHint: 'Если ничего не выбрано — доступен для всех счетов.',
    incomeCategories: 'Категории доходов:',
    noIncomeCategories: 'Нет категорий доходов',
    availableFunds: 'Доступные фонды и категории (для Поставщика)',
    fundsHint: 'Если ничего не выбрано — доступен везде.',
    fundsLabel: 'Фонды:',
    expenseCategories: 'Категории расходов:',
    // Валидации и ошибки
    enterLegalName: 'Введите юридическое название',
    enterBin: 'Введите БИН/ИИН',
    selectContractorRole: 'Выберите роль контрагента',
    duplicateContractor: (bin, name) => `Контрагент с таким БИН (${bin}) или Юридическим названием уже существует: ${name}`,
    selectAccountForDebt: 'Выберите счет для привязки обязательства',
    selectFundForPayable: 'Выберите фонд списания для кредиторской задолженности',
    correctAmountAlert: 'Укажите корректную сумму',
    selectDebtType: 'Выберите тип обязательства (нам должны / мы должны)',
    selectAccount: 'Выберите счёт',
    specifyContractor: 'Укажите или создайте контрагента',
    paymentExceedsRemaining: (payAmt, remAmt) => `Сумма оплаты (${payAmt}) превышает остаток (${remAmt})`,
    noCategoryConfirm: 'Категория не выбрана. Транзакция будет создана без категории.\n\nПродолжить без категории?',
    archiveConfirm: 'Отправить обязательство в архив?',
    // Лейблы контрагента (модалка создания)
    nameForYouLabel: 'Название (для вас)*',
    legalNameLabelReq: 'Юр. название*',
    binLabelReq: 'БИН / ИИН*',
    // Лейблы модалки создания долга
    accountDebitLabel: 'Счет списания*',
    accountCreditLabelReq: 'Счет зачисления*',
    fundDebitLabelReq: 'Фонд списания*',
    noActiveAccounts: 'Нет активных счетов',
    noActiveFunds: 'Нет активных фондов',
    monthLabel: 'Месяц:',
    quarterMonthLabel: 'Месяц квартала:',
    quarterFirst: 'Первый',
    quarterSecond: 'Второй',
    quarterThird: 'Третий',
    dayOfMonthLabel: 'День месяца:',
    // Модалка редактирования
    editDebtTitle: 'Редактировать обязательство',
    editContractorLabel: 'Контрагент',
    editContractorPlaceholder: 'Выберите контрагента',
    expectedPaymentDate: 'Ожидаемая дата оплаты',
    editAccountCreditLabel: 'Счёт зачисления',
    editFundDebitLabel: 'Фонд списания',
    // Модалка просмотра (расширенная)
    viewWeOweUs: 'Нам должны',
    viewWeOwe: 'Мы должны',
    editBtnTitle: 'Изменить',
    archiveBtnTitle: 'В архив',
    deleteBtnTitle: 'Удалить',
    closeBtnTitle: 'Закрыть',
    dateLabel: 'Дата',
    totalAmount: 'Общая сумма',
    paidLabelUpper: 'Оплачено',
    remainingLabel: 'Остаток',
    paidPercent: (pct) => `${pct}% оплачено`,
    paymentHistory: (count) => `История оплат (${count})`,
    attachmentLabel: 'Вложение',
    downloadBtn: 'Скачать',
    fixPayment: 'Зафиксировать оплату',
    toWhichAccount: 'На какой счёт?',
    fromWhichAccount: 'С какого счёта?',
    categoryIncomeLabel: 'Категория дохода',
    categoryExpenseLabel: 'Категория расхода',
    recommendSelect: 'Рекомендуем выбрать',
    fromWhichFund: 'Из какого фонда?',
    fullPayment: (amount) => `Полная оплата (${amount})`,
    partialPayment: 'Частичная оплата',
    enterPaymentAmount: 'Введите сумму оплаты:',
    payBtn: 'Оплатить',
    maxPaymentHint: (amount) => `Максимум: ${amount} · Будет создана транзакция на введённую сумму`,
    fullyPaid: 'Полностью оплачено',
    allPaymentsFixed: 'Все платежи по этому обязательству зафиксированы',
    ofAmount: 'из',
    debtPaymentComment: (desc) => `Оплата долга: ${desc}`,
    debtPaymentPartial: (paid, total) => ` (частично ${paid} из ${total})`,
    systemUser: 'Система',
    // Импорт счёта из PDF
    uploadInvoice: 'Загрузить счёт',
    importFromPdf: 'Импорт из PDF-документа',
    dragDropPdf: 'Перетащите PDF-файл или нажмите для выбора',
    invoiceOrPaymentOrder: 'Счёт на оплату или Платёжное поручение',
    recognizingDoc: 'Распознаём документ...',
    onlyPdfSupported: 'Поддерживаются только PDF-файлы',
    pdfReadError: 'Не удалось прочитать PDF. Убедитесь, что файл не защищен паролем.',
    tryAgain: 'Попробовать снова',
    invoiceDocType: 'Счёт на оплату',
    paymentOrderDocType: 'Платёжное поручение',
    documentDocType: 'Документ',
    debtTypeLabel: 'Тип обязательства*',
    receivableShort: 'Нам должны',
    payableShort: 'Мы должны',
    autoDetectFailed: 'Не удалось определить автоматически. Проверьте IBAN в настройках счетов.',
    typeUncertain: 'Тип определён предположительно — проверьте «Нам должны / Мы должны».',
    foundLabel: 'Найден',
    foundByBin: 'Найден по БИН',
    contractorNotFound: 'Контрагент не найден. Будет создан новый:',
    nameFieldLabel: 'Название',
    binFieldLabel: 'БИН/ИИН',
    orSelectExisting: 'Или выберите существующего:',
    searchPlaceholder: '-- Поиск --',
    descriptionImport: 'Описание',
    forWhatPlaceholder: 'За что?',
    createDebt: 'Создать обязательство',
    invoiceDesc: (num) => `Счет на оплату №${num}`,
    paymentOrderDesc: (num) => `Платежное поручение №${num}`,
    categoryDynamic: (isPayable) => isPayable ? 'Категория расхода' : 'Категория дохода',
    categoryHelpTooltip: 'Категория помогает отслеживать, за что именно получена или потрачена сумма.\n\n💡 Рекомендуем всегда указывать категорию для точного учёта.',
    // Аудит
    auditDeleted: (desc) => `Удалено обязательство: ${desc}`,
    auditTypeReceivable: 'Дебиторка',
    auditTypePayable: 'Кредиторка',
    auditTypeAmount: (type, amount) => `Тип: ${type}. Сумма: ${amount}`,
    auditNewDebt: (direction, amount) => `Новое обязательство: ${direction} ${amount}`,
    auditImportDebt: (direction, amount) => `Импорт из документа: ${direction} ${amount}`,
    auditWeOwe: 'Мы должны',
    auditTheyOwe: 'Нам должны',
  },

  // ─── TxTab ───
  tx: {
    title: 'Транзакции',
    totalLabel: (total, shown) =>
      `Всего: ${total}${shown !== total ? ` (показано: ${shown})` : ''}`,
    viewGrid: 'Вид: сетка',
    viewTable: 'Вид: таблица',
    allTypes: 'Все типы',
    allAccountsFunds: 'Все счета и фонды',
    fund: 'Фонд:',
    archivedSuffix: ' (архив)',
    dateFrom: 'Дата от',
    dateTo: 'Дата до',
    toLatest: 'К последним',
    toLatestOps: 'К последним операциям',
    toLatestOpsFull: 'Перейти к последним операциям',
    futureMonthTitle: 'Этот месяц ещё не наступил',
    futureMonthHint: 'Для будущих месяцев сетка не показывается. Переключись на текущий или прошлый месяц.',
    noTxInMonth: 'В этом месяце транзакций нет',
    noTxInMonthHint: 'Показываются только уже наступившие дни выбранного месяца.',
    noTransactions: 'Нет транзакций',
    opsCount: (n) => `${n} оп.`,
    moreItems: (n) => `+ еще ${n}`,
    // Таблица
    thDate: 'Дата',
    thType: 'Тип',
    thRoute: 'Маршрут',
    thCategory: 'Категория',
    thFund: 'Фонд',
    thAmount: 'Сумма',
    thComment: 'Комментарий',
    thContractor: 'Контрагент',
    thWho: 'Кто',
    thActions: '⚙',
    edited: '(ред.)',
    deleteTxConfirm: (amount) => `Удалить транзакцию на ${amount}?`,
    editLogComment: (oldAmt, newAmt, comment) =>
      `Было: ${oldAmt}, стало: ${newAmt}. ${comment}`,
    // День
    dayOps: 'Операций:',
    dayIncome: 'Поступления',
    dayExpense: 'Расходы',
    dayTransfers: 'Переводы',
    transferFundLabel: 'Перевод фонда',
    transferBetweenFunds: 'Перевод между фондами',
    transferBetweenAccounts: 'Перевод между счетами',
    noCommentShort: 'Без комментария',
    resetFilters: 'Сбросить',
    opsCountShort: (n) => `${n} оп.`,
  },

  // ─── ReportsTab ───
  reports: {
    title: 'Отчёты',
    charts: 'Графики',
    opu: 'ОПиУ',
    dds: 'ДДС',
    allReport: 'Общий отчёт',
    onlyOpu: 'Только ОПиУ',
    onlyDds: 'Только ДДС',
    allAccounts: 'Все счета',
    allFunds: 'Все фонды',
    // Статкарточки
    incomeLabel: 'Поступления',
    expenseLabel: 'Расходы',
    onAccounts: 'На счетах',
    inFunds: 'В фондах',
    // Графики
    chartIncomeExpense: 'Доход / расход',
    chartExpenseByFunds: 'Структура расходов по фондам',
    chartIncomeByAccounts: 'Поступления по счетам',
    chartCumulativeResult: 'Накопленный результат периода',
    // ОПиУ таблица
    opuTitle: 'ОПиУ',
    opuDescription: 'ОПиУ показывает результат бизнеса: сколько заработано и сколько потрачено. Внутренние переводы между счетами и фондами сюда не входят.',
    opuPeriodResult: 'Итог периода',
    opuIncome: 'ДОХОДЫ',
    opuExpense: 'РАСХОДЫ',
    opuProfit: 'ПРИБЫЛЬ / УБЫТОК',
    opuArticle: 'Статья',
    opuTotal: 'Итого',
    noFund: 'Без фонда',
    // ДДС таблица
    ddsTitle: 'ДДС',
    ddsDescription: 'ДДС показывает именно движение денег по счетам: остаток на начало, притоки, оттоки, внутренние переводы между счетами и остаток на конец. Поэтому это не то же самое, что ОПиУ.',
    ddsOpening: 'Остаток на начало',
    ddsInflow: 'Приток денег',
    ddsOutflow: 'Отток денег',
    ddsInternal: 'Внутренние переводы между счетами',
    ddsNetFlow: 'ЧИСТЫЙ ДЕНЕЖНЫЙ ПОТОК',
    ddsClosing: 'Остаток на конец',
    ddsNetFlowLabel: 'Чистый поток',
    ddsFilterHint: 'При фильтре по конкретному счёту внутренний перевод учитывается как перемещение денег внутри выбранного счета. При фильтре «Все счета» такой перевод не влияет на чистый поток.',
    // Экспорт
    exportSummary: 'СВОДКА',
    exportIncomes: 'Поступления',
    exportExpenses: 'Расходы',
    exportTransfers: 'Переводы',
    exportReportName: { all: 'Общий', opu: 'ОПиУ', dds: 'ДДС', categories: 'Категории', contractors: 'Контрагенты', timeliness: 'Дисциплина', liquidity: 'Ликвидность' },
    reportLabel: 'Отчёт',
    // Excel export strings
    exportIncomeByCat: 'АНАЛИЗ ДОХОДОВ ПО КАТЕГОРИЯМ',
    exportExpenseByCat: 'АНАЛИЗ РАСХОДОВ ПО КАТЕГОРИЯМ',
    exportContractorActivity: 'АКТИВНОСТЬ КОНТРАГЕНТОВ',
    exportContractorCol: 'Контрагент',
    exportRevenueCol: 'Выручка (+)',
    exportCostCol: 'Затраты (-)',
    exportBalanceCol: 'Сальдо',
    exportDebtBalanceCol: 'Долг / Баланс',
    exportTimeliness: 'ДИСЦИПЛИНА ОПЛАТ (Дебиторы)',
    exportClientCol: 'Клиент',
    exportDealsCol: 'Сделок',
    exportPunctualityCol: 'Пунктуальность (%)',
    exportAvgDelayCol: 'Средняя задержка',
    exportDaysShort: 'дн.',
    exportLiquidityPosition: 'ПОЗИЦИЯ ЛИКВИДНОСТИ',
    exportCashOnAccounts: 'Наличные на счетах',
    exportReceivablesPlus: 'Дебиторская задолженность (+)',
    exportPayablesMinus: 'Кредиторская задолженность (-)',
    exportTotalLiquidity: 'ИТОГОВАЯ ЛИКВИДНОСТЬ',
    exportCategoryCol: 'Категория',
    exportAmountCol: 'Сумма',
    // Экспорт - дополнительные типы
    exportCategoriesName: 'Категории',
    exportContractorsName: 'Контрагенты',
    exportTimelinessName: 'Дисциплина',
    exportLiquidityName: 'Ликвидность',
    // Вкладки отчетов
    categoriesTab: 'Категории',
    contractorsTab: 'Контрагенты',
    timelinessTab: 'Сроки',
    liquidityTab: 'Ликвидность',
    onlyCategoriesExport: 'Только Категории',
    onlyContractorsExport: 'Только Контрагенты',
    onlyTimelinessExport: 'Только Сроки',
    onlyLiquidityExport: 'Только Ликвидность',
    // Графики дополнительные
    obligationsCalendar: 'Календарь обязательств (Прогноз платежей)',
    noUpcomingPayments: 'Нет предстоящих платежей по долгам',
    weWillReceive: 'Нам поступят (+)',
    weWillPay: 'Мы заплатим (-)',
    // Категории вид
    incomeByCategories: 'Анализ доходов по категориям',
    expenseByCategories: 'Анализ расходов по категориям',
    categoryColumn: 'Категория',
    amountColumn: 'Сумма',
    // Контрагенты вид
    contractorActivity: 'Активность контрагентов',
    contractorColumn: 'Контрагент',
    revenueColumn: 'Выручка (+)',
    costsColumn: 'Затраты (-)',
    balanceColumn: 'Сальдо',
    debtBalanceColumn: 'Долг / Баланс',
    contractorFilterAll: 'Все',
    contractorFilterClients: 'Клиенты',
    contractorFilterSuppliers: 'Поставщики',
    // Дисциплина вид
    paymentDiscipline: 'Дисциплина оплат (Дебиторы)',
    paymentDisciplineHint: 'Средний показатель задержки или опережения графика платежей по реализованным сделкам. Пожизненная статистика.',
    clientsOnlyNote: 'В отчёте только клиенты: показатель рассчитывается по тем, кто платит вам. Для поставщиков (кому платите вы) эти сроки неприменимы.',
    clientColumn: 'Клиент',
    dealsColumn: 'Сделок',
    punctualityColumn: 'Пунктуальность (%)',
    avgDelayColumn: 'Средняя задержка',
    onTime: 'Вовремя',
    noPaymentHistory: 'Нет истории оплат для анализа',
    daysShort: 'дн.',
    // Ликвидность вид
    liquidityPosition: 'Позиция ликвидности',
    cashOnAccounts: 'Наличные на счетах:',
    receivablesLabel: 'Нам должны (Дебиторка):',
    payablesLabel: 'Мы должны (Кредиторка):',
    totalLiquidity: 'Итоговая ликвидность:',
    liquidityHint: 'Этот отчет показывает ваш финансовый "запас прочности", суммируя реальные деньги и предстоящие платежи.',
    capitalStructure: 'Структура капитала',
    cashName: 'Наличные',
    receivablesName: 'Дебиторка',
    resultChartName: 'Результат',
    resetFilters: 'Сбросить',
    dateFrom: 'Дата от',
    dateTo: 'Дата до',
    noDataAvailable: 'Нет данных о доходах за период',
  },

  // ─── AdminTab ───
  admin: {
    title: 'Настройки и профиль',
    subtitle: 'Управление компанией, персоналом и системой',
    profileTab: 'Профиль компании',
    usersTab: 'Персонал',
    systemTab: 'Система',
    securityTab: 'Безопасность',
    // Безопасность (Stage 11): UI-строки для SessionsCard и SecurityEventsCard
    security: {
      sectionSubtitle: 'Управление сессиями и история действий с аккаунтом',
      devicesTitle: 'Мои устройства',
      refresh: 'Обновить',
      loading: 'Загрузка...',
      noSessions: 'Нет активных сессий.',
      currentBadge: 'Текущая',
      thisDevice: 'Это устройство',
      terminate: 'Завершить',
      logoutAll: 'Выйти со всех устройств',
      logoutAllInProgress: 'Выход...',
      logoutAllHint: 'После этого нужно будет залогиниться заново на каждом устройстве.',
      confirmRevoke: 'Завершить эту сессию? Если это ваше текущее устройство — придётся войти заново.',
      confirmLogoutAll: 'Завершить ВСЕ сессии? Вы будете разлогинены сразу же на этом устройстве.',
      errorLoad: 'Не удалось загрузить',
      errorPrefix: 'Ошибка: ',
      unknownDevice: 'Неизвестное устройство',
      ipLabel: 'IP',
      loggedInAt: 'вход',
      // Stage 11: блок UI для двухфакторной аутентификации (TwoFactorCard + setup wizard)
      twoFactor: {
        title: 'Двухфакторная аутентификация',
        subtitle: 'Дополнительный код при входе — даже если кто-то узнает ваш пароль, без приложения он не сможет войти.',
        statusEnabled: 'Включена',
        statusDisabled: 'Не настроена',
        enabledSince: (date) => `Включена с ${date}`,
        backupRemaining: (n) => `Резервных кодов осталось: ${n}`,
        backupLowWarning: 'Резервных кодов мало. Рекомендуем сгенерировать новые.',
        enableBtn: 'Включить 2FA',
        disableBtn: 'Отключить 2FA',
        loading: 'Загрузка...',
        // Setup wizard (3 шага)
        setupTitle: 'Подключение 2FA',
        step1Title: 'Шаг 1 из 3 — Сканирование QR',
        step1Desc: 'Установите приложение для двухфакторной аутентификации (Google Authenticator, Microsoft Authenticator, Authy или 1Password) и отсканируйте QR-код.',
        manualSecretLabel: 'Не получается отсканировать?',
        manualSecretHint: 'Введите этот секрет вручную в приложении:',
        copyBtn: 'Копировать',
        copied: 'Скопировано',
        nextBtn: 'Далее',
        cancelBtn: 'Отмена',
        backBtn: '← Назад',
        step2Title: 'Шаг 2 из 3 — Подтверждение',
        step2Desc: 'Введите 6-значный код, который сейчас показывает приложение.',
        codeInputPh: '6 цифр',
        verifyBtn: 'Проверить',
        verifying: 'Проверка...',
        codeInvalid: 'Неверный код. Проверьте время на устройстве.',
        step3Title: 'Шаг 3 из 3 — Резервные коды',
        step3Desc: 'Сохраните эти 10 одноразовых кодов в надёжном месте. Они помогут войти, если вы потеряете доступ к приложению. Каждый код можно использовать только ОДИН раз.',
        step3Notice: '⚠️ Эти коды показываются только сейчас. Повторно их получить нельзя — в системе хранятся только хеши.',
        copyAllBtn: 'Копировать все',
        downloadBtn: 'Скачать .txt',
        confirmSavedLabel: 'Я сохранил резервные коды в надёжном месте',
        finishBtn: 'Готово',
        setupSuccess: '2FA успешно включена',
        // Disable flow
        disableTitle: 'Отключение 2FA',
        disableDesc: 'Введите текущий пароль и код 2FA (или один из резервных кодов), чтобы отключить двухфакторную аутентификацию.',
        passwordPh: 'Текущий пароль',
        codeOrBackupPh: '6 цифр или XXXX-XXXX',
        disableConfirmBtn: 'Отключить',
        disablingBtn: 'Отключение...',
        disableSuccess: '2FA отключена',
        confirmDisable: 'Вы уверены, что хотите отключить 2FA? Безопасность аккаунта снизится.',
        // Errors
        errorPrefix: 'Ошибка: ',
        errorLoad: 'Не удалось загрузить статус 2FA',
        errorInit: 'Не удалось начать настройку',
        currentPasswordInvalid: 'Неверный текущий пароль',
      },
      events: {
        title: 'Активность безопасности',
        empty: 'Нет событий за последнее время.',
        showingLast: 'Показаны последние 50 событий',
        criticalWarning: 'Если это были не вы — немедленно смените пароль и проверьте устройства выше.',
        warningBanner: 'Обнаружены подозрительные события — проверьте список ниже',
        // Локализованные названия action'ов в description критических событий
        // (например, sensitive_action_locked: 'change_password' → 'Смена пароля').
        actions: {
          change_password: 'Смена пароля',
          delete_account: 'Удаление аккаунта',
          delete_org: 'Удаление организации',
        },
        types: {
          password_changed: 'Пароль изменён',
          password_reset: 'Пароль сброшен по email',
          email_changed: 'Email изменён',
          '2fa_enabled': 'Двухфакторная аутентификация включена',
          '2fa_disabled': 'Двухфакторная аутентификация отключена',
          org_deleted: 'Организация удалена',
          account_deleted: 'Аккаунт удалён',
          reuse_detected: 'Обнаружен украденный токен сессии',
          logout_all: 'Завершены все сессии',
          session_revoked: 'Сессия завершена',
          admin_revoke: 'Сессия отозвана администратором',
          sensitive_action_locked: 'Подозрительные попытки ввода пароля',
        },
      },
    },
    // Профиль
    companyInfo: 'Информация о компании',
    ownerOnly: 'Только Владелец может изменять эти данные.',
    baseCurrency: 'Базовая валюта учета',
    currencyHint: 'Эта валюта будет отображаться во всех отчетах, операциях и экспортных файлах.',
    companyName: 'Название компании (Отображается в отчетах)',
    companyPlaceholder: 'Рога и Копыта ООО',
    emailLabel: 'Email для уведомлений',
    emailConfirmed: 'Подтверждена',
    confirmEmail: 'Подтвердить почту',
    emailPlaceholder: 'director@company.com',
    codePlaceholder: 'Код из письма',
    wrongCode: 'Неверный код. Попробуйте ещё раз.',
    codeSent: 'Код отправлен на вашу почту',
    codeSendError: 'Не удалось отправить код',
    codeSending: 'Отправка...',
    verificationError: 'Ошибка проверки кода',
    phoneLabel: 'Контактный телефон',
    phonePlaceholder: '77770001122',
    detailsLabel: 'Реквизиты и дополнительная информация',
    detailsPlaceholder: 'БИН/ИИН, юридический адрес...',
    // Мой аккаунт
    myAccount: 'Мой аккаунт',
    // Этап 2: подзаголовки секций в MyAccountTab (отдельный таб для всех ролей)
    myAccountSubtitle: 'Управление личными данными и безопасностью',
    personalDataSection: 'Личные данные',
    securitySection: 'Безопасность',
    currentPasswordPh: 'Текущий пароль',
    currentPasswordRequired: 'Введите текущий пароль',
    myPhoneLabel: 'Мой телефон',
    phoneNotSet: 'Не указан',
    phoneHint: 'Только цифры, минимум 7. Пробелы система расставит автоматически.',
    phoneMinDigits: 'Минимум 7 цифр',
    phoneSaved: 'Телефон сохранён',
    phoneSaveErr: 'Не удалось сохранить',
    filterUsersPlaceholder: 'Поиск: имя, логин, email или цифры телефона',
    roleLabel: 'Роль:',
    changePassword: 'Сменить мой пароль',
    newPasswordPlaceholder: 'Новый пароль',
    passwordHint: 'Оставьте пустым, если не хотите менять текущий пароль.',
    saveAll: 'Сохранить все изменения',
    saveSuccess: 'Настройки профиля успешно сохранены!',
    // Персонал
    employees: (count) => `Сотрудники (${count})`,
    viewOnlyHint: 'Вы в режиме просмотра. Управлять доступом может только Владелец.',
    addEmployee: 'Добавить сотрудника',
    dupLogin: 'Пользователь с таким логином уже есть в этой организации',
    addExisting: 'Добавить существующего',
    addNew: 'Создать нового',
    searchUserPlaceholder: 'Логин или email пользователя',
    searchBtn: 'Найти',
    searching: 'Поиск...',
    userFound: 'Пользователь найден',
    userNotFound: 'Пользователь не найден. Проверьте логин или email.',
    userAlreadyInOrg: 'Этот пользователь уже добавлен в организацию',
    addToOrg: 'Добавить в организацию',
    addToOrgHint: 'Пользователь получит доступ к этой организации с выбранной ролью',
    userOrgsCount: n => `Состоит в ${n} орг.`,
    suggestedRole: 'Рекомендуемая роль (из другой орг.)',
    accessRevoked: 'Доступ к организации отозван',
    accessRevokedHint: 'Ваш доступ к этой организации был отозван. Выберите другую организацию.',
    employeeName: 'Имя сотрудника',
    employeeNamePlaceholder: 'Иван Иванов',
    roleSelectLabel: 'Роль',
    loginLabel: 'Логин для входа',
    loginPlaceholder: 'ivan_buh',
    initialPassword: 'Начальный пароль',
    initialPasswordPlaceholder: 'pass123',
    deleteEmployeeConfirm: 'Точно удалить этого сотрудника из организации?',
    permOverrides: 'Индивидуальные допуски (переопределяют роль)',
    permHint: '* Подсвеченные допуски установлены вручную. Остальные наследуются от роли.',
    orgsLabel: 'Организации:',
    loginDisplayLabel: 'Логин:',
    passwordDisplayLabel: 'Пароль:',
    showPassword: 'Показать',
    resetPassword: 'Сменить пароль',
    resetPasswordPlaceholder: 'Новый пароль',
    passwordResetSuccess: 'Пароль изменён',
    passwordNotSet: 'Задать',
    // Stage 11: смена логина владельцем (только owner)
    changeLogin: 'Изменить логин',
    changeLoginTitle: 'Смена логина сотрудника',
    changeLoginDesc: 'Новый логин должен начинаться с латинской буквы и состоять из 3-32 символов: a-z, 0-9, точка, подчёркивание, дефис.',
    changeLoginPlaceholder: 'Новый логин',
    changeLoginSubmit: 'Изменить',
    changeLoginSuccess: 'Логин изменён',
    loginInvalidFormat: 'Неверный формат: только латиница, цифры, ".", "_", "-", 3-32 символа, начинается с буквы.',
    loginTaken: 'Этот логин уже занят другим пользователем',
    loginUnchanged: 'Логин не изменился',
    // Stage 11: предупреждение при смене пароля владельцем — что именно сбрасывается
    resetPasswordTitle: 'Сброс доступа сотрудника',
    resetPasswordWarning: 'Будут сброшены: пароль, двухфакторная аутентификация (если включена), все активные сессии. Сотрудник будет немедленно вылогинен со всех устройств.\n\nЕсли это передача аккаунта новому человеку — НЕ ЗАБУДЬТЕ также изменить email сотрудника! Иначе старый сотрудник может через "Забыли пароль" получить новый reset-link.\n\nПродолжить?',
    resetPasswordSimpleHint: 'Новый пароль — минимум 8 символов.',
    // Этап 2 v2: смена email sub-user-а владельцем
    changeEmail: 'Изменить email',
    changeEmailTitle: 'Email сотрудника',
    changeEmailDesc: 'Email сотрудника используется для самостоятельного восстановления пароля. При увольнении обязательно очистите или замените email, иначе старый сотрудник может через "Забыли пароль" получить доступ.',
    changeEmailPlaceholder: 'employee@example.com',
    changeEmailSubmit: 'Сохранить',
    changeEmailRemoveBtn: 'Удалить email',
    changeEmailSuccess: 'Email обновлён',
    changeEmailRemoveSuccess: 'Email удалён',
    emailInvalid: 'Некорректный формат email',
    emailTaken: 'Этот email уже используется другим пользователем',
    emailNotSet: 'не указан',
    emailVerifiedYes: 'Подтверждён',
    emailVerifiedNo: 'Не подтверждён',
    orgAssignment: 'Организации',
    orgAssignmentHint: 'Отметьте организации, к которым пользователь имеет доступ',
    orgRoleIn: role => `Роль: ${role}`,
    // Система
    backupTitle: 'Резервное копирование базы данных',
    backupHint: 'Регулярно скачивайте резервную копию на свой компьютер. Если вы случайно удалите данные в браузере или зайдете с другого устройства, вы сможете загрузить этот файл и восстановить всю бухгалтерию.',
    saveBackup: 'Сохранить бэкап (.json)',
    restoreBackup: 'Восстановить бэкап',
    restoreOwnerOnly: 'Только Владелец может восстанавливать базу данных!',
    restoreConfirm: 'ВНИМАНИЕ! Текущие данные будут удалены и заменены данными из файла. Продолжить?',
    restoreSuccess: 'База данных успешно восстановлена!',
    restoreFormatError: 'Неверный формат файла резервной копии.',
    restoreReadError: 'Произошла ошибка при чтении файла.',
    auditTitle: 'Журнал аудита системы',
    auditHint: 'В журнале аудита фиксируются все действия пользователей (кто, когда и какую операцию провел, добавил или удалил). Вы можете выгрузить его для проверки.',
    downloadAudit: 'Скачать Журнал аудита (.tsv)',
    statsTitle: 'Статистика загруженности',
    statsUsers: 'Пользователей',
    statsAccounts: 'Активных счетов',
    statsFunds: 'Активных фондов',
    statsTx: 'Транзакций',
    langLabel: 'Язык интерфейса',
  },

  // ─── DataTab ───
  data: {
    accountsTab: 'Счета и фонды',
    contractorsTab: 'Контрагенты',
    categoriesTab: 'Категории',
    archiveTab: 'Архив',

    // --- CategoriesTab ---
    linkedToAccount: (name) => `Привязан к счету: ${name}`,
    linkedToFund: (name) => `Привязан к фонду: ${name}`,
    deletedLink: 'Удален',
    notLinkedToAccount: 'Не привязана к счету',
    notLinkedToFund: 'Не привязана к фонду',
    baseSystemCategory: 'Базовая системная категория',
    noTurnover: 'Оборота нет',
    archiveCategoryConfirm: (name) => `Отправить категорию "${name}" в архив?`,
    archiveCategoryExtra: 'Категория исчезнет из выпадающих списков при создании новых операций, но все прошлые операции с ней останутся без изменений в финансовых отчетах. Вы сможете восстановить её из Единого Архива.',
    editCategory: 'Изменить категорию',
    newCategory: 'Новая категория',
    incomeType: 'Доход',
    expenseType: 'Расход',
    categoryNamePlaceholder: 'Название категории',
    linkToAccountLabel: 'Привязка к Счету (опционально)',
    noAccountLink: 'Без привязки к счету',
    linkToFundLabel: 'Привязка к Фонду (опционально)',
    noFundLink: 'Без привязки к фонду',
    linkedContractors: 'Привязанные контрагенты',
    noneSelected: 'Никто не выбран',
    searchByNameOrBin: 'Поиск по имени или БИН...',
    contractorsNotFound: 'Контрагенты не найдены',
    binIinLabel: 'БИН/ИИН: ',
    categoriesTitle: 'Справочник категорий',
    categoriesSubtitle: (count) => `Аналитика доходов и расходов (${count})`,
    addCategory: 'Добавить категорию',
    searchCategory: 'Поиск категории...',
    sortRecent: 'Сначала недавние',
    sortVolDesc: 'По обороту (убывание)',
    sortVolAsc: 'По обороту (возрастание)',
    sortNameAsc: 'По алфавиту (А-Я)',
    sortNameDesc: 'По алфавиту (Я-А)',
    categoriesNotFound: 'Категории не найдены',
    categoriesForAccounts: 'Категории для Счетов',
    categoriesForFunds: 'Категории для Фондов',

    // --- CategoryModal ---
    enterName: 'Введите название',
    archiveCategoryMinOne: 'Невозможно перенести в архив: должна оставаться хотя бы одна активная категория.',
    archiveCategoryQuestion: 'Отправить категорию в архив?',
    noLinkGeneral: 'Без привязки (Общая)',
    linkToAccountEdit: 'Привязка к счету (опционально)',
    linkToFundEdit: 'Привязка к фонду (опционально)',
    noLinkGeneralCategory: 'Без привязки (Общая категория)',
    categoryNameLabel: 'Название категории',
    notFoundShort: 'Не найдено',
    saveChanges: 'Сохранить изменения',
    linkSection: 'Привязка',
    accountPrefix: 'Счет: ',
    fundPrefix: 'Фонд: ',
    categoryOperations: 'Операции по категории',
    periodAll: 'За все время',
    periodMonth: 'За последний месяц',
    period6months: 'За полгода',
    periodYear: 'За год',
    periodCustom: 'Произвольно',
    totalTurnover: 'Суммарный оборот за выбранный период:',
    operationsList: 'Список операций (Кликабельно)',
    noOpsInPeriod: 'В этом периоде операций нет',

    // --- TxEditModal ---
    moveToArchiveTitle: 'Перенести в архив?',
    moveToArchiveDesc: 'Балансы счетов и фондов будут пересчитаны: сумма операции будет возвращена. Операцию можно восстановить из архива.',
    yesToArchive: 'Да, в архив',
    viewOperation: 'Просмотр операции',
    txEditHelp: 'Вы можете изменить категорию, контрагента и комментарий. Сумма не редактируется для точности учёта. Для отмены операции — перенесите в архив.',
    txTypeIncome: 'Поступление',
    txTypeExpense: 'Расход',
    txTypeTransfer: 'Перевод',
    contractor: 'Контрагент',
    noContractor: 'Без контрагента',
    categorySelectLabel: 'Категория',
    noCategory: 'Без категории',
    commentLabel: 'Комментарий',
    descPlaceholder: 'Описание...',

    // --- AccountsFundsTab ---
    enterAccountName: 'Введите название счета',
    enterFundName: 'Введите название фонда',
    newAccount: 'Новый счет',
    accountNameLabel: 'Название счета',
    accountNamePlaceholder: 'Каспи Gold, Наличные...',
    makePrimary: 'Сделать основным',
    hiddenAccountLabel: 'Скрытый счет (Личный)',
    whoCanSee: 'Кто может видеть этот счет?',
    visibleOnlyMe: 'Только я (владелец)',
    visibleMeAccountant: 'Я и бухгалтер',
    visibleAdmins: 'Я, бухгалтер и администратор',
    visibleAll: 'Вся команда',
    ibanLabel: 'ИИК / IBAN (Обязательно)',
    ibanRequired: 'Введите ИИК / IBAN счета. Это необходимо для работы импорта счетов.',
    colorLabel: 'Цвет ярлыка',
    saveAccount: 'Сохранить счет',
    newFund: 'Новый фонд',
    fundNameLabel: 'Название фонда',
    fundNamePlaceholder: 'Зарплаты, Резерв...',
    fundPercentLabel: 'Процент отчислений (%)',
    fundTargetLabel: 'Целевая сумма',
    saveFund: 'Сохранить фонд',
    yourAccounts: 'Ваши счета',
    sortLabel: 'Сортировка...',
    sortAlpha: 'По алфавиту (А-Я)',
    sortBalDesc: 'По балансу (Убывание)',
    sortBalAsc: 'По балансу (Возрастание)',
    sortPctDesc: 'По процентам (Убывание)',
    addBtn: 'Добавить',
    noActiveAccounts: 'Нет активных счетов',
    primaryBadge: 'Основной',
    fundsTitle: 'Фонды (Копилки)',
    noActiveFunds: 'Нет активных фондов',

    // --- AccountModal ---
    primaryAccount: 'Основной счет',
    hiddenAccountEdit: 'Скрытый счет (Личный)',
    visibilityPrefix: 'Видимость: ',
    balancePrefix: (bal) => `Баланс: ${bal}`,
    historyBtn: 'История',
    ibanShort: 'ИИК:',
    incomeCategories: 'Категории доходов',
    noFreeCategories: 'Нет свободных категорий',
    noLinkedCategories: 'Нет привязанных категорий',
    removeLink: 'Убрать',
    clientsTitle: 'Клиенты',
    allClientsLinked: 'Все клиенты привязаны',
    noLinkedClients: 'Нет привязанных клиентов',
    enterIban: 'Введите ИИК / IBAN. Это необходимо для автоматического распознавания счетов.',

    // --- FundModal ---
    fundPercentExceeds: 'Сумма процентов фондов превысит 100%',
    inFundPrefix: (bal) => `В фонде: ${bal}`,
    expenseCategories: 'Категории расходов',
    noLinkedExpenseCategories: 'Нет привязанных категорий',
    noFreeExpenseCategories: 'Нет свободных категорий',

    // --- QuickHiddenAdjustModal ---
    topUpStash: 'Пополнить заначку',
    withdrawStash: 'Взять из заначки',
    hiddenAccountName: 'Скрытый счет',
    stashNow: 'В заначке сейчас',
    topUpAmount: 'Сумма пополнения',
    withdrawAmount: 'Сумма изъятия',
    commentForSelf: 'Комментарий (для себя)',
    topUpPlaceholder: 'Скинул с премии...',
    withdrawPlaceholder: 'Потратил на подарок...',
    enterAmount: 'Введите сумму',
    insufficientStash: (balance) => `Недостаточно средств. В заначке: ${balance}`,
    topUpComment: 'Пополнение заначки',
    withdrawComment: 'Изъятие из заначки',
    topUpBtn: 'Пополнить',
    withdrawBtn: 'Изъять',

    // --- Tooltips (title attributes) ---
    editTooltip: 'Изменить',
    toArchiveTooltip: 'В архив',
    closeTooltip: 'Закрыть',
    hiddenAccountTooltip: 'Скрытый счет',
    quickIncomeTooltip: 'Быстрое поступление',
    quickExpenseTooltip: 'Быстый расход',
    accountHelpFallback: 'Счёт — это ваш банковский счёт или касса. На него поступают деньги и с него происходят расходы.',
    fundHelpFallback: 'Фонд — это виртуальная копилка. Деньги распределяются по фондам согласно процентам.',
    ibanHelpTooltip: 'ИИК необходим для автоматического распознавания счетов при импорте PDF.',
  },

  // ─── ContractorsTab ───
  contractors: {
    title: 'Справочник контрагентов',
    subtitle: (count) => `База клиентов и поставщиков (${count})`,
    addContractor: 'Добавить контрагента',
    searchPlaceholder: 'Поиск по имени или БИН...',
    sortRecent: 'Сначала недавние',
    sortVolDesc: 'По объему (убывание)',
    sortVolAsc: 'По объему (возрастание)',
    sortNameAsc: 'По алфавиту (А-Я)',
    sortNameDesc: 'По алфавиту (Я-А)',
    notFound: 'Контрагентов не найдено',
    clientsTitle: 'Клиенты (Нам платят)',
    suppliersTitle: 'Поставщики (Мы платим)',
    emptyList: 'Список пуст',
    partner: '↔ Партнер',
    oweUs: (amount) => `Нам: ${amount}`,
    weOwe: (amount) => `Мы: ${amount}`,
    zeroBalance: 'По нулям',
    noExtraData: 'Нет доп. данных',
    legalNotSpecified: 'Юр. лицо не указано',
    client: 'Клиент',
    supplier: 'Поставщик',
    // Модалка
    editTitle: 'Редактировать',
    newTitle: 'Новый контрагент',
    roleRequired: 'Роль контрагента (Обязательно)',
    clientRole: 'Клиент (Платит нам)',
    supplierRole: 'Поставщик (Платим мы)',
    nameLabel: 'Название (для вас)*',
    namePlaceholder: 'Например: ИП Иванов',
    legalNameLabel: 'Юридическое название',
    legalNamePlaceholder: 'ТОО Ромашка',
    binLabel: 'БИН / ИИН',
    binPlaceholder: '123456789012',
    phoneLabel: 'Телефон',
    phonePlaceholder: '+7 700 000 00 00',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Физический адрес',
    addressPlaceholder: 'г. Астана, ул. Абая 1',
    saveContractor: 'Сохранить контрагента',
    saveChanges: 'Сохранить изменения',
    enterName: 'Введите название контрагента',
    selectType: 'Выберите хотя бы один тип контрагента (Клиент или Поставщик)',
    archiveConfirm: 'Отправить контрагента в архив?',
    enterNameShort: 'Введите название',
    // Финансовая сводка
    financeSummary: 'Финансовая сводка',
    periodAll: 'За все время',
    periodMonth: 'За месяц',
    period6months: 'За полгода',
    periodYear: 'За год',
    periodCustom: 'Произвольно',
    fromClient: 'От клиента (Нам платят)',
    toSupplier: 'Поставщику (Мы платим)',
    alreadyPaidUs: 'Уже заплатил нам:',
    stillOwesUs: 'Еще должен заплатить:',
    alreadyPaidThem: 'Уже заплатили мы:',
    ourDebt: 'Наш долг (Кредит):',
    oweNow: 'Должны сейчас:',
    totalPaid: 'Всего оплачено:',
    opsForPeriod: 'Операции за период',
    noOpsInPeriod: 'В этом периоде операций нет',
    ourDebtLabel: 'Наш долг',
    clientDebtLabel: 'Долг клиента',
    paidStatus: '✓ Оплачено',
    awaitingPayment: 'Ожидает оплаты',
    linkedCategories: 'Привязанные категории',
    // Привязки
    availableAccounts: 'Доступные счета (для Клиента)',
    accountsHint: 'Если ничего не выбрано — доступен для всех счетов.',
    incomeCategories: 'Категории доходов:',
    noIncomeCategories: 'Нет категорий доходов',
    availableFundsAndCats: 'Доступные фонды и категории (для Поставщика)',
    fundsHint: 'Если ничего не выбрано — доступен везде.',
    fundsLabel: 'Фонды:',
    expenseCategories: 'Категории расходов:',
    editTitleFull: 'Редактирование',
    archiveConfirmDetailed: (name) => `Отправить контрагента "${name}" в архив?`,
    archiveHint: 'Контрагент исчезнет из выпадающих списков при создании новых операций, но все прошлые операции с ним останутся без изменений. Вы сможете восстановить его из Единого Архива.',
    accountsLabelClient: 'Доступные счета (Клиент)',
    fundsAndCatsSupplier: 'Фонды и категории (Поставщик)',
    titleLabel: 'Название (заголовок)*',
    legalNameRequired: 'Юридическое название*',
    binRequired: 'БИН / ИИН*',
    enterLegalNameError: 'Введите юридическое название контрагента',
    enterBinError: 'Введите БИН/ИИН контрагента',
    enterLegalNameShort: 'Введите юридическое название',
    enterBinShort: 'Введите БИН/ИИН',
    duplicateError: (bin, name) => `Контрагент с таким БИН (${bin}) или Юридическим названием уже существует: ${name}`,
    selectTypeError: 'Выберите хотя бы одну роль (Клиент или Поставщик)',
    phoneUpperLabel: 'ТЕЛЕФОН',
    emailUpperLabel: 'EMAIL',
    addressUpperLabel: 'АДРЕС',
    clientPaysUs: 'Клиент (Нам платят)',
    supplierWePay: 'Поставщик (Мы платим)',
    debtToUsLabel: 'Долг нам:',
    ourDebtLabel2: 'Наш долг:',
    currentBindings: 'Текущие привязки',
    accountsAndIncomeCategories: 'Счета и Категории доходов',
    fundsAndExpenseCategories: 'Фонды и Категории расходов',
    noBindings: 'Нет привязок',
    lastOperations: 'Последние операции',
    historyEmpty: 'История пуста',
    noDescriptionShort: 'Без описания',
    paidStatusUpper: 'ОПЛАЧЕНО',
    awaitingStatusUpper: 'ОЖИДАЕТ',
    clientTypeLabel: 'Клиент',
    supplierTypeLabel: 'Поставщик',
  },

  // ─── HistoryTab / HistoryModal ───
  history: {
    title: (name) => `История: ${name}`,
    noOps: 'Нет операций',
    pageTitle: 'История действий',
    pageSubtitle: 'Журнал активности системы и аудит изменений',
    exportTsv: 'Экспорт .TSV',
    allActions: 'Все действия',
    accountantsTab: 'Бухгалтеры',
    adminsTab: 'Админы',
    searchPlaceholder: 'Поиск по имени или действию...',
    eventDetails: 'Детали события',
    dateTime: 'Дата и время',
    executor: 'Исполнитель',
    action: 'Действие',
    stateChanges: 'ИЗМЕНЕНИЯ СОСТОЯНИЯ',
    wasBefore: 'БЫЛО',
    becameAfter: 'СТАЛО',
    understood: 'Понятно',
    adminRole: 'Админ',
    // Типы действий (фильтры)
    actionTypes: {
      all: 'Все действия',
      income: 'Доходы',
      expense: 'Расходы',
      transfer: 'Переводы',
      accounts: 'Счета/Фонды',
      categories: 'Категории',
      users: 'Сотрудники',
      debts: 'Обязательства',
      contractors: 'Контрагенты',
      system: 'Система',
    },
    // Заголовки таблицы
    colDateTime: 'Дата / Время',
    colUser: 'Пользователь',
    colType: 'Тип',
    colAction: 'Действие',
    colChanges: 'Изменения',
    colDetails: 'Детали',
    // Фильтры
    filterActionType: 'Тип действия',
    filterUser: 'Пользователь',
    filterDateFrom: 'Дата от',
    filterDateTo: 'Дата до',
    filterAll: 'Все',
    resetFilters: 'Сбросить фильтры',
  },

  // ─── Архив (ArchiveTab.js) ───
  archive: {
    tabAccounts: 'Счета',
    tabFunds: 'Фонды',
    tabContractors: 'Контрагенты',
    tabCategories: 'Категории',
    tabTx: 'Операции',
    deleteForever: 'Удалить навсегда?',
    deleteForeverDesc: 'Это действие необратимо. Запись будет удалена без возможности восстановления. Финансовое воздействие операций уже было отменено при архивации.',
    restoreFromArchive: 'Восстановить из архива?',
    restoreTxDesc: (amount, verb) => `Сумма ${amount} будет вновь учтена в текущих балансах и отчетах. Средства будут ${verb} соответствующих счетов/фондов.`,
    restoreTxCredited: 'зачислены на',
    restoreTxDebited: 'списаны с',
    restoreAccFundDesc: (name) => `Счет/фонд "${name}" снова станет доступен для выбора в операциях и отобразится на главном экране (если не скрыт). Баланс останется прежним.`,
    restoreContractorDesc: (name) => `Контрагент "${name}" снова станет доступен для выбора в новых операциях.`,
    restoreCategoryDesc: (name) => `Категория "${name}" снова станет доступна для распределения средств.`,
    noArchived: (entity) => `Нет архивных ${entity}`,
    emptyAccounts: 'Счетов',
    emptyFunds: 'Фондов',
    emptyContractors: 'Контрагентов',
    emptyCategories: 'Категорий',
    emptyTx: 'Операций',
    balance: 'Баланс:',
    restore: 'Восстановить',
    income: 'Поступление',
    expense: 'Расход',
    transfer: 'Перевод',
    incomePrefix: '[Доход] ',
    expensePrefix: '[Расход] ',
  },

  // ─── Юридические документы (LegalDocsModal) ───
  legal: {
    tosTitle: 'Пользовательское соглашение (Публичная Оферта)',
    privacyTitle: 'Политика конфиденциальности и защиты данных',
    consentTitle: 'Согласие на сбор и обработку данных',
    agreeBtn: 'Я ознакомлен и согласен',
    // Содержимое документов — длинные юридические тексты оставим как есть
    // (их переводить имеет смысл когда будет реальная мультиязычность)
  },

  // ─── Подсказки (бывший HelpText.js) ───
  help: {
    contractor: `Для чего это нужно? Контрагент — это тот, с кем вы ведете расчеты.

Пока вы не выберете его роль, остальные поля будут заблокированы.
• Клиент (Платит вам): Привязывается к вашим Счетам (доходам) и Категориям доходов.
• Поставщик (Платите вы): Привязывается к Фондам и Категориям расходов.

Если это универсальный партнер (вы и покупаете у него, и продаете ему), можно выбрать обе галочки сразу.`,

    category: `Для чего нужны категории?
Категории помогают анализировать структуру доходов и расходов.

• Категории доходов: показывают, за что именно платят клиенты. Можно привязать к конкретному Счету.
• Категории расходов: показывают, на что уходят деньги. Можно привязать к конкретному Фонду.

Вы можете создать категорию без привязки к фонду или счету — тогда она будет "Общей" и доступной при любой операции.
Также к категории можно привязать контрагентов, чтобы ускорить работу (они будут предлагаться первыми).`,

    uncategorized: `Почему эта категория называется «Без категории»?

Сюда автоматически попадают все операции, которым вы при создании не задали конкретную категорию.

Как навести порядок?
Просто кликните на любую операцию в списке ниже — откроется окно редактирования. Выберите там нужную категорию, сохраните, и операция автоматически переместится в правильный раздел!`,

    debtReceivable: `Дебиторская задолженность (Нам должны)

Здесь фиксируются суммы, которые клиенты или партнеры обещают вам выплатить.
• Вы можете сразу привязать планируемый доход к нужному Счету и Категории.
• Когда должник вернет деньги, нажмите «Оплатил» — система автоматически создаст транзакцию пополнения и закроет этот долг.`,

    debtPayable: `Кредиторская задолженность (Мы должны)

Здесь фиксируются ваши обязательства перед поставщиками, сотрудниками или партнерами.
• Укажите Фонд, из которого планируете отдавать долг, и Категорию расхода.
• Когда вы погасите долг, нажмите «Оплатили» — система автоматически спишет деньги из фонда и переведет долг в историю.`,

    primaryAccount: `Что значит «Основной»?

Этот счет или фонд всегда будет отображаться первым в списках. Именно он будет предлагаться по умолчанию при добавлении новых доходов или расходов, чтобы ускорить работу.`,

    hiddenAccount: `Что такое «Скрытый счет»?

Это ваша личная "заначка" или резерв.
• Он не учитывается в общем балансе компании (в других вкладках).
• Поступления на него не распределяются по фондам.
• Ему не нужны категории доходов и расходов.

Вы можете настроить приватность: сделать его видимым только для вас или для определенных ролей в компании.`,

    quickIncome: `Как работает Быстрое поступление?

1. Возврат долга: Если вам оплачивают ранее созданный долг, просто выберите его в списке "Погашение долга". Система сама заполнит все поля (сумму, клиента, категорию) и автоматически закроет этот долг в Обязательствах.

2. Новая оплата: Если это новая выручка, пропустите выбор долга. Просто укажите сумму, выберите категорию (за что заплатили) и клиента (кто заплатил).

💡 Важно: Деньги автоматически распределятся по вашим фондам (копилкам) в зависимости от заданных там процентов!`,

    setupAccount: `Что такое «Счёт» в INMONEX?

Счёт — это отражение вашего реального банковского счёта или кассы. Сюда поступают деньги от клиентов, и отсюда вы оплачиваете расходы.

• Каждый счёт показывает баланс в реальном времени
• Переводы между счетами тоже отслеживаются
• Можно добавить сколько угодно счетов

Типичные примеры:
💳 Kaspi Бизнес — основной для приёма оплат
🏦 Halyk — для крупных расчётов
💵 Наличные — касса в офисе`,

    setupFund: `Фонд — это «умная копилка» для вашего бизнеса.

Каждый раз, когда клиент платит вам, деньги автоматически «раскидываются» по фондам в заданных пропорциях.

Зачем? Чтобы не попасть в ловушку: «деньги на счету есть, а на налоги / зарплаты не хватает».

• Зарплата (50%) — всегда знаете, сколько можете платить
• Налоги (20%) — никаких сюрпризов от налоговой
• Прибыль (30%) — ваш личный доход от бизнеса

💡 Фонды не блокируют деньги — это виртуальный учёт. Вы всегда видите, сколько «принадлежит» каждому направлению.`,

    setupFundLink: `Как связаны Счета и Фонды?

📥 Счёт — это ГДЕ лежат деньги (Kaspi, Halyk, касса)
📊 Фонд — это ДЛЯ ЧЕГО эти деньги предназначены (зарплата, налоги, прибыль)

Пример:
→ Клиент перевёл 1 000 000 ₸ на ваш Kaspi
→ INMONEX автоматически записывает:
   • 500 000 ₸ в фонд «Зарплата»
   • 200 000 ₸ в фонд «Налоги»
   • 300 000 ₸ в фонд «Прибыль»

Деньги физически остаются на Kaspi, но вы чётко видите, сколько можно тратить из каждого фонда.`,

    debtPayment: `Как работает оплата обязательства?

Когда клиент оплачивает вам долг (дебиторка) или вы оплачиваете свой долг (кредиторка), система автоматически:

📥 Дебиторка (Нам оплатили):
• Создаёт транзакцию-поступление на выбранный счёт
• Распределяет сумму по фондам (согласно процентам)
• Записывает комментарий и категорию из обязательства

📤 Кредиторка (Мы оплатили):
• Создаёт транзакцию-расход с выбранного счёта
• Списывает из указанного фонда
• Записывает категорию расхода

💡 Все данные (контрагент, категория, комментарий) заполняются автоматически из обязательства. Вам нужно только подтвердить и при необходимости выбрать счёт.`,

    debtPartial: `Частичная оплата — что это?

Если клиент оплатил не всю сумму, а только часть, используйте «Частичная оплата»:

1. Введите сколько именно оплачено
2. Система создаст транзакцию на эту сумму
3. Остаток долга автоматически пересчитается
4. В истории оплат будет видно каждый платёж

Пример:
• Долг: 1 000 000 ₸
• Первый платёж: 400 000 ₸ → остаток 600 000 ₸
• Второй платёж: 600 000 ₸ → долг закрыт ✅

💡 Прогресс-бар покажет, сколько уже оплачено. Когда сумма оплат достигнет полной суммы долга, обязательство автоматически закроется.`,

    debtViewInfo: `Детальная информация об обязательстве.

Здесь вы видите полную картину: кто должен, сколько, за что и какой статус оплаты.

• «Общая сумма» — изначальная сумма обязательства
• «Оплачено» — сколько уже оплачено (сумма всех платежей)
• «Остаток» — сколько ещё нужно оплатить
• Привязки — к какому счёту, фонду и категории привязана оплата

Используйте кнопки внизу для оплаты, редактирования или архивирования обязательства.`,

    perm_manageAccounts: `Доступ к Счетам:
Позволяет просматривать балансы счетов, создавать новые счета, редактировать их реквизиты (IBAN) и отправлять в архив.`,

    perm_manageFunds: `Доступ к Фондам:
Позволяет создавать виртуальные копилки (фонды), настраивать проценты распределения и целевые суммы, а также архивировать неиспользуемые фонды.`,

    perm_manageCategories: `Управление Категориями:
Позволяет создавать и редактировать категории доходов и расходов, привязывать их к конкретным счетам или фондам для автоматизации учета.`,

    perm_manageContractors: `Работа с Контрагентами:
Дает право на создание и редактирование базы клиентов и поставщиков, просмотр их финансовой истории и управление связями с категориями.`,

    perm_manageOperations: `Действия с Операциями:
Позволяет проводить новые поступления, расходы и переводы, а также отменять последние действия и редактировать транзакции.`,

    perm_manageDebts: `Управление Обязательствами:
Позволяет фиксировать долги (дебиторку и кредиторку), выставлять счета и отмечать их оплату с автоматическим созданием проводок.`,
  },

  // ─── AFTab (Главная: Счета и фонды) ───
  af: {
    // Вкладки
    tabSummary: 'Сводка',
    tabAccounts: 'Счета',
    tabFunds: 'Фонды',
    tabArchive: (count) => `Архив (${count})`,
    // Стат-карточки
    onAccounts: 'На счетах',
    inFunds: 'В фондах',
    incomeMonth: 'Доход (за месяц)',
    expenseMonth: 'Расход (за месяц)',
    // Типы операций (для getTypeLabel)
    transferFundShort: 'Перевод ф.',
    // Кассовый разрыв
    cashGapTitle: 'Кассовый разрыв (дефицит)!',
    cashGapDesc: (fundTotal, accTotal) => `Сумма в фондах (${fundTotal}) превышает реальные деньги на счетах (${accTotal}). Проверьте балансы.`,
    // Состояния
    accountsState: 'Состояние счетов',
    fundsState: 'Состояние фондов',
    adjustFundsTitle: 'Корректировка балансов',
    adjustTitle: 'Корректировка балансов фондов',
    adjustSubtitle: 'Ручное изменение баланса каждого фонда',
    adjustTotalAccounts: 'Общая сумма на счетах',
    adjustTotalFunds: 'Итого в фондах',
    adjustBalanced: '✓ Балансы совпадают',
    adjustNeedAdd: 'Нужно распределить ещё',
    adjustNeedRemove: 'Превышение на фондах',
    adjustSave: 'Сохранить',
    adjustAuditDesc: 'Ручная корректировка балансов фондов',
    // FundManager
    fmTitle: 'Корректировка фондов',
    fmSubtitle: 'Проценты, балансы, порядок, добавление и удаление',
    fmHelp: 'Управляйте фондами: меняйте проценты, балансы, порядок. Добавляйте и удаляйте фонды с перераспределением.',
    fmTogglePercent: 'Менять проценты',
    fmToggleBalance: 'Менять баланс',
    fmTogglePctHint: 'Проценты влияют только на распределение новых поступлений',
    fmToggleBalHint: 'Изменение текущего баланса каждого фонда',
    fmFundName: 'Название',
    fmBalance: 'Баланс',
    fmDeleteFund: 'Удалить фонд',
    fmAddFund: 'Добавить фонд',
    fmInfoAccounts: 'Сумма на счетах',
    fmInfoFunds: 'Сумма в фондах',
    fmInfoBalanced: '✓ Совпадает',
    fmInfoDiff: 'Разница',
    fmPctTotal: 'Процентов',
    fmAddStep1: 'Новый фонд',
    fmAddStep2: 'Откуда взять проценты?',
    fmAddStep2Hint: 'Укажите, сколько процентов снять с каждого существующего фонда',
    fmAddNeedTake: 'Нужно снять',
    fmAddTaken: 'Указано',
    fmAddBack: 'Назад',
    fmAddConfirm: 'Добавить',
    fmAddNext: 'Далее',
    fmAddPctMismatch: (need, taken) => `Нужно распределить ${need}%, указано ${taken}%`,
    fmDelTitle: name => `Удаление фонда «${name}»`,
    fmDelStep1Hint: pct => `Распределите ${pct}% между оставшимися фондами`,
    fmDelAutoDistribute: 'Распределить пропорционально',
    fmDelStep2Hint: bal => `Куда перевести ${bal}?`,
    fmDelWriteOff: 'Списать (обнулить)',
    fmDelConfirm: 'Удалить',
    fmDelPctMismatch: (need, taken) => `Нужно распределить ${need}%, указано ${taken}%`,
    fmValPctNot100: pct => `Сумма процентов: ${pct}%. Должно быть 100%.`,
    fmValPctAction: d => `Добавьте новый фонд на ${d}% или распределите ${d}% по имеющимся фондам.`,
    fmValPctOverAction: d => `Уберите лишние ${d}% из имеющихся фондов.`,
    fmValFundsExceed: 'Сумма фондов превышает сумму на счетах. Уменьшите балансы.',
    fmValBalMismatch: diff => `Разница между счетами и фондами: ${diff}. Баланс фондов должен совпадать с балансом счетов.`,
    fmAddFreePct: pct => `Свободно ${pct}% — не распределены по фондам`,
    fmAddNoFreePct: 'Все 100% распределены. Проценты для нового фонда будут взяты из существующих.',
    fmAddHasFree: pct => `У вас есть свободные ${pct}% (без фонда).`,
    fmAddNeedFromOthers: (total, need) => `Новый фонд: ${total}%. Нужно снять с других фондов: ${need}%.`,
    fmAuditAdjust: 'Корректировка фондов',
    fmSave: 'Сохранить',
    fundNamePlaceholder: 'Название фонда',
    // Дубликаты
    dupAccName: 'Счёт с таким названием уже существует',
    dupAccIban: 'Счёт с таким IBAN уже существует',
    dupFundName: 'Фонд с таким названием уже существует',
    dupFundNameSave: 'Несколько фондов с одинаковым названием. Переименуйте.',
    // Пустые списки
    noActiveAccounts: 'Нет активных счетов',
    noActiveFunds: 'Нет активных фондов',
    noActiveDebts: 'Нет активных долгов',
    noOps: 'Нет операций',
    // Плейсхолдеры
    accountNamePlaceholder: 'Название счёта',
    ibanPlaceholder: 'ИИК / IBAN (Обязательно)',
    fundNamePlaceholder: 'Название',
    // Подсказки
    ibanTooltip: 'ИИК необходим для автоматического распознавания счетов при импорте PDF.',
    // Бейджики
    primaryBadge: 'Основной',
    hiddenAccountTitle: 'Скрытый счет',
    // Кнопки title
    addToStash: 'Пополнить заначку',
    takeFromStash: 'Взять из заначки',
    quickIncomeTitle: 'Быстрое поступление',
    quickExpenseTitle: 'Быстрый расход',
    historyBtn: 'История',
    // Долги (панели)
    receivableLabel: 'Дебиторка',
    payableLabel: 'Кредиторка',
    theyOweUs: 'Нам должны',
    weOwe: 'Мы должны',
    dueDate: (date) => `До: ${date}`,
    // Модалка «Будущее пополнение фондов» (клик по сумме дебиторки)
    futureFundsTitle: 'Будущее пополнение фондов',
    futureFundsHint: 'Эти деньги ещё не поступили — расчёт, сколько попадёт в каждый фонд, если должники заплатят полностью',
    futureFundsNoDebts: 'Нет активных обязательств',
    futureFundsNoFunds: 'Нет активных фондов',
    // Последние
    recentOps: 'Последние операции',
    // Alerts
    enterAccountName: 'Введите название счета',
    enterIban: 'Введите ИИК / IBAN. Это необходимо для автоматического распознавания счетов при импорте PDF.',
    exceedsHundred: 'Превысит 100%',
    // Confirm
    archiveAccountMsg: (name) => `Скрыть счет "${name}" в архив?`,
    archiveAccountExtra: 'Этот счет исчезнет из списков выбора, но все связанные с ним уже проведенные операции останутся без изменений в отчетах. Вы сможете восстановить его из Единого Архива.',
    archiveFundMsg: (name) => `Отправить фонд "${name}" в архив?`,
    archiveFundExtra: 'Фонд будет скрыт, но все операции по нему останутся в истории. Вы сможете восстановить его из Единого Архива.',
    defaultCompanyName: 'Наша компания',
    // Модалка счета (AccountModal)
    accModalEdit: 'Изменить',
    accModalArchive: 'В архив',
    accModalPrimaryLabel: 'Основной счет',
    accModalHiddenLabel: 'Скрытый счет (Личный)',
    addAccPrimaryHint: 'Основной счет используется по умолчанию при создании операций. Может быть только один.',
    addAccHiddenHint: 'Скрытый счет не отображается в общем балансе и статистике. Подходит для личных накоплений или заначки.',
    accModalVisibilityLabel: 'Кто может видеть этот счет?',
    accModalVisMe: 'Только я (владелец)',
    accModalVisMeAccountant: 'Я и бухгалтер',
    accModalVisAdmins: 'Я, бухгалтер и администратор',
    accModalVisAll: 'Вся команда',
    accModalIbanLabel: 'ИИК / IBAN (Обязательно)',
    accModalIbanDisplay: 'ИИК / IBAN:',
    accModalBalance: (bal) => `Баланс: ${bal}`,
    accModalVisibilityDisplay: 'Видимость:',
    accModalEnterName: 'Введите название счета',
    accModalEnterIban: 'Введите ИИК / IBAN. Это необходимо для автоматического распознавания счетов.',
    accModalIncomeCategories: 'Категории доходов',
    accModalAddBtn: 'Добавить',
    accModalNoFreeCategories: 'Нет свободных категорий',
    accModalNoLinkedCategories: 'Нет привязанных категорий',
    accModalRemoveBtn: 'Убрать',
    accModalClients: 'Клиенты',
    accModalAllClientsLinked: 'Все клиенты привязаны',
    accModalNoLinkedClients: 'Нет привязанных клиентов',
    // Модалка фонда (FundModal)
    fundModalExceedsHundred: 'Сумма процентов фондов превысит 100%',
    fundModalBelowHundred: (total, missing) => `Сумма процентов = ${total}%. Должна быть ровно 100%. Не хватает ${missing}%`,
    fundModalInFund: (bal) => `В фонде: ${bal}`,
    fundModalExpenseCategories: 'Категории расходов',
    fundModalNoFreeCategories: 'Нет свободных категорий',
    fundModalNoLinkedCategories: 'Нет привязанных категорий',
    fundModalRemoveBtn: 'Убрать',
    // Быстрое поступление (QuickIncomeModal)
    qIncTitle: 'Быстрое поступление',
    qIncAccountLabel: 'Счёт зачисления',
    qIncCurrentBalance: 'Текущий баланс',
    qIncDebtLabel: '1. Погашение долга (Опционально)',
    qIncNewRevenue: '-- Это новая выручка (не долг) --',
    qIncNoDebtors: 'Нет активных должников',
    qIncAmountLabel: 'Сумма поступления *',
    qIncCategoryLabel: 'Категория (За что?)',
    qIncNoCategory: 'Без категории',
    qIncClientLabel: 'Клиент (Кто платит?)',
    qIncClientNone: 'Не указан',
    qIncCommentLabel: 'Комментарий',
    qIncCommentPlaceholder: 'Детали платежа...',
    qIncSubmit: 'Зачислить',
    qIncDebtPayComment: 'Оплата долга',
    qIncEnterAmount: 'Введите корректную сумму',
    qIncRemainder: (amount) => `остаток: ${amount}`,
    // Быстрый расход (QuickExpenseModal)
    qExpTitle: 'Быстрый расход',
    qExpFundLabel: 'Фонд списания',
    qExpFundAvailable: 'В фонде доступно',
    qExpPayDebtLabel: 'Погасить обязательство',
    qExpNoDebtBinding: '— Без привязки к обязательству —',
    qExpDebtDefault: 'Обязательство',
    qExpDebtRemainder: (amount) => `остаток: ${amount}`,
    qExpAccountLabel: 'Счёт списания (откуда уйдут реальные деньги)',
    qExpCategoryLabel: 'Категория расхода',
    qExpNoCategory: 'Без категории',
    qExpAmountLabel: 'Сумма расхода',
    qExpCommentLabel: 'Комментарий',
    qExpCommentPlaceholder: 'Кому, за что...',
    qExpSubmit: 'Списать',
    qExpEnterAmount: 'Введите сумму',
    qExpFundInsufficient: (fundName, available) => `В фонде "${fundName}" не хватает средств. Доступно: ${available}`,
    qExpAccountInsufficient: (available) => `На выбранном счёте не хватает средств. Доступно: ${available}`,
    qExpPayDebtComment: 'Погашение обязательства',
    // Скрытый счет (QuickHiddenAdjustModal)
    qHidAddTitle: 'Пополнить заначку',
    qHidTakeTitle: 'Взять из заначки',
    qHidAccountLabel: 'Скрытый счет',
    qHidCurrentBalance: 'В заначке сейчас',
    qHidAddAmountLabel: 'Сумма пополнения',
    qHidTakeAmountLabel: 'Сумма изъятия',
    qHidCommentLabel: 'Комментарий (для себя)',
    qHidAddPlaceholder: 'Скинул с премии...',
    qHidTakePlaceholder: 'Потратил на подарок...',
    qHidSubmitAdd: 'Пополнить',
    qHidSubmitTake: 'Изъять',
    qHidEnterAmount: 'Введите сумму',
    qHidInsufficient: (available) => `Недостаточно средств. В заначке: ${available}`,
    qHidDefaultCommentAdd: 'Пополнение заначки',
    qHidDefaultCommentTake: 'Изъятие из заначки',
    // Перевод с фонда в заначку (FundToHiddenTransferModal)
    qFTHTitle: 'Перевод в заначку',
    qFTHFundLabel: 'Из фонда',
    qFTHAccountLabel: 'Со счёта',
    qFTHHiddenLabel: 'В заначку (скрытый счёт)',
    qFTHAmountLabel: 'Сумма',
    qFTHCommentLabel: 'Комментарий (для себя)',
    qFTHCommentPlaceholder: 'На личные траты...',
    qFTHSubmit: 'Перевести',
    qFTHEnterAmount: 'Введите сумму',
    qFTHFundInsufficient: (fundName, available) => `В фонде "${fundName}" не хватает средств. Доступно: ${available}`,
    qFTHAccountInsufficient: (available) => `На выбранном счёте не хватает средств. Доступно: ${available}`,
    qFTHNoHidden: 'Сначала создайте скрытый счёт',
    qFTHButtonTitle: 'Перевести в заначку',
    qFTHDefaultComment: 'Перевод в заначку',
    qFTHHint: 'Для фонда это списание (расход), сумма не попадёт в отчёты о доходах.',
    // Фоллбэки подсказок
    helpAccount: 'Счёт — ваш банковский счёт, касса или кошелёк. На него поступают деньги и с него происходят расходы. Вы можете привязать категории дохода и контрагентов к счёту.',
    helpFund: 'Фонд — это виртуальная копилка. При каждом поступлении деньги автоматически распределяются по фондам согласно процентам. Вы можете привязать категории расхода к фонду.',
  },

  // ─── PeriodFilter (общий компонент) ───
  periodFilter: {
    label: 'Период:',
    d30: '30 дней',
    m3: '3 месяца',
    m6: '6 месяцев',
    y1: 'Год',
    all: 'Всё время',
    custom: 'Свой период',
    from: 'с',
    to: 'по',
    contractorsLabel: 'Суммы по контрагентам за:',
    categoriesLabel: 'Суммы по категориям за:',
  },

  // ─── MultiOrgReportsTab ───
  multiOrg: {
    title: 'Сводка по организациям',
    periodLabel: (period, count) => `${period} · ${count} организаций`,
    totalIncome: 'ОБЩИЙ ДОХОД',
    totalExpense: 'ОБЩИЙ РАСХОД',
    netProfit: 'ЧИСТАЯ ПРИБЫЛЬ',
    onAccounts: 'НА СЧЕТАХ',
    comparison: 'Сравнение организаций',
    thOrg: 'ОРГАНИЗАЦИЯ',
    thIncome: 'Доход',
    thExpense: 'Расход',
    thProfit: 'Прибыль',
    thBalance: 'Баланс',
    thOps: 'Операции',
    incomeVsExpense: 'Доход vs Расход',
    incomeShare: 'Доля доходов',
    topExpenses: 'Топ категорий расходов',
    noExpenses: 'Нет расходов',
    periodMonth: 'Месяц',
    periodQuarter: 'Квартал',
    periodYear: 'Год',
    needMinOrgs: 'Для сравнения нужно минимум 2 организации',
    addOneMoreOrg: 'Добавьте ещё одну организацию для просмотра сравнительной аналитики',
    currentMonth: 'За текущий месяц',
    currentQuarter: 'За текущий квартал',
    currentYear: 'За текущий год',
    noIncomeForPeriod: 'Нет данных о доходах за период',
    incomeBarName: 'Доход',
    expenseBarName: 'Расход',
  },

  // ─── Подписка ───
  subscription: {
    useBonuses: 'Использовать бонусы',
    bonusAvailable: (amount) => `Доступно: ${amount}`,
    basePrice: 'Базовая цена',
    bonusDiscount: 'Бонусы',
    finalPrice: 'К оплате',
    title: 'Подписка',
    subtitle: 'Управление подпиской и оплатой',
    planTab: 'Тариф',
    historyTab: 'История оплат',
    statusTrial: 'Пробный период',
    statusActive: 'Подписка активна',
    statusExpired: 'Подписка истекла',
    daysLeft: (n) => `Осталось ${n} дн.`,
    expiresAt: 'до',
    expiredHint: 'Создание и редактирование данных заблокировано. Оформите подписку.',
    pricingTitle: 'Стоимость подписки',
    firstOrg: 'Первая организация',
    extraOrg: 'Каждая дополнительная',
    month: 'мес',
    yourOrgs: (n) => `Ваши организации (${n})`,
    choosePeriod: 'Выберите период',
    month1: '1 месяц',
    month3: '3 месяца',
    month6: '6 месяцев',
    month12: '12 месяцев',
    monthN: (n) => `${n} мес.`,
    totalFor: (m) => `за ${m} мес.`,
    payBtn: 'Оплатить',
    payDisabledHint: 'Оплата временно недоступна. Для активации свяжитесь с администратором: inmonex@support.kz',
    historyEmpty: 'Нет оплат',
    monthsLabel: 'мес.',
    paymentSuccess: 'Оплачено',
    paymentPending: 'Ожидание',
    paymentFailed: 'Ошибка',
    expiringBanner: (days) => `Пробный период заканчивается через ${days} дн. Оформите подписку!`,
    expiredBanner: 'Подписка истекла. Оформите подписку для продолжения работы.',
    renewBtn: 'Подписка',
    modalTitle: 'Подписка истекла',
    modalText: 'Для создания и редактирования данных необходима активная подписка. Все ваши данные сохранены и доступны для просмотра.',
    modalBtn: 'Оформить подписку',
    selectOrgs: 'Выберите организации для подписки',
    selectOrgsHint: 'Первая организация — 5 500 ₸/мес. Каждая следующая — 3 500 ₸/мес.',
    selectedOrgs: (n) => `Выбрано организаций: ${n}`,
    savings: (s) => `Экономия: ${s}`,
    selectAtLeast1: 'Выберите хотя бы 1 организацию',
  },
  deletion: {
    deleteOrg: 'Удалить организацию',
    deleteOrgConfirm: (name) => `Вы действительно хотите удалить организацию «${name}»? Все данные будут безвозвратно утеряны.`,
    deleteOrgHint: 'Удаление организации приведёт к безвозвратной потере всех данных: счетов, фондов, операций, транзакций и истории. Перед удалением рекомендуем сделать резервную копию.',
    deleteAccount: 'Удалить аккаунт',
    deleteAccountConfirm: 'Вы действительно хотите удалить свой аккаунт? Все ваши организации и данные будут безвозвратно утеряны.',
    backupTitle: 'Резервная копия',
    backupOrgText: 'Рекомендуем сохранить резервную копию данных организации перед удалением.',
    backupAccountText: 'Рекомендуем сохранить резервную копию всех ваших данных перед удалением аккаунта.',
    downloadBackup: 'Скачать резервную копию',
    backupDownloaded: 'Резервная копия сохранена',
    deleteWithoutBackup: 'Удалить без копии',
    confirmDelete: 'Подтвердить удаление',
    deleting: 'Удаление...',
    orgDeleted: 'Организация удалена',
    accountDeleted: 'Аккаунт удалён',
    backupOrg: 'Резервная копия организации',
    backupAll: 'Резервная копия всех данных',
    step1of2: 'Шаг 1 из 2',
    step2of2: 'Шаг 2 из 2',
  },
};

// ══════════════════════════════════════════════════════════
// ҚАЗАҚ ТІЛІ
// ══════════════════════════════════════════════════════════
const kz = {

  // ─── Жалпы (бірнеше файлдарда қолданылады) ───
  common: {
    save: 'Сақтау',
    cancel: 'Болдырмау',
    confirm: 'Растау',
    delete: 'Жою',
    edit: 'Өңдеу',
    close: 'Жабу',
    add: 'Қосу',
    back: 'Артқа',
    next: 'Келесі',
    search: 'Іздеу...',
    noData: 'Деректер жоқ',
    loading: 'Жүктелуде...',
    yes: 'Иә',
    no: 'Жоқ',
    all: 'Барлығы',
    archive: 'Мұрағат',
    noRights: 'Құқық жоқ',
    check: 'Тексеру',
    show: 'Көрсету',
    hide: 'Жасыру',
    reset: 'Тастау',
    download: 'Жүктеп алу',
    noCategoryShort: 'Санатсыз',
    noComment: 'Түсініктемесіз',
    noDescription: 'Сипаттамасыз',
    unknown: 'Белгісіз',
    notSpecified: 'Көрсетілмеген',
    notSpecifiedFem: 'Көрсетілмеген',
    defaultUser: 'Пайдаланушы',
    system: 'Жүйе',
    toArchive: 'Мұрағатқа',
    saving: 'Сақталуда...',
    genericError: 'Қате',
    deleteError: 'Жою кезіндегі қате',
    pdfUploadError: 'PDF жүктеу қатесі',
    unknownError: 'белгісіз қате',
  },

  defaultFunds: {
    salary: 'Жалақы',
    taxes: 'Салықтар',
    profit: 'Пайда',
  },

  // ─── Пайдаланушы рөлдері ───
  roles: {
    owner: 'Иесі',
    admin: 'Әкімші',
    accountant: 'Бухгалтер',
    viewer: 'Қарау',
  },

  // ─── Ай атаулары ───
  months: ['Қаңтар', 'Ақпан', 'Наурыз', 'Сәуір', 'Мамыр', 'Маусым', 'Шілде', 'Тамыз', 'Қыркүйек', 'Қазан', 'Қараша', 'Желтоқсан'],
  monthsShort: ['қаң', 'ақп', 'нау', 'сәу', 'мам', 'мау', 'шіл', 'там', 'қыр', 'қаз', 'қар', 'жел'],

  // ─── Сүзгі кезеңдері ───
  periods: {
    day: 'Күн',
    week: 'Апта',
    month: 'Ай',
    year: 'Жыл',
    twoYears: '2 жыл',
    all: 'Барлығы',
  },

  // ─── Апта күндері ───
  weekdays: ['Дс', 'Сс', 'Ср', 'Бс', 'Жм', 'Сб', 'Жс'],

  // ─── Валюталар (белгілер) ───
  currencies: {
    KZT: 'Теңге (KZT)',
    RUB: 'Ресей рублі (RUB)',
    USD: 'АҚШ доллары (USD)',
    EUR: 'Еуро (EUR)',
    CNY: 'Қытай юані (CNY)',
    AED: 'БАӘ дирхамы (AED)',
    GBP: 'Британ фунты (GBP)',
    TRY: 'Түрік лирасы (TRY)',
    UZS: 'Өзбек сомы (UZS)',
    KGS: 'Қырғыз сомы (KGS)',
    TJS: 'Тәжік сомониі (TJS)',
    BYN: 'Беларусь рублі (BYN)',
    UAH: 'Украин гривнасы (UAH)',
    GEL: 'Грузин лариі (GEL)',
    AZN: 'Әзірбайжан манаты (AZN)',
    AMD: 'Армян драмы (AMD)',
    MDL: 'Молдова лейі (MDL)',
    TMT: 'Түрікмен манаты (TMT)',
  },

  // ─── Транзакция түрлері ───
  txTypes: {
    income: 'Түсім',
    expense: 'Шығыс',
    transfer: 'Аударым',
    transferFund: 'Қор аударымы',
    transferAccount: 'Шоттар арасындағы аударым',
    transferFundFull: 'Қорлар арасындағы аударым',
  },

  // ─── HomeScreen (App.js) ───
  home: {
    brand: 'INMONEX',
    subtitle: 'Ақшаң бақылауда. Таңдау өзіңде',
    yourOrgs: 'Сіздің ұйымдарыңыз:',
    createOrg: 'Ұйым құру',
    light: 'Ашық',
    dark: 'Қараңғы',
    deleteOrgConfirm: 'Ұйымды және барлық деректерді жою керек пе?',
    // Жаңа кілттер
    loginTitle: 'Жүйеге кіру',
    forgotPassword: 'Құпия сөзді ұмыттыңыз ба?',
    registerLink: 'Тіркелу',
    addOrg: 'Ұйым құру',
    passwordHint: 'Аккаунттар туралы кеңес:',
    contactOwner: 'ұйым иесіне хабарласыңыз',
    noUsersYet: 'Тіркелген пайдаланушылар жоқ.',
    createFirstOrg: 'Жұмысты бастау үшін бірінші ұйымды құрыңыз',
    loginHint: (login, len) => `Логин: ${login} · Кеңес: құпия сөз ${len} таңбадан тұрады`,
    cantRememberPassword: 'Құпия сөзді есіңізге түсіре алмасаңыз, ұйым иесіне хабарласыңыз.',
  },

  // ─── LoginScreen (App.js) ───
  login: {
    loginPlaceholder: 'Логин немесе email',
    passwordPlaceholder: 'Құпия сөз',
    enter: 'Кіру',
    wrongCredentials: 'Логин немесе құпия сөз қате',
    // Stage 11: 2FA қосылған пайдаланушылар үшін екінші экран
    twoFA: {
      title: 'Қосымшадан кодты енгізіңіз',
      desc: 'Google Authenticator (немесе басқа TOTP-қосымша) ашып, 6 таңбалы кодты енгізіңіз. Қосымшаға қол жеткізу мүмкіндігіңіз болмаса — XXXX-XXXX түріндегі резервтік кодты пайдаланыңыз.',
      codePlaceholder: '6 таңба немесе XXXX-XXXX',
      verifyBtn: 'Кіру',
      verifying: 'Тексеру...',
      backToLogin: '← Логинге қайту',
      expired: '2FA сессиясының мерзімі өтті. Қайта кіріңіз.',
      invalidCode: 'Қате код',
      backupCodesLow: (n) => `Назар аударыңыз: тек ${n} резервтік код қалды. Қауіпсіздік параметрлерінде жаңасын жасаңыз.`,
      usedBackup: 'Сіз резервтік код арқылы кірдіңіз. Бір код пайдаланылды.',
    },
  },

  // ─── "Құпия сөзді ұмыттыңыз ба" модалы ───
  forgotPwd: {
    title: 'Құпия сөзді ұмыттыңыз ба?',
    desc: 'Тіркелу кезінде көрсетілген email-ді енгізіңіз. Құпия сөзді қалпына келтіру сілтемесін жібереміз.',
    noEmailHint: 'Егер сізде email болмаса немесе оған кіру жоқ болса — компания иесіне хабарласыңыз. Ол құпия сөзді қолмен қалпына келтіре алады.',
    emailPh: 'email@example.com',
    sendBtn: 'Сілтеме жіберу',
    sentMsg: 'Егер бұл email тіркелген болса, оған құпия сөзді қалпына келтіру сілтемесі жіберілді. Поштаңызды (спам қалтасын да) тексеріңіз. Сілтеме 1 сағат жарамды.',
    close: 'Жабу',
    failed: 'Сұранысты жіберу мүмкін болмады. Қайта көріңіз.',
  },

  // ─── Құпия сөзді қалпына келтіру парағы ───
  resetPwd: {
    logoSub: 'Қаражатты бақылау. Шешім еркіндігі',
    title: 'Жаңа құпия сөз құру',
    subtitle: 'Аккаунтыңыз үшін сенімді құпия сөз ойлап табыңыз',
    newPwd: 'Жаңа құпия сөз',
    newPwdPh: 'Кемінде 8 таңба',
    confirmPwd: 'Жаңа құпия сөзді қайталаңыз',
    confirmPwdPh: 'Құпия сөзді тағы енгізіңіз',
    pwdTooShort: 'Құпия сөз кемінде 8 таңбадан болуы керек',
    pwdMismatch: 'Құпия сөздер сәйкес келмейді',
    save: 'Сақтау',
    saving: 'Сақталуда...',
    successMsg: 'Құпия сөз сәтті өзгертілді! Кіру парағы ашылады...',
    noToken: 'Құпия сөзді қалпына келтіру токені табылмады. Жаңа сілтеме сұраңыз.',
    failed: 'Құпия сөзді өзгерту мүмкін болмады. Қайта көріңіз.',
  },

  // ─── Email өзгерту OTP-пен ───
  emailChange: {
    title: 'Email өзгерту',
    step1Desc: 'Жаңа email-ді енгізіңіз. Оған растау коды жіберіледі.',
    newEmailLabel: 'Жаңа email',
    newEmailPh: 'new@example.com',
    sendCode: 'Код жіберу',
    step2Desc: 'Біз жаңа email-ге 6 таңбалы код жібердік. Оны төменге енгізіңіз.',
    codeLabel: 'Хаттан алынған код',
    codePh: '123456',
    confirm: 'Растау',
    successMsg: 'Email сәтті өзгертілді',
    rateLimit: (s) => `${s} сек. күтіңіз.`,
    invalidCode: 'Код қате',
    expired: 'Кодтың мерзімі өтті, жаңа кодты сұраңыз',
    changeBtn: 'Өзгерту',
    sameEmail: 'Жаңа email ағымдағыдай',
    sendError: 'Кодты жіберу мүмкін болмады',
  },

  // ─── Phase 10: Optimistic locking (App.js) ───
  sync: {
    conflictTitle: 'Деректер жаңартылды',
    conflictBody: 'Басқа пайдаланушы өзгерістерді бұрын сақтап үлгерді. Жаңа нұсқа қолданылды — қажет болса, әрекетіңізді қайталаңыз.',
  },

  // ─── SetupWizard (App.js) ───
  setup: {
    title: 'INMONEX БАПТАУ',
    stepOf: (step, total) => `${total} қадамның ${step}-і`,
    orgColorLabel: 'Ұйым түсі',
    orgColorTaken: (label) => `${label} (бос емес)`,
    welcomeText: 'INMONEX-ке қош келдіңіз! Компанияңыз туралы негізгі мәліметтерді толтырыңыз және Иесі аккаунтын жасаңыз. Бұл деректер қауіпсіз кіру және есептерде қолданылады.',
    companyName: 'Компания немесе жоба атауы',
    companyPlaceholder: 'Мысалы: Рога и Копыта ЖШС',
    yourName: 'Сіздің атыңыз',
    namePlaceholder: 'Иван Иванов',
    loginLabel: 'Кіру логині',
    loginPlaceholder: 'admin',
    passwordLabel: 'Сенімді құпия сөз ойлап табыңыз',
    passwordMinLength: 'Кемінде 8 таңба',
    passwordsDoNotMatch: 'Құпия сөздер сәйкес келмейді',
    confirmPasswordLabel: 'Құпия сөзді растаңыз',
    confirmPasswordPlaceholder: 'Құпия сөзді қайталаңыз',
    emailLabel: 'Email (чектер және қалпына келтіру үшін)',
    emailPlaceholder: 'director@company.com',
    emailConfirmed: 'Расталған',
    confirmEmail: 'Растау',
    codePlaceholder: 'Хаттағы код',
    wrongCode: 'Код қате. Қайта көріңіз.',
    codeSent: 'Код поштаңызға жіберілді',
    codeSendError: 'Кодты жіберу мүмкін болмады',
    codeSending: 'Жіберілуде...',
    verificationError: 'Кодты тексеру қатесі',
    phoneLabel: 'Телефон',
    phonePlaceholder: '77770001122',
    phoneMinDigits: 'Кемінде 7 сан',
    phoneHint: 'Тек сандар, бос орын жоқ. Бос орындарды жүйе өзі қояды.',
    accountLabel: (i) => `Шот ${i}`,
    ibanLabel: 'Шот IBAN',
    ibanPlaceholder: 'KZ00 0000 0000 0000 0000',
    balanceLabel: 'Ағымдағы баланс (міндетті емес)',
    balancePlaceholder: '0',
    balanceHint: 'Балансты көрсетсеңіз — ол қорларға автоматты түрде бөлінеді',
    addAccount: 'Шот қосу',
    fundsWhy: 'Қорлар не үшін керек?',
    fundsExplain: 'Қорлар түсімді автоматты түрде бөледі: Жалақы, Салықтар, Дивидендтер.',
    fundsSkipHint: 'Оларды қазір баптай аласыз немесе жойып, бұл қадамды өткізе аласыз. INMONEX кейін оларды қосуды ұсынады.',
    percentHint: (pct) => `Пайыздар сомасы = 100% болуы керек. Қазір: ${pct}%`,
    fundsPctError: 'Барлық қорлардың пайыздарының сомасы дәл 100% болуы керек. Жалғастыру үшін мәндерді түзетіңіз.',
    duplicateAccountName: 'Шоттардың атаулары қайталанбауы керек',
    duplicateIban: 'Шоттардың IBAN-дары қайталанбауы керек',
    duplicateFundName: 'Қорлардың атаулары қайталанбауы керек',
    fundDistribution: (amount) => `Бастапқы баланс: ${amount} — қорлар бойынша бөлу:`,
    fundNamePlaceholder: 'Қор атауы',
    addFund: 'Қор қосу',
    removeAllFunds: 'Барлығын жойып, өткізу',
    skipFunds: 'Қорларды өткізу',
    noFundsConfirm: 'Сіз бірде-бір қор қоспадыңыз. Шығыстар тікелей шоттардан алынады. Жалғастыру керек пе?',
    ownerLabel: 'Иесі:',
    emailLabelShort: 'Email:',
    accountsCount: 'Шоттар:',
    fundsCount: 'Қорлар:',
    agreementText: 'Мен шарттарды оқыдым және қабылдаймын',
    tosLink: 'Пайдалану келісімі',
    privacyLink: 'Құпиялылық саясаты',
    consentLink: 'Жеке деректерді жинау және өңдеуге келісім',
    finishSetup: 'Тіркелуді аяқтау',
    existingUserInfo: 'Сіз қазірдің өзінде кірдіңіз',
    // ─── Қадамдардың кеңейтілген сипаттамалары ───
    step1Title: 'Компанияңыз және аккаунт',
    step1Desc: 'Бастайық! Компания деректерін енгізіңіз және басшы аккаунтын жасаңыз. Кейін әр түрлі қолжетімділік құқығымен басқа қызметкерлерді қоса аласыз.',
    step2Title: 'Сіздің банк шоттарыңыз',
    step2Desc: 'Шот — бұл сіздің нақты банк шотыңыз немесе кассаңыз. Мысалы, Kaspi Бизнес, Halyk немесе кассадағы қолма-қол ақша. Олар арқылы клиенттерден төлем қабылдайсыз және шығыстарды төлейсіз.',
    step2Examples: 'Kaspi Бизнес · Halyk · Forte · Қолма-қол ақша · Jýsan',
    step2Hint: 'Кем дегенде бір шот қосыңыз. Кейін баптауларда тағы қоса аласыз.',
    step3Title: 'Қорлар — қаржылық қауіпсіздігіңіз',
    step3Desc: 'Қорлар — жүйе ішіндегі «қоржындар». Әр түсім белгіленген пайыздарға сәйкес автоматты түрде бөлінеді. Бұл салықтарға, жалақыларға және пайдаға алдын ала ақша жинауға көмектеседі.',
    step3Example: 'Мысал: клиент 1 000 000 ₸ төледі → 500 000 ₸ «Жалақы» қорына, 200 000 ₸ «Салықтар» қорына, 300 000 ₸ «Пайда» қорына түседі.',
    step3FlowTitle: 'Бұл қалай жұмыс істейді?',
    flowClientPays: 'Клиент төлейді',
    flowYourAccount: 'Сіздің шотыңыз',
    flowFundsNotConfigured: 'Қорлар бапталмаған',
    examplesTitle: 'Атау мысалдары:',
    accountExample: 'Мысалы: Kaspi Бизнес',
    step4Title: 'Бәрі дайын! Деректерді тексеріңіз',
    step4Desc: 'Ұйымды жасағаннан кейін сіз жұмыс аймағына кіресіз, онда толық қаржылық есеп жүргізе аласыз.',
    step4Next1: 'Кіріс және шығыс санаттарын қосасыз',
    step4Next2: 'Контрагенттер жасайсыз (клиенттер мен жеткізушілер)',
    step4Next3: 'Операцияларды тіркеп, есептер алуды бастайсыз',
    step4WhatsNext: 'Не болады?',
    switchTo: (name) => `Ауыстыру: ${name}`,
  },

  // ─── Welcome Tour ───
  tour: {
    welcome: 'INMONEX-ке қош келдіңіз!',
    welcomeDesc: '30 секундта негізгі құралдарды көрсетейік. Турды кез келген сәтте өткізе аласыз.',
    step1Title: '💳 Шоттар мен қорлар',
    step1Desc: 'Мұнда барлық шоттар мен қорларды көресіз. Ақша түсімін тез тіркеу үшін шоттағы «+» белгісін басыңыз. Ақша автоматты түрде қорларға бөлінеді.',
    step2Title: '📚 Анықтамалықтар',
    step2Desc: 'Мұнда контрагенттер (клиенттер мен жеткізушілер) және шығыс/кіріс санаттары орналасқан. Оларды неғұрлым дәл баптасаңыз, есептер соғұрлым пайдалы болады.',
    step3Title: '⚡ Операциялар',
    step3Desc: 'Негізгі құрал! Түсімдерді, шығыстарды және аударымдарды тіркеңіз. Әр операция автоматты түрде есептер мен баланстарда көрсетіледі.',
    step4Title: '📊 Есептер',
    step4Desc: 'Мұнда ОПжЗ және АҚҚ — екі негізгі қаржылық есеп қалыптасады. Қанша табыс тауып, ақша қайда кететінін көресіз.',
    skipBtn: 'Өткізу',
    nextBtn: 'Келесі',
    prevBtn: 'Артқа',
    finishBtn: 'Жұмысты бастау!',
    stepOf: (step, total) => `${step} / ${total}`,
  },

  // ─── Sidebar / MainApp (App.js) ───
  sidebar: {
    tabs: {
      af: 'Басты бет',
      data: 'Құрылым',
      ops: 'Операциялар',
      debts: 'Міндеттемелер',
      tx: 'Транзакциялар',
      reports: 'Есептер',
      history: 'Тарих',
      admin: 'Баптаулар',
      myaccount: 'Менің аккаунтым',
      multiorg: 'Жиынтық',
      support: 'Қолдау',
      referral: 'Рефералдар',
      subscription: 'Жазылым',
    },
    cooperation: 'Ынтымақтастық',
    logout: 'Шығу',
    backToOrgs: '← Ұйым.',
    menu: '☰ Мәзір',
    copyright: (year) => `© ${year} INMONEX`,
    allRightsReserved: 'Барлық құқықтар қорғалған.',
    tosLink: 'Пайдалану келісімі',
    privacyLink: 'Құпиялылық саясаты',
    dataProcessingLink: 'Деректерді өңдеу',
    // Жаңа кілттер
    addOrg: 'Ұйым қосу',
    allOrgs: 'Барлық ұйым.',
    sync: {
      updated: 'Жаңартылды',
      justNow: 'жаңа ғана',
      secondsAgo: 'N сек бұрын',
      minutesAgo: 'N мин бұрын',
      hoursAgo: 'N сағ бұрын',
    },
  },

  // ─── Реферал бағдарламасы (ReferralTab.js) ───
  referral: {
    title: 'Реферал бағдарламасы',
    subtitle: 'Достарыңызды шақырып, бонус алыңыз',
    myLink: 'Менің реферал сілтемем',
    copyLink: 'Сілтемені көшіру',
    copied: 'Көшірілді!',
    myCode: 'Менің кодым',
    balance: 'Бонус балансы',
    balanceUnit: '₸',
    howItWorks: 'Бұл қалай жұмыс істейді',
    rule1: 'Сілтемеңізді достарыңызбен бөлісіңіз',
    rule2: 'Досыңыз сілтеме арқылы тіркеліп, 6+ айға жазылады',
    rule3: 'Сіз төлемнің 10%-ын бонус балансына аласыз',
    rule4: 'Бонустар келесі жазылу төлемінде автоматты түрде қолданылады',
    subUserNoticeTitle: 'Ұйым қызметкеріне арналған ескертпе',
    subUserNoticeText: 'Егер INMONEX жазылымын сіздің жұмыс берушіңіз төлейтін болса, сіз жинаған бонустарды ұйым иесіне бере аласыз. Ол оларды жазылымды төлеу кезінде пайдаланып, компания қаражатын үнемдейді. Жұмыс берушімен ішкі келісім бойынша сіз берілген бонустар үшін ақшалай сыйақы ала аласыз — сыйақының мөлшері, нысаны және төлеу мерзімдері тек сіз бен жұмыс беруші арасында анықталады. INMONEX мұндай келісімнің тарабы болып табылмайды және қызметкер мен жұмыс беруші арасындағы есеп айырысуға қатыспайды.',
    invitedTitle: 'Шақырылған достар',
    invitedEmpty: 'Сіздің сілтемеңіз бойынша әлі ешкім тіркелген жоқ',
    invitedTotal: (n) => `Барлығы шақырылды: ${n}`,
    invitedRewarded: (n) => `Алынған бонустар: ${n}`,
    colDate: 'Күні',
    colLogin: 'Логин',
    colEmail: 'Email',
    colStatus: 'Мәртебесі',
    statusRewarded: '✓ Бонус есептелді',
    statusPending: 'Төлемді күтуде',
    historyTitle: 'Бонус тарихы',
    historyEmpty: 'Тарих бос',
    typeReferralReward: 'Реферал бонусы',
    typeTransferIn: 'Аударым алынды',
    typeTransferOut: 'Аударым жіберілді',
    typeSpend: 'Төлемде қолданылды',
    typeAdminAdjust: 'Әкімшінің түзетуі',
    transferButton: 'Бонустарды аудару',
    transferTitle: 'Бонустарды аудару',
    transferHint: 'Алушыны логин немесе email арқылы табыңыз',
    transferSearch: 'Логин немесе email енгізе бастаңыз (мин. 3 таңба)',
    transferAmount: 'Аударым сомасы',
    transferNote: 'Пікір (міндетті емес)',
    transferSend: 'Жіберу',
    transferCancel: 'Болдырмау',
    transferSuccess: (amount, login) => `${login} пайдаланушысына ${amount} ₸ аударылды`,
    transferSelectUser: 'Алушыны таңдаңыз',
    transferNoResults: 'Пайдаланушылар табылмады',
    transferConfirm: (amount, login) => `${login} пайдаланушысына ${amount} ₸ аудару керек пе?`,
    errorInvalidAmount: 'Дұрыс соманы енгізіңіз',
    errorNoRecipient: 'Алушыны таңдаңыз',
    errorTransferFailed: 'Аудару қатесі',
  },

  // ─── Байланыс модалкасы (shared.js) ───
  contacts: {
    title: 'Ынтымақтастық',
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
  },

  // ─── Растау компоненті (shared.js) ───
  confirmDialog: {
    cancel: 'Болдырмау',
    confirm: 'Растау',
  },

  // ─── Empty компоненті (shared.js) ───
  empty: 'Деректер жоқ',

  // ─── NumInput компоненті (shared.js) ───
  numInputPlaceholder: 'Соманы енгізіңіз',

  // ─── GlobalStats компоненті (shared.js) ───
  globalStats: {
    totalOnAccounts: 'Шоттардағы жиыны:',
  },

  // ─── Audit (shared.js) ───
  audit: {
    headers: 'Күні\tТүрі\tСомасы\tШот\tҚор\tСанат\tТүсініктеме\tПайдаланушы',
  },

  // ─── Әдепкі санаттар (shared.js) ───
  defaultCategories: ['Аванс', 'Жалақы', 'Циркон', 'Материалдар', 'Жарнама', 'Логистика', 'Басқа'],

  // ─── OpsTab ───
  ops: {
    title: 'Операциялар',
    subtitle: 'Түсімдер, шығыстар және аударымдар',
    income: 'Түсім',
    expense: 'Шығыс',
    transfer: 'Аударым',
    betweenAccounts: 'Шоттар арасында',
    betweenFunds: 'Қорлар арасында',
    fromAccount: 'Шоттан',
    toAccount: 'Шотқа',
    account: 'Шот',
    fromFund: 'Қордан',
    toFund: 'Қорға',
    fundDebit: 'Есептен шығару қоры',
    expenseCategory: 'Шығыс санаты',
    amount: 'Сома',
    comment: 'Түсініктеме',
    incomeDescPlaceholder: 'Түсім сипаттамасы',
    expenseDescPlaceholder: 'Шығыс сипаттамасы',
    transferDescPlaceholder: 'Аударым сипаттамасы',
    submitIncome: 'Түсімді өткізу',
    submitExpense: 'Шығысты өткізу',
    submitTransfer: 'Аударымды өткізу',
    undoLast: 'Соңғысын болдырмау',
    undoConfirm: 'Соңғы операцияны болдырмау керек пе?',
    undoSuccess: 'Операция болдырылмады',
    enterAmount: 'Соманы енгізіңіз',
    differentAccounts: 'Әртүрлі шоттарды таңдаңыз',
    differentFunds: 'Әртүрлі қорларды таңдаңыз',
    fundInsufficient: (fundName, balance, deficit) =>
      `«${fundName}» қорында тек ${balance} бар. Жетіспейді: ${deficit}`,
    accountInsufficient: (balance, deficit) =>
      `Шотта тек ${balance} бар. Жетіспейді: ${deficit}`,
    fundOnlyInsufficient: (balance, deficit) =>
      `Қорда тек ${balance} бар. Жетіспейді: ${deficit}`,
    distributionTitle: 'Қорлар бойынша бөлу:',
    lastOps: 'Соңғы операциялар',
    noOpsYet: 'Әзірге операциялар жоқ',
    transferBetweenAccounts: 'Шоттар арасындағы аударым',
    transferBetweenFunds: 'Қорлар арасындағы аударым',
    // Жинаққа аудару (жасырын шот)
    transferToHidden: 'Жинаққа',
    toHiddenAccount: 'Жинаққа (жасырын шот)',
    transferToHiddenComment: 'Жинаққа аудару',
    transferToHiddenHint: 'Қор үшін бұл шығыс, сома кіріс есептеріне түспейді.',
    // Борыштармен байланыс
    closeDebtLabel: 'Борышты жабу (Бізге төледі)',
    simpleIncome: '-- Қарапайым түсім --',
    payDebtLabel: 'Біздің борышты төлеу / тұрақты төлем',
    simpleExpense: '-- Қарапайым шығыс --',
    debtPaymentComment: (desc) => `Міндеттеме бойынша төлем: ${desc}`,
    freeOperation: 'Еркін операция',
    debtPaymentIncoming: 'Міндеттеме бойынша төлем (Бізге)',
    debtPaymentOutgoing: 'Міндеттеме бойынша төлем (Біз)',
    selectDebtReceivable: 'Борышты таңдаңыз (Бізге тиесілі)',
    selectDebtPlaceholder: '-- Борышты таңдаңыз --',
    remainLabel: (amount) => `Қалдық: ${amount}`,
    contractorOptional: 'Контрагент (міндетті емес)',
    noContractor: 'Контрагентсіз',
    addContractorQuick: 'Контрагентті жылдам қосу',
    addCategoryQuick: 'Санатты жылдам қосу',
    // Санаттар
    expenseCategories: 'Шығыс санаттары',
    categoriesLinkedToFund: 'Санаттар таңдалған қорға байланысты.',
    categoriesForFund: 'Қор санаттары:',
    newCategory: 'Жаңа санат',
    noCategories: 'Санаттар жоқ',
    deleteCategoryConfirm: 'Шығыс санатын жою керек пе?',
  },

  // ─── DebtsTab ───
  debts: {
    title: 'Міндеттемелер',
    subtitle: 'Дебиторлық және кредиторлық берешекті басқару',
    receivableTitle: 'Бізге тиесілі (Дебиторлық)',
    receivableSubtitle: 'Шығарылған шот-фактуралар, клиенттердің борыштары',
    payableTitle: 'Біз тиеспіз (Кредиторлық)',
    payableSubtitle: 'Жеткізушілер шот-фактуралары, тұрақты төлемдер',
    totalExpected: 'Күтілетін жиыны:',
    totalOwed: 'Борыш жиыны:',
    addDebtor: 'Борышкер қосу',
    addOurDebt: 'Біздің борышты қосу',
    noActiveDebts: 'Белсенді борыштар жоқ',
    whoOwesUs: 'Кім бізге тиесілі?',
    weOweTo: 'Кімге біз тиеспіз?',
    // Қарау модалкасы
    viewReceivable: 'Толығырақ: Бізге тиесілі',
    viewPayable: 'Толығырақ: Біз тиеспіз',
    contractor: 'Контрагент:',
    debtAmount: 'Борыш сомасы:',
    date: 'Күні:',
    commentLabel: 'Түсініктеме:',
    paymentBindings: 'Төлем байланыстары:',
    accountCredit: 'Есептесу шоты: ',
    fundDebit: 'Есептен шығару қоры: ',
    category: 'Санат: ',
    paid: 'Төледі',
    wePaid: 'Төледік',
    paidAlert: 'Батырма жұмыс істейді! Келесі қадамда транзакция модалкасын байланыстырамыз.',
    // Жасау модалкасы
    contractorLabel: 'Контрагент*',
    newContractor: '+ Жаңа',
    selectPlaceholder: '-- Таңдаңыз --',
    accountCreditLabel: 'Есептесу шоты',
    incomeCategoryLabel: 'Кіріс санаты',
    fundDebitLabel: 'Есептен шығару қоры',
    expenseCategoryLabel: 'Шығыс санаты',
    noBinding: 'Байланыссыз',
    noCategoryOption: 'Санатсыз',
    amountLabel: 'Сома*',
    issueDateLabel: 'Шығарылған күні',
    expectedPaymentLabel: 'Күтілетін төлем',
    descriptionLabel: 'Сипаттама (Не үшін?)',
    descriptionPlaceholder: 'Мысалы: Тауар жеткізу үшін',
    regularPayment: 'Тұрақты төлем (Жалға алу және т.б.)',
    repeatLabel: 'Қайталау:',
    frequencyMonth: 'Айына бір рет',
    frequencyQuarter: 'Тоқсанына бір рет',
    frequencyYear: 'Жылына бір рет',
    saveDebt: 'Міндеттемені сақтау',
    fillContractorAndAmount: 'Контрагент пен соманы толтырыңыз',
    deleteConfirm: 'Бұл жазбаны жою керек пе?',
    daysPassed: (days) => `${days} күн өтті`,
    // DebtsTab ішіндегі контрагент
    newContractorTitle: 'Жаңа контрагент',
    saveContractor: 'Контрагентті сақтау',
    enterContractorName: 'Контрагент атауын енгізіңіз',
    selectContractorType: 'Контрагент түрін кем дегенде біреуін таңдаңыз',
    clientLabel: 'Клиент (Бізге төлейді)',
    supplierLabel: 'Жеткізуші (Біз төлейміз)',
    namePlaceholder: 'ЖК Иванов',
    legalNameLabel: 'Заңды атауы',
    legalNamePlaceholder: 'ЖШС Ромашка',
    binLabel: 'БИН / ИИН',
    binPlaceholder: '123456789012',
    phoneLabel: 'Телефон',
    phonePlaceholder: '+7 700 000 00 00',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Мекенжай',
    addressPlaceholder: 'Астана қ., Үлгілі к-сі 1',
    availableAccounts: 'Қолжетімді шоттар (Клиент үшін)',
    accountsHint: 'Ештеңе таңдалмаса — барлық шоттар үшін қолжетімді.',
    incomeCategories: 'Кіріс санаттары:',
    noIncomeCategories: 'Кіріс санаттары жоқ',
    availableFunds: 'Қолжетімді қорлар мен санаттар (Жеткізуші үшін)',
    fundsHint: 'Ештеңе таңдалмаса — барлық жерде қолжетімді.',
    fundsLabel: 'Қорлар:',
    expenseCategories: 'Шығыс санаттары:',
    // Валидациялар мен қателер
    enterLegalName: 'Заңды атауды енгізіңіз',
    enterBin: 'БИН/ИИН енгізіңіз',
    selectContractorRole: 'Контрагент рөлін таңдаңыз',
    duplicateContractor: (bin, name) => `Осындай БИН (${bin}) немесе заңды атаумен контрагент бар: ${name}`,
    selectAccountForDebt: 'Міндеттеме байланыстыру үшін шот таңдаңыз',
    selectFundForPayable: 'Кредиторлық берешек үшін есептен шығару қорын таңдаңыз',
    correctAmountAlert: 'Дұрыс соманы көрсетіңіз',
    selectDebtType: 'Міндеттеме түрін таңдаңыз (бізге тиесілі / біз тиеспіз)',
    selectAccount: 'Шотты таңдаңыз',
    specifyContractor: 'Контрагентті көрсетіңіз немесе жасаңыз',
    paymentExceedsRemaining: (payAmt, remAmt) => `Төлем сомасы (${payAmt}) қалдықтан (${remAmt}) асып кетеді`,
    noCategoryConfirm: 'Санат таңдалмаған. Транзакция санатсыз жасалады.\n\nСанатсыз жалғастыру керек пе?',
    archiveConfirm: 'Міндеттемені мұрағатқа жіберу керек пе?',
    // Контрагент модалкасының лейблдері
    nameForYouLabel: 'Атауы (сіз үшін)*',
    legalNameLabelReq: 'Заңды атауы*',
    binLabelReq: 'БИН / ИИН*',
    // Борыш жасау модалкасының лейблдері
    accountDebitLabel: 'Есептен шығару шоты*',
    accountCreditLabelReq: 'Есептесу шоты*',
    fundDebitLabelReq: 'Есептен шығару қоры*',
    noActiveAccounts: 'Белсенді шоттар жоқ',
    noActiveFunds: 'Белсенді қорлар жоқ',
    monthLabel: 'Ай:',
    quarterMonthLabel: 'Тоқсан айы:',
    quarterFirst: 'Бірінші',
    quarterSecond: 'Екінші',
    quarterThird: 'Үшінші',
    dayOfMonthLabel: 'Айдың күні:',
    // Өңдеу модалкасы
    editDebtTitle: 'Міндеттемені өңдеу',
    editContractorLabel: 'Контрагент',
    editContractorPlaceholder: 'Контрагентті таңдаңыз',
    expectedPaymentDate: 'Күтілетін төлем күні',
    editAccountCreditLabel: 'Есептесу шоты',
    editFundDebitLabel: 'Есептен шығару қоры',
    // Қарау модалкасы (кеңейтілген)
    viewWeOweUs: 'Бізге тиесілі',
    viewWeOwe: 'Біз тиеспіз',
    editBtnTitle: 'Өзгерту',
    archiveBtnTitle: 'Мұрағатқа',
    deleteBtnTitle: 'Жою',
    closeBtnTitle: 'Жабу',
    dateLabel: 'Күні',
    totalAmount: 'Жалпы сома',
    paidLabelUpper: 'Төленді',
    remainingLabel: 'Қалдық',
    paidPercent: (pct) => `${pct}% төленді`,
    paymentHistory: (count) => `Төлем тарихы (${count})`,
    attachmentLabel: 'Тіркеме',
    downloadBtn: 'Жүктеп алу',
    fixPayment: 'Төлемді тіркеу',
    toWhichAccount: 'Қай шотқа?',
    fromWhichAccount: 'Қай шоттан?',
    categoryIncomeLabel: 'Кіріс санаты',
    categoryExpenseLabel: 'Шығыс санаты',
    recommendSelect: 'Таңдауды ұсынамыз',
    fromWhichFund: 'Қай қордан?',
    fullPayment: (amount) => `Толық төлем (${amount})`,
    partialPayment: 'Ішінара төлем',
    enterPaymentAmount: 'Төлем сомасын енгізіңіз:',
    payBtn: 'Төлеу',
    maxPaymentHint: (amount) => `Максимум: ${amount} · Енгізілген сомаға транзакция жасалады`,
    fullyPaid: 'Толық төленді',
    allPaymentsFixed: 'Осы міндеттеме бойынша барлық төлемдер тіркелді',
    ofAmount: 'ішінен',
    debtPaymentComment: (desc) => `Борыш төлемі: ${desc}`,
    debtPaymentPartial: (paid, total) => ` (ішінара ${paid} ішінен ${total})`,
    systemUser: 'Жүйе',
    // PDF-тен шот-фактура импорты
    uploadInvoice: 'Шот-фактура жүктеу',
    importFromPdf: 'PDF-құжаттан импорт',
    dragDropPdf: 'PDF файлды апарыңыз немесе таңдау үшін басыңыз',
    invoiceOrPaymentOrder: 'Төлемге шот немесе Төлем тапсырмасы',
    recognizingDoc: 'Құжатты тану жүріп жатыр...',
    onlyPdfSupported: 'Тек PDF файлдар қолдау көрсетіледі',
    pdfReadError: 'PDF оқу мүмкін болмады. Файл құпия сөзбен қорғалмағанына көз жеткізіңіз.',
    tryAgain: 'Қайталап көріңіз',
    invoiceDocType: 'Төлемге шот',
    paymentOrderDocType: 'Төлем тапсырмасы',
    documentDocType: 'Құжат',
    debtTypeLabel: 'Міндеттеме түрі*',
    receivableShort: 'Бізге тиесілі',
    payableShort: 'Біз тиеспіз',
    autoDetectFailed: 'Автоматты түрде анықтау мүмкін болмады. Шот баптауларындағы IBAN-ды тексеріңіз.',
    typeUncertain: 'Түрі болжаммен анықталды — «Бізге қарыз / Біз қарыз» тексеріңіз.',
    foundLabel: 'Табылды',
    foundByBin: 'БИН бойынша табылды',
    contractorNotFound: 'Контрагент табылмады. Жаңасы жасалады:',
    nameFieldLabel: 'Атауы',
    binFieldLabel: 'БИН/ИИН',
    orSelectExisting: 'Немесе бар контрагентті таңдаңыз:',
    searchPlaceholder: '-- Іздеу --',
    descriptionImport: 'Сипаттама',
    forWhatPlaceholder: 'Не үшін?',
    createDebt: 'Міндеттеме жасау',
    invoiceDesc: (num) => `Төлемге шот №${num}`,
    paymentOrderDesc: (num) => `Төлем тапсырмасы №${num}`,
    categoryDynamic: (isPayable) => isPayable ? 'Шығыс санаты' : 'Кіріс санаты',
    categoryHelpTooltip: 'Санат сома не үшін алынғанын немесе жұмсалғанын қадағалауға көмектеседі.\n\n💡 Дәл есеп жүргізу үшін санатты әрқашан көрсетуді ұсынамыз.',
    auditDeleted: (desc) => `Міндеттеме жойылды: ${desc}`,
    auditTypeReceivable: 'Дебиторлық',
    auditTypePayable: 'Кредиторлық',
    auditTypeAmount: (type, amount) => `Түрі: ${type}. Сомасы: ${amount}`,
    auditNewDebt: (direction, amount) => `Жаңа міндеттеме: ${direction} ${amount}`,
    auditImportDebt: (direction, amount) => `Құжаттан импорт: ${direction} ${amount}`,
    auditWeOwe: 'Біз борыштымыз',
    auditTheyOwe: 'Бізге борышты',
  },

  // ─── TxTab ───
  tx: {
    title: 'Транзакциялар',
    totalLabel: (total, shown) =>
      `Барлығы: ${total}${shown !== total ? ` (көрсетілген: ${shown})` : ''}`,
    viewGrid: 'Көрініс: тор',
    viewTable: 'Көрініс: кесте',
    allTypes: 'Барлық түрлер',
    allAccountsFunds: 'Барлық шоттар мен қорлар',
    fund: 'Қор:',
    archivedSuffix: ' (мұрағат)',
    dateFrom: 'Күні бастап',
    dateTo: 'Күні дейін',
    toLatest: 'Соңғыларға',
    toLatestOps: 'Соңғы операцияларға',
    toLatestOpsFull: 'Соңғы операцияларға өту',
    futureMonthTitle: 'Бұл ай әлі келмеді',
    futureMonthHint: 'Болашақ айлар үшін тор көрсетілмейді. Ағымдағы немесе өткен айға ауысыңыз.',
    noTxInMonth: 'Бұл айда транзакциялар жоқ',
    noTxInMonthHint: 'Тек таңдалған айдың келген күндері көрсетіледі.',
    noTransactions: 'Транзакциялар жоқ',
    opsCount: (n) => `${n} оп.`,
    opsCountShort: (n) => `${n} оп.`,
    moreItems: (n) => `+ тағы ${n}`,
    // Кесте
    thDate: 'Күні',
    thType: 'Түрі',
    thRoute: 'Бағыт',
    thCategory: 'Санат',
    thFund: 'Қор',
    thAmount: 'Сома',
    thComment: 'Түсініктеме',
    thContractor: 'Контрагент',
    thWho: 'Кім',
    thActions: '⚙',
    edited: '(өңд.)',
    deleteTxConfirm: (amount) => `${amount} сомасындағы транзакцияны жою керек пе?`,
    editLogComment: (oldAmt, newAmt, comment) =>
      `Болды: ${oldAmt}, болды: ${newAmt}. ${comment}`,
    // Күн
    dayOps: 'Операциялар:',
    dayIncome: 'Түсімдер',
    dayExpense: 'Шығыстар',
    dayTransfers: 'Аударымдар',
  },

  // ─── ReportsTab ───
  reports: {
    title: 'Есептер',
    charts: 'Графиктер',
    opu: 'ОПжЗ',
    dds: 'АҚҚ',
    allReport: 'Жалпы есеп',
    onlyOpu: 'Тек ОПжЗ',
    onlyDds: 'Тек АҚҚ',
    allAccounts: 'Барлық шоттар',
    allFunds: 'Барлық қорлар',
    // Стат-карточкалар
    incomeLabel: 'Түсімдер',
    expenseLabel: 'Шығыстар',
    onAccounts: 'Шоттарда',
    inFunds: 'Қорларда',
    // Графиктер
    chartIncomeExpense: 'Кіріс / шығыс',
    chartExpenseByFunds: 'Қорлар бойынша шығыс құрылымы',
    chartIncomeByAccounts: 'Шоттар бойынша түсімдер',
    chartCumulativeResult: 'Кезең бойынша жинақталған нәтиже',
    // ОПжЗ кестесі
    opuTitle: 'ОПжЗ',
    opuDescription: 'ОПжЗ бизнес нәтижесін көрсетеді: қанша табылды және қанша жұмсалды. Шоттар мен қорлар арасындағы ішкі аударымдар мұнда кірмейді.',
    opuPeriodResult: 'Кезең қорытындысы',
    opuIncome: 'КІРІСТЕР',
    opuExpense: 'ШЫҒЫСТАР',
    opuProfit: 'ПАЙДА / ЗИЯН',
    opuArticle: 'Бап',
    opuTotal: 'Жиыны',
    noFund: 'Қорсыз',
    // АҚҚ кестесі
    ddsTitle: 'АҚҚ',
    ddsDescription: 'АҚҚ шоттар бойынша ақша қозғалысын көрсетеді: басындағы қалдық, ағындар, шығындар, шоттар арасындағы ішкі аударымдар және соңындағы қалдық. Сондықтан бұл ОПжЗ-мен бірдей емес.',
    ddsOpening: 'Басындағы қалдық',
    ddsInflow: 'Ақша ағыны',
    ddsOutflow: 'Ақша шығыны',
    ddsInternal: 'Шоттар арасындағы ішкі аударымдар',
    ddsNetFlow: 'ТАЗА АҚША АҒЫНЫ',
    ddsClosing: 'Соңындағы қалдық',
    ddsNetFlowLabel: 'Таза ағын',
    ddsFilterHint: 'Нақты шот бойынша сүзгілеу кезінде ішкі аударым таңдалған шот ішіндегі ақша қозғалысы ретінде ескеріледі. «Барлық шоттар» сүзгісінде мұндай аударым таза ағынға әсер етпейді.',
    // Экспорт
    exportSummary: 'ЖИЫНТЫҚ',
    exportIncomes: 'Түсімдер',
    exportExpenses: 'Шығыстар',
    exportTransfers: 'Аударымдар',
    exportReportName: { all: 'Жалпы', opu: 'ОПжЗ', dds: 'АҚҚ' },
    // Экспорт - қосымша түрлер
    exportCategoriesName: 'Санаттар',
    exportContractorsName: 'Контрагенттер',
    exportTimelinessName: 'Тәртіп',
    exportLiquidityName: 'Өтімділік',
    // Есеп қойындылары
    categoriesTab: 'Санаттар',
    contractorsTab: 'Контрагенттер',
    timelinessTab: 'Мерзімдер',
    liquidityTab: 'Өтімділік',
    onlyCategoriesExport: 'Тек Санаттар',
    onlyContractorsExport: 'Тек Контрагенттер',
    onlyTimelinessExport: 'Тек Мерзімдер',
    onlyLiquidityExport: 'Тек Өтімділік',
    // Қосымша графиктер
    obligationsCalendar: 'Міндеттемелер күнтізбесі (Төлем болжамы)',
    noUpcomingPayments: 'Борыш бойынша алдағы төлемдер жоқ',
    weWillReceive: 'Бізге түседі (+)',
    weWillPay: 'Біз төлейміз (-)',
    // Санаттар көрінісі
    incomeByCategories: 'Санаттар бойынша кіріс талдауы',
    expenseByCategories: 'Санаттар бойынша шығыс талдауы',
    categoryColumn: 'Санат',
    amountColumn: 'Сома',
    // Контрагенттер көрінісі
    contractorActivity: 'Контрагенттер белсенділігі',
    contractorColumn: 'Контрагент',
    revenueColumn: 'Түсім (+)',
    costsColumn: 'Шығындар (-)',
    balanceColumn: 'Сальдо',
    debtBalanceColumn: 'Борыш / Баланс',
    contractorFilterAll: 'Барлығы',
    contractorFilterClients: 'Клиенттер',
    contractorFilterSuppliers: 'Жеткізушілер',
    // Тәртіп көрінісі
    paymentDiscipline: 'Төлем тәртібі (Дебиторлар)',
    paymentDisciplineHint: 'Іске асырылған мәмілелер бойынша төлем кестесінен кешігу немесе алда болудың орташа көрсеткіші. Өмірлік статистика.',
    clientsOnlyNote: 'Есепте тек клиенттер көрсетілген: көрсеткіш сізге төлейтіндер бойынша есептеледі. Жеткізушілер үшін (сіз төлейтіндер) бұл мерзімдер қолданылмайды.',
    clientColumn: 'Клиент',
    dealsColumn: 'Мәмілелер',
    punctualityColumn: 'Уақтылылық (%)',
    avgDelayColumn: 'Орташа кешігу',
    onTime: 'Уақытында',
    noPaymentHistory: 'Талдау үшін төлем тарихы жоқ',
    daysShort: 'күн',
    // Өтімділік көрінісі
    liquidityPosition: 'Өтімділік жағдайы',
    cashOnAccounts: 'Шоттардағы қолма-қол ақша:',
    receivablesLabel: 'Бізге тиесілі (Дебиторлық):',
    payablesLabel: 'Біз тиеспіз (Кредиторлық):',
    totalLiquidity: 'Жалпы өтімділік:',
    liquidityHint: 'Бұл есеп нақты ақша мен алдағы төлемдерді қосып, сіздің қаржылық «беріктік қорыңызды» көрсетеді.',
    capitalStructure: 'Капитал құрылымы',
    cashName: 'Қолма-қол ақша',
    receivablesName: 'Дебиторлық',
    resultChartName: 'Нәтиже',
    resetFilters: 'Тастау',
    dateFrom: 'Күні бастап',
    dateTo: 'Күні дейін',
    noDataAvailable: 'Кезеңдегі кіріс туралы деректер жоқ',
  },

  // ─── AdminTab ───
  admin: {
    title: 'Баптаулар мен профиль',
    subtitle: 'Компанияны, қызметкерлерді және жүйені басқару',
    profileTab: 'Компания профилі',
    usersTab: 'Қызметкерлер',
    systemTab: 'Жүйе',
    securityTab: 'Қауіпсіздік',
    // Қауіпсіздік (Stage 11): UI strings for SessionsCard and SecurityEventsCard
    security: {
      sectionSubtitle: 'Сеанстарды басқару және аккаунт әрекеттерінің тарихы',
      devicesTitle: 'Менің құрылғыларым',
      refresh: 'Жаңарту',
      loading: 'Жүктелуде...',
      noSessions: 'Белсенді сеанстар жоқ.',
      currentBadge: 'Ағымдағы',
      thisDevice: 'Бұл құрылғы',
      terminate: 'Аяқтау',
      logoutAll: 'Барлық құрылғылардан шығу',
      logoutAllInProgress: 'Шығу...',
      logoutAllHint: 'Содан кейін әр құрылғыда қайта кіру керек болады.',
      confirmRevoke: 'Бұл сеансты аяқтайсыз ба? Егер бұл сіздің ағымдағы құрылғыңыз болса — қайта кіру керек болады.',
      confirmLogoutAll: 'БАРЛЫҚ сеанстарды аяқтайсыз ба? Сіз бұл құрылғыдан бірден шығасыз.',
      errorLoad: 'Жүктеу мүмкін болмады',
      errorPrefix: 'Қате: ',
      unknownDevice: 'Белгісіз құрылғы',
      ipLabel: 'IP',
      loggedInAt: 'кіру',
      // Stage 11: екі факторлы аутентификация UI блогы (TwoFactorCard + setup wizard)
      twoFactor: {
        title: 'Екі факторлы аутентификация',
        subtitle: 'Кіру кезінде қосымша код — біреу құпия сөзді білсе де, қосымшасыз кіре алмайды.',
        statusEnabled: 'Қосылған',
        statusDisabled: 'Орнатылмаған',
        enabledSince: (date) => `${date} бастап қосылған`,
        backupRemaining: (n) => `Резервтік кодтар қалды: ${n}`,
        backupLowWarning: 'Резервтік кодтар аз. Жаңасын жасауды ұсынамыз.',
        enableBtn: '2FA қосу',
        disableBtn: '2FA өшіру',
        loading: 'Жүктелуде...',
        // Setup wizard (3 қадам)
        setupTitle: '2FA-ны қосу',
        step1Title: '3-тен 1-қадам — QR кодын сканерлеу',
        step1Desc: 'Екі факторлы аутентификация қосымшасын орнатыңыз (Google Authenticator, Microsoft Authenticator, Authy немесе 1Password) және QR кодын сканерлеңіз.',
        manualSecretLabel: 'Сканерлеу мүмкін емес пе?',
        manualSecretHint: 'Бұл құпияны қолмен қосымшаға енгізіңіз:',
        copyBtn: 'Көшіру',
        copied: 'Көшірілді',
        nextBtn: 'Әрі қарай',
        cancelBtn: 'Бас тарту',
        backBtn: '← Артқа',
        step2Title: '3-тен 2-қадам — Растау',
        step2Desc: 'Қазір қосымша көрсетіп тұрған 6 таңбалы кодты енгізіңіз.',
        codeInputPh: '6 таңба',
        verifyBtn: 'Тексеру',
        verifying: 'Тексеру...',
        codeInvalid: 'Қате код. Құрылғыдағы уақытты тексеріңіз.',
        step3Title: '3-тен 3-қадам — Резервтік кодтар',
        step3Desc: 'Осы 10 бір реттік кодты сенімді жерге сақтаңыз. Қосымшаға қол жеткізуді жоғалтсаңыз, олар кіруге көмектеседі. Әр кодты тек БІР рет пайдалануға болады.',
        step3Notice: '⚠️ Бұл кодтар тек қазір көрсетіледі. Оларды қайта алу мүмкін емес — жүйеде тек хештер сақталады.',
        copyAllBtn: 'Барлығын көшіру',
        downloadBtn: '.txt жүктеу',
        confirmSavedLabel: 'Резервтік кодтарды сенімді жерге сақтадым',
        finishBtn: 'Дайын',
        setupSuccess: '2FA сәтті қосылды',
        // Disable flow
        disableTitle: '2FA-ны өшіру',
        disableDesc: 'Екі факторлы аутентификацияны өшіру үшін ағымдағы құпия сөзді және 2FA кодын (немесе резервтік кодтардың бірін) енгізіңіз.',
        passwordPh: 'Ағымдағы құпия сөз',
        codeOrBackupPh: '6 таңба немесе XXXX-XXXX',
        disableConfirmBtn: 'Өшіру',
        disablingBtn: 'Өшірілуде...',
        disableSuccess: '2FA өшірілді',
        confirmDisable: '2FA-ны өшіргіңіз келетініне сенімдісіз бе? Аккаунт қауіпсіздігі төмендейді.',
        // Errors
        errorPrefix: 'Қате: ',
        errorLoad: '2FA күйін жүктеу мүмкін болмады',
        errorInit: 'Орнатуды бастау мүмкін болмады',
        currentPasswordInvalid: 'Ағымдағы құпия сөз қате',
      },
      events: {
        title: 'Қауіпсіздік әрекеттері',
        empty: 'Соңғы уақытта оқиғалар жоқ.',
        showingLast: 'Соңғы 50 оқиға көрсетілген',
        criticalWarning: 'Егер бұл сіз болмасаңыз — құпиясөзді дереу өзгертіп, жоғарыдағы құрылғыларды тексеріңіз.',
        warningBanner: 'Күмәнді оқиғалар анықталды — төмендегі тізімді тексеріңіз',
        actions: {
          change_password: 'Құпиясөзді ауыстыру',
          delete_account: 'Аккаунтты жою',
          delete_org: 'Ұйымды жою',
        },
        types: {
          password_changed: 'Құпиясөз өзгертілді',
          password_reset: 'Email арқылы құпиясөз қалпына келтірілді',
          email_changed: 'Email өзгертілді',
          '2fa_enabled': 'Екі факторлы аутентификация қосылды',
          '2fa_disabled': 'Екі факторлы аутентификация өшірілді',
          org_deleted: 'Ұйым жойылды',
          account_deleted: 'Аккаунт жойылды',
          reuse_detected: 'Ұрланған сеанс токені табылды',
          logout_all: 'Барлық сеанстар аяқталды',
          session_revoked: 'Сеанс аяқталды',
          admin_revoke: 'Сеанс әкімшімен қайтарылды',
          sensitive_action_locked: 'Күмәнді құпиясөз енгізу әрекеттері',
        },
      },
    },
    // Профиль
    companyInfo: 'Компания туралы ақпарат',
    ownerOnly: 'Тек Иесі бұл деректерді өзгерте алады.',
    baseCurrency: 'Есептің негізгі валютасы',
    currencyHint: 'Бұл валюта барлық есептерде, операцияларда және экспорт файлдарында көрсетіледі.',
    companyName: 'Компания атауы (Есептерде көрсетіледі)',
    companyPlaceholder: 'Рога и Копыта ЖШС',
    emailLabel: 'Хабарламалар Email',
    emailConfirmed: 'Расталған',
    confirmEmail: 'Поштаны растау',
    emailPlaceholder: 'director@company.com',
    codePlaceholder: 'Хаттағы код',
    wrongCode: 'Код қате. Қайта көріңіз.',
    codeSent: 'Код поштаңызға жіберілді',
    codeSendError: 'Кодты жіберу мүмкін болмады',
    codeSending: 'Жіберілуде...',
    verificationError: 'Кодты тексеру қатесі',
    phoneLabel: 'Байланыс телефоны',
    phonePlaceholder: '77770001122',
    detailsLabel: 'Деректемелер және қосымша ақпарат',
    detailsPlaceholder: 'БИН/ИИН, заңды мекенжай...',
    // Менің аккаунтым
    myAccount: 'Менің аккаунтым',
    // Этап 2: бөлім тақырыптары MyAccountTab
    myAccountSubtitle: 'Жеке деректер мен қауіпсіздікті басқару',
    personalDataSection: 'Жеке деректер',
    securitySection: 'Қауіпсіздік',
    currentPasswordPh: 'Ағымдағы құпия сөз',
    currentPasswordRequired: 'Ағымдағы құпия сөзді енгізіңіз',
    myPhoneLabel: 'Менің телефоным',
    phoneNotSet: 'Көрсетілмеген',
    phoneHint: 'Тек сандар, кемінде 7. Бос орындарды жүйе өзі қояды.',
    phoneMinDigits: 'Кемінде 7 сан',
    phoneSaved: 'Телефон сақталды',
    phoneSaveErr: 'Сақтау мүмкін болмады',
    filterUsersPlaceholder: 'Іздеу: аты, логин, email немесе телефон сандары',
    roleLabel: 'Рөлі:',
    changePassword: 'Құпия сөзімді ауыстыру',
    newPasswordPlaceholder: 'Жаңа құпия сөз',
    passwordHint: 'Ағымдағы құпия сөзді өзгерткіңіз келмесе, бос қалдырыңыз.',
    saveAll: 'Барлық өзгерістерді сақтау',
    saveSuccess: 'Профиль баптаулары сәтті сақталды!',
    // Қызметкерлер
    employees: (count) => `Қызметкерлер (${count})`,
    viewOnlyHint: 'Сіз қарау режиміндесіз. Қолжетімділікті тек Иесі басқара алады.',
    addEmployee: 'Қызметкер қосу',
    dupLogin: 'Мұндай логинмен пайдаланушы бұл ұйымда бұрыннан бар',
    addExisting: 'Бар пайдаланушыны қосу',
    addNew: 'Жаңасын жасау',
    searchUserPlaceholder: 'Пайдаланушы логині немесе email',
    searchBtn: 'Іздеу',
    searching: 'Іздеу...',
    userFound: 'Пайдаланушы табылды',
    userNotFound: 'Пайдаланушы табылмады. Логинді немесе email-ді тексеріңіз.',
    userAlreadyInOrg: 'Бұл пайдаланушы ұйымға қосылып қойған',
    addToOrg: 'Ұйымға қосу',
    addToOrgHint: 'Пайдаланушы таңдалған рөлмен осы ұйымға қол жеткізе алады',
    userOrgsCount: n => `${n} ұйымда`,
    suggestedRole: 'Ұсынылатын рөл (басқа ұйымнан)',
    accessRevoked: 'Ұйымға рұқсат қайтарып алынды',
    accessRevokedHint: 'Бұл ұйымға рұқсатыңыз қайтарып алынды. Басқа ұйымды таңдаңыз.',
    employeeName: 'Қызметкер аты',
    employeeNamePlaceholder: 'Иван Иванов',
    roleSelectLabel: 'Рөлі',
    loginLabel: 'Кіру логині',
    loginPlaceholder: 'ivan_buh',
    initialPassword: 'Бастапқы құпия сөз',
    initialPasswordPlaceholder: 'pass123',
    deleteEmployeeConfirm: 'Бұл қызметкерді ұйымнан жою керек пе?',
    permOverrides: 'Жеке рұқсаттар (рөлді қайта анықтайды)',
    permHint: '* Бөлектелген рұқсаттар қолмен орнатылған. Қалғандары рөлден мұраланады.',
    orgsLabel: 'Ұйымдар:',
    loginDisplayLabel: 'Логин:',
    passwordDisplayLabel: 'Құпия сөз:',
    showPassword: 'Көрсету',
    resetPassword: 'Құпия сөзді ауыстыру',
    resetPasswordPlaceholder: 'Жаңа құпия сөз',
    passwordResetSuccess: 'Құпия сөз өзгертілді',
    passwordNotSet: 'Орнату',
    // Stage 11: логинді ауыстыру (тек owner)
    changeLogin: 'Логинді өзгерту',
    changeLoginTitle: 'Қызметкер логинін ауыстыру',
    changeLoginDesc: 'Жаңа логин латын әрпімен басталуы керек, 3-32 таңбадан тұрады: a-z, 0-9, нүкте, асты сызық, дефис.',
    changeLoginPlaceholder: 'Жаңа логин',
    changeLoginSubmit: 'Өзгерту',
    changeLoginSuccess: 'Логин өзгертілді',
    loginInvalidFormat: 'Қате формат: тек латын, цифрлар, ".", "_", "-", 3-32 таңба, әріптен басталады.',
    loginTaken: 'Бұл логин басқа пайдаланушыға тіркелген',
    loginUnchanged: 'Логин өзгермеді',
    // Stage 11: құпия сөз ауыстыру кезінде ескерту
    resetPasswordTitle: 'Қызметкер қол жетімділігін қалпына келтіру',
    resetPasswordWarning: 'Қалпына келтіріледі: құпия сөз, екі факторлы аутентификация (қосылған болса), барлық белсенді сеанстар. Қызметкер барлық құрылғылардан дереу шығарылады.\n\nЕгер бұл аккаунтты жаңа адамға беру болса — қызметкер email-ін де өзгертуді ҰМЫТПАҢЫЗ! Әйтпесе ескі қызметкер "Құпия сөзді ұмыттым" арқылы жаңа reset-сілтеме ала алады.\n\nЖалғастыру?',
    resetPasswordSimpleHint: 'Жаңа құпия сөз — кемінде 8 таңба.',
    // Этап 2 v2: қызметкер email-ін иесі арқылы өзгерту
    changeEmail: 'Email өзгерту',
    changeEmailTitle: 'Қызметкер email-і',
    changeEmailDesc: 'Қызметкер email-і өзіндік құпия сөзді қалпына келтіру үшін қолданылады. Жұмыстан шығарған кезде email-ді міндетті түрде тазалаңыз немесе ауыстырыңыз — әйтпесе ескі қызметкер "Құпия сөзді ұмыттым" арқылы қол жеткізуі мүмкін.',
    changeEmailPlaceholder: 'employee@example.com',
    changeEmailSubmit: 'Сақтау',
    changeEmailRemoveBtn: 'Email-ді жою',
    changeEmailSuccess: 'Email жаңартылды',
    changeEmailRemoveSuccess: 'Email жойылды',
    emailInvalid: 'Email форматы дұрыс емес',
    emailTaken: 'Бұл email басқа пайдаланушыда',
    emailNotSet: 'көрсетілмеген',
    emailVerifiedYes: 'Расталды',
    emailVerifiedNo: 'Расталмаған',
    orgAssignment: 'Ұйымдар',
    orgAssignmentHint: 'Пайдаланушы қол жеткізе алатын ұйымдарды белгілеңіз',
    orgRoleIn: role => `Рөлі: ${role}`,
    // Жүйе
    backupTitle: 'Дерекқордың сақтық көшірмесі',
    backupHint: 'Сақтық көшірмені үнемі компьютеріңізге жүктеп алыңыз. Браузердегі деректерді кездейсоқ жойсаңыз немесе басқа құрылғыдан кірсеңіз, осы файлды жүктеп, бүкіл бухгалтерияны қалпына келтіре аласыз.',
    saveBackup: 'Сақтық көшірмені сақтау (.json)',
    restoreBackup: 'Сақтық көшірмені қалпына келтіру',
    restoreOwnerOnly: 'Тек Иесі дерекқорды қалпына келтіре алады!',
    restoreConfirm: 'НАЗАР АУДАРЫҢЫЗ! Ағымдағы деректер жойылып, файлдағы деректермен ауыстырылады. Жалғастыру керек пе?',
    restoreSuccess: 'Дерекқор сәтті қалпына келтірілді!',
    restoreFormatError: 'Сақтық көшірме файлының пішімі дұрыс емес.',
    restoreReadError: 'Файлды оқу кезінде қате пайда болды.',
    auditTitle: 'Жүйе аудит журналы',
    auditHint: 'Аудит журналында барлық пайдаланушы әрекеттері тіркеледі (кім, қашан және қандай операция жасады, қосты немесе жойды). Тексеру үшін оны жүктеп ала аласыз.',
    downloadAudit: 'Аудит журналын жүктеп алу (.tsv)',
    statsTitle: 'Жүктелу статистикасы',
    statsUsers: 'Пайдаланушылар',
    statsAccounts: 'Белсенді шоттар',
    statsFunds: 'Белсенді қорлар',
    statsTx: 'Транзакциялар',
    // Жаңа кілт
    langLabel: 'Интерфейс тілі',
  },

  // ─── DataTab ───
  data: {
    accountsTab: 'Шоттар мен қорлар',
    contractorsTab: 'Контрагенттер',
    categoriesTab: 'Санаттар',
    archiveTab: 'Мұрағат',

    // --- CategoriesTab ---
    linkedToAccount: (name) => `Шотқа байланысты: ${name}`,
    linkedToFund: (name) => `Қорға байланысты: ${name}`,
    deletedLink: 'Жойылды',
    notLinkedToAccount: 'Шотқа байланысты емес',
    notLinkedToFund: 'Қорға байланысты емес',
    baseSystemCategory: 'Базалық жүйелік санат',
    noTurnover: 'Айналым жоқ',
    archiveCategoryConfirm: (name) => `"${name}" санатын мұрағатқа жіберу керек пе?`,
    archiveCategoryExtra: 'Санат жаңа операциялар жасау кезінде тізімдерден жоғалады, бірақ онымен байланысты барлық өткен операциялар қаржылық есептерде өзгеріссіз қалады. Оны Бірыңғай мұрағаттан қалпына келтіруге болады.',
    editCategory: 'Санатты өзгерту',
    newCategory: 'Жаңа санат',
    incomeType: 'Кіріс',
    expenseType: 'Шығыс',
    categoryNamePlaceholder: 'Санат атауы',
    linkToAccountLabel: 'Шотқа байланыстыру (міндетті емес)',
    noAccountLink: 'Шотқа байланыссыз',
    linkToFundLabel: 'Қорға байланыстыру (міндетті емес)',
    noFundLink: 'Қорға байланыссыз',
    linkedContractors: 'Байланысты контрагенттер',
    noneSelected: 'Ешкім таңдалмаған',
    searchByNameOrBin: 'Атау немесе БИН бойынша іздеу...',
    contractorsNotFound: 'Контрагенттер табылмады',
    binIinLabel: 'БИН/ИИН: ',
    categoriesTitle: 'Санаттар анықтамалығы',
    categoriesSubtitle: (count) => `Кіріс пен шығыс аналитикасы (${count})`,
    addCategory: 'Санат қосу',
    searchCategory: 'Санат іздеу...',
    sortRecent: 'Алдымен соңғылар',
    sortVolDesc: 'Айналым бойынша (кемуі)',
    sortVolAsc: 'Ай��алым бойынша (өсуі)',
    sortNameAsc: 'Әліпби бойынша (А-Я)',
    sortNameDesc: 'Әліпби бойынша (Я-А)',
    categoriesNotFound: 'Санаттар табылмады',
    categoriesForAccounts: 'Шоттар үшін санаттар',
    categoriesForFunds: 'Қорлар үшін санаттар',

    // --- CategoryModal ---
    enterName: 'Атауды енгізіңіз',
    archiveCategoryMinOne: 'Мұрағатқа жіберу мүмкін емес: кем дегенде бір белсенді санат қалуы тиіс.',
    archiveCategoryQuestion: 'Санатты мұрағатқа жіберу керек пе?',
    noLinkGeneral: 'Байланыссыз (Жалпы)',
    linkToAccountEdit: 'Шотқа байланыстыру (міндетті емес)',
    linkToFundEdit: 'Қорға байланыстыру (міндетті емес)',
    noLinkGeneralCategory: 'Байланыссыз (Жалпы санат)',
    categoryNameLabel: 'Санат атауы',
    notFoundShort: 'Табылмады',
    saveChanges: 'Өзгерістерді сақтау',
    linkSection: 'Байланыстыру',
    accountPrefix: 'Шот: ',
    fundPrefix: 'Қор: ',
    categoryOperations: 'Санат бойынша операциялар',
    periodAll: 'Барлық уақыт үшін',
    periodMonth: 'Соңғы ай үшін',
    period6months: 'Жарты жыл үшін',
    periodYear: 'Жыл үші��',
    periodCustom: 'Еркін',
    totalTurnover: 'Таңдалған кезеңдегі жалпы айналым:',
    operationsList: 'Операциялар тізімі (Басуға болады)',
    noOpsInPeriod: 'Бұл кезеңде операциялар жоқ',

    // --- TxEditModal ---
    moveToArchiveTitle: 'Мұрағатқа жіберу керек пе?',
    moveToArchiveDesc: 'Шоттар мен қорлардың балансы қайта есептеледі: операция сомасы қайтарылады. Операцияны мұрағаттан қалпына келтіруге ��олады.',
    yesToArchive: 'Иә, мұрағатқа',
    viewOperation: 'Операцияны қарау',
    txEditHelp: 'Санатты, контрагентті және түсініктемені өзгертуге болады. Есептің дәлдігі үшін сома өңделмейді. Операцияны болдырмау үшін — мұрағатқа жіберіңіз.',
    txTypeIncome: 'Түсім',
    txTypeExpense: 'Шығыс',
    txTypeTransfer: 'Аударым',
    contractor: 'Контрагент',
    noContractor: 'Контрагентсіз',
    categorySelectLabel: 'Санат',
    noCategory: 'Санатсыз',
    commentLabel: 'Түсініктеме',
    descPlaceholder: 'Сипаттама...',

    // --- AccountsFundsTab ---
    enterAccountName: 'Шот атауын енгізіңіз',
    enterFundName: 'Қор атауын енгізіңіз',
    newAccount: 'Жаңа шот',
    accountNameLabel: 'Шот атауы',
    accountNamePlaceholder: 'Каспи Gold, Қолма-қол...',
    makePrimary: 'Негізгі ету',
    hiddenAccountLabel: 'Жасырын шот (Жеке)',
    whoCanSee: 'Бұл шотты кім көре алады?',
    visibleOnlyMe: 'Тек мен (иесі)',
    visibleMeAccountant: 'Мен және бухгалтер',
    visibleAdmins: 'Мен, бухгалтер және әкімші',
    visibleAll: 'Бүкіл команда',
    ibanLabel: 'ИИК / IBAN (Міндетті)',
    ibanRequired: 'Шоттың ИИК / IBAN енгізіңіз. Бұл шот импорты үшін қажет.',
    colorLabel: 'Белгі түсі',
    saveAccount: 'Шотты сақтау',
    newFund: 'Жаңа ��ор',
    fundNameLabel: 'Қор атауы',
    fundNamePlaceholder: 'Жалақы, Резерв...',
    fundPercentLabel: 'Аударым пайызы (%)',
    fundTargetLabel: 'Мақсатты сома',
    saveFund: 'Қорды сақтау',
    yourAccounts: 'Сіздің шоттарыңыз',
    sortLabel: 'Сұрыптау...',
    sortAlpha: 'Әліпби бойынша (А-Я)',
    sortBalDesc: 'Баланс бойынша (к��муі)',
    sortBalAsc: 'Баланс бойынша (өсуі)',
    sortPctDesc: 'Пайыз бойынша (кемуі)',
    addBtn: 'Қосу',
    noActiveAccounts: 'Белсенді шоттар жоқ',
    primaryBadge: 'Негізгі',
    fundsTitle: 'Қорлар (Жинақтар)',
    noActiveFunds: 'Белсенді қорлар жоқ',

    // --- AccountModal ---
    primaryAccount: 'Негізгі ��от',
    hiddenAccountEdit: 'Жасырын шот (Жеке)',
    visibilityPrefix: 'Көрінуі: ',
    balancePrefix: (bal) => `Баланс: ${bal}`,
    historyBtn: 'Тарих',
    ibanShort: 'ИИК:',
    incomeCategories: 'Кі��іс санаттары',
    noFreeCategories: 'Бос санаттар жоқ',
    noLinkedCategories: 'Байланысты санаттар жоқ',
    removeLink: 'Алып тастау',
    clientsTitle: 'Клиенттер',
    allClientsLinked: 'Барлық клиенттер байланысты',
    noLinkedClients: 'Байланысты клиенттер жоқ',
    enterIban: 'ИИК / IBAN енгізіңіз. Бұл шоттарды автоматты тану үшін қажет.',

    // --- FundModal ---
    fundPercentExceeds: 'Қорлар пайыздарының сомасы 100%-дан асып кетеді',
    inFundPrefix: (bal) => `Қорда: ${bal}`,
    expenseCategories: 'Шығыс санаттары',
    noLinkedExpenseCategories: 'Байланысты санаттар жоқ',
    noFreeExpenseCategories: '��ос санаттар жоқ',

    // --- QuickHiddenAdjustModal ---
    topUpStash: 'Жинақты толтыру',
    withdrawStash: 'Жинақтан алу',
    hiddenAccountName: 'Жасырын шот',
    stashNow: 'Жинақта қазір',
    topUpAmount: 'Толтыру сомасы',
    withdrawAmount: 'Алу сом��сы',
    commentForSelf: 'Түсініктеме (өзіңіз үшін)',
    topUpPlaceholder: 'Сыйлықтан салдым...',
    withdrawPlaceholder: 'Сыйлыққа жұмсадым...',
    enterAmount: 'Соманы енгізіңіз',
    insufficientStash: (balance) => `Қаражат жеткіліксіз. Жинақта: ${balance}`,
    topUpComment: 'Жинақты толтыр��',
    withdrawComment: 'Жинақтан алу',
    topUpBtn: 'Толтыру',
    withdrawBtn: 'Алу',

    // --- Көрсеткіштер (title атрибуттары) ---
    editTooltip: 'Өзгерту',
    toArchiveTooltip: 'Мұрағатқа',
    closeTooltip: 'Жабу',
    hiddenAccountTooltip: 'Жасырын шот',
    quickIncomeTooltip: 'Жылдам түсім',
    quickExpenseTooltip: 'Жылдам шығыс',
    accountHelpFallback: 'Шот — бұл сіздің банк шотыңыз немесе кассаңыз. Оған ақша түседі және одан шығыстар жасалады.',
    fundHelpFallback: 'Қор — бұл виртуалды жинақ. Ақша пайыздарға сәйкес қорларға бөл��неді.',
    ibanHelpTooltip: 'ИИК PDF импорты кезінде шоттарды автоматты тану үшін қажет.',
  },

  // ─── ContractorsTab ───
  contractors: {
    title: 'Контрагенттер анықтамалығы',
    subtitle: (count) => `Клиенттер мен жеткізушілер базасы (${count})`,
    addContractor: 'Контрагент қосу',
    searchPlaceholder: 'Атау немесе БИН бойынша іздеу...',
    sortRecent: 'Алдымен соңғылар',
    sortVolDesc: 'Көлемі бойынша (кемуі)',
    sortVolAsc: 'Көлемі бойынша (өсуі)',
    sortNameAsc: 'Әліпби бойынша (А-Я)',
    sortNameDesc: 'Әліпби бойынша (Я-А)',
    notFound: 'Контрагенттер табылмады',
    clientsTitle: 'Клиенттер (Бізге төлейді)',
    suppliersTitle: 'Жеткізушілер (Біз төлейміз)',
    emptyList: 'Тізім бос',
    partner: '↔ Серіктес',
    oweUs: (amount) => `Бізге: ${amount}`,
    weOwe: (amount) => `Біз: ${amount}`,
    zeroBalance: 'Нөлде',
    noExtraData: 'Қосымша деректер жоқ',
    legalNotSpecified: 'Заңды тұлға көрсетілмеген',
    client: 'Клиент',
    supplier: 'Жеткізуші',
    // Модалка
    editTitle: 'Өңдеу',
    newTitle: 'Жаңа контрагент',
    roleRequired: 'Контрагент рөлі (Міндетті)',
    clientRole: 'Клиент (Бізге төлейді)',
    supplierRole: 'Жеткізуші (Біз төлейміз)',
    nameLabel: 'Атауы (сіз үшін)*',
    namePlaceholder: 'Мысалы: ЖК Иванов',
    legalNameLabel: 'Заңды атауы',
    legalNamePlaceholder: 'ЖШС Ромашка',
    binLabel: 'БИН / ИИН',
    binPlaceholder: '123456789012',
    phoneLabel: 'Телефон',
    phonePlaceholder: '+7 700 000 00 00',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Нақты мекенжай',
    addressPlaceholder: 'Астана қ., Абай к-сі 1',
    saveContractor: 'Контрагентті сақтау',
    saveChanges: 'Өзгерістерді сақтау',
    enterName: 'Контрагент атауын енгізіңіз',
    selectType: 'Контрагент түрін кем дегенде біреуін таңдаңыз (Клиент немесе Жеткізуші)',
    archiveConfirm: 'Контрагентті мұрағатқа жіберу керек пе?',
    enterNameShort: 'Атауды енгізіңіз',
    // Қаржылық жиынтық
    financeSummary: 'Қаржылық жиынтық',
    periodAll: 'Барлық уақыт үшін',
    periodMonth: 'Ай үшін',
    period6months: 'Жарты жыл үшін',
    periodYear: 'Жыл үшін',
    periodCustom: 'Еркін',
    fromClient: 'Клиенттен (Бізге төлейді)',
    toSupplier: 'Жеткізушіге (Біз төлейміз)',
    alreadyPaidUs: 'Бізге төлегені:',
    stillOwesUs: 'Әлі төлеуі керек:',
    alreadyPaidThem: 'Біз төлегеніміз:',
    ourDebt: 'Біздің борышымыз (Несие):',
    oweNow: 'Қазір тиесілі:',
    totalPaid: 'Барлығы төленді:',
    opsForPeriod: 'Кезеңдегі операциялар',
    noOpsInPeriod: 'Бұл кезеңде операциялар жоқ',
    ourDebtLabel: 'Біздің борыш',
    clientDebtLabel: 'Клиент борышы',
    paidStatus: '✓ Төленді',
    awaitingPayment: 'Төлем күтілуде',
    linkedCategories: 'Байланысты санаттар',
    // Байланыстар
    availableAccounts: 'Қолжетімді шоттар (Клиент үшін)',
    accountsHint: 'Ештеңе таңдалмаса — барлық шоттар үшін қолжетімді.',
    incomeCategories: 'Кіріс санаттары:',
    noIncomeCategories: 'Кіріс санаттары жоқ',
    availableFundsAndCats: 'Қолжетімді қорлар мен санаттар (Жеткізуші үшін)',
    fundsHint: 'Ештеңе таңдалмаса — барлық жерде қолжетімді.',
    fundsLabel: 'Қорлар:',
    expenseCategories: 'Шығыс санаттары:',
    editTitleFull: 'Өңдеу',
    archiveConfirmDetailed: (name) => `"${name}" контрагентін мұрағатқа жіберу керек пе?`,
    archiveHint: 'Контрагент жаңа операциялар жасау кезінде тізімдерден жоғалады, бірақ онымен байланысты барлық өткен операциялар өзгеріссіз қалады. Оны Бірыңғай мұрағаттан қалпына келтіруге болады.',
    accountsLabelClient: 'Қолжетімді шоттар (Клиент)',
    fundsAndCatsSupplier: 'Қорлар мен санаттар (Жеткізуші)',
    titleLabel: 'Атауы (тақырып)*',
    legalNameRequired: 'Заңды атауы*',
    binRequired: 'БИН / ИИН*',
    enterLegalNameError: 'Контрагенттің заңды атауын енгізіңіз',
    enterBinError: 'Контрагенттің БИН/ИИН енгізіңіз',
    enterLegalNameShort: 'Заңды атауды енгізіңіз',
    enterBinShort: 'БИН/ИИН енгізіңіз',
    duplicateError: (bin, name) => `Осындай БИН (${bin}) немесе заңды атаумен контрагент бар: ${name}`,
    selectTypeError: 'Кем дегенде бір рөлді таңдаңыз (Клиент немесе Жеткізуші)',
    phoneUpperLabel: 'ТЕЛЕФОН',
    emailUpperLabel: 'EMAIL',
    addressUpperLabel: 'МЕКЕНЖАЙ',
    clientPaysUs: 'Клиент (Бізге төлейді)',
    supplierWePay: 'Жеткізуші (Біз төлейміз)',
    debtToUsLabel: 'Бізге борыш:',
    ourDebtLabel2: 'Біздің борыш:',
    currentBindings: 'Ағымдағы байланыстар',
    accountsAndIncomeCategories: 'Шоттар мен Кіріс санаттары',
    fundsAndExpenseCategories: 'Қорлар мен Шығыс санаттары',
    noBindings: 'Байланыстар жоқ',
    lastOperations: 'Соңғы операциялар',
    historyEmpty: 'Тарих бос',
    noDescriptionShort: 'Сипаттамасыз',
    paidStatusUpper: 'ТӨЛЕНДІ',
    awaitingStatusUpper: 'КҮТІЛУДЕ',
    clientTypeLabel: 'Клиент',
    supplierTypeLabel: 'Жеткізуші',
  },

  // ─── HistoryModal ───
  history: {
    title: (name) => `Тарих: ${name}`,
    noOps: 'Операциялар жоқ',
    pageTitle: 'Әрекеттер тарихы',
    pageSubtitle: 'Жүйе белсенділігінің журналы және өзгерістер аудиті',
    exportTsv: 'Экспорт .TSV',
    allActions: 'Барлық әрекеттер',
    accountantsTab: 'Бухгалтерлер',
    adminsTab: 'Әкімшілер',
    searchPlaceholder: 'Атау немесе әрекет бойынша іздеу...',
    eventDetails: 'Оқиға мәліметтері',
    dateTime: 'Күні мен уақыты',
    executor: 'Орындаушы',
    action: 'Әрекет',
    stateChanges: 'ЖАҒДАЙ ӨЗГЕРІСТЕРІ',
    wasBefore: 'БОЛДЫ',
    becameAfter: 'БОЛДЫ (жаңа)',
    understood: 'Түсінікті',
    adminRole: 'Әкімші',
    actionTypes: {
      all: 'Барлық әрекеттер',
      income: 'Кірістер',
      expense: 'Шығыстар',
      transfer: 'Аударымдар',
      accounts: 'Шоттар/Қорлар',
      categories: 'Санаттар',
      users: 'Қызметкерлер',
      debts: 'Міндеттемелер',
      contractors: 'Контрагенттер',
      system: 'Жүйе',
    },
    colDateTime: 'Күні / Уақыты',
    colUser: 'Пайдаланушы',
    colType: 'Түрі',
    colAction: 'Әрекет',
    colChanges: 'Өзгерістер',
    colDetails: 'Мәліметтер',
    filterActionType: 'Әрекет түрі',
    filterUser: 'Пайдаланушы',
    filterDateFrom: 'Күні бастап',
    filterDateTo: 'Күні дейін',
    filterAll: 'Барлығы',
    resetFilters: 'Сүзгілерді тастау',
  },

  // ─── Мұрағат (ArchiveTab.js) ───
  archive: {
    tabAccounts: 'Шоттар',
    tabFunds: 'Қорлар',
    tabContractors: 'Контрагенттер',
    tabCategories: 'Санаттар',
    tabTx: 'Операциялар',
    deleteForever: 'Біржола жою?',
    deleteForeverDesc: 'Бұл әрекетті қайтару мүмкін емес. Жазба қалпына келтіру мүмкіндігінсіз жойылады. Операциялардың қаржылық әсері мұрағатталу кезінде жойылған.',
    restoreFromArchive: 'Мұрағаттан қалпына келтіру?',
    restoreTxDesc: (amount, verb) => `${amount} сомасы ағымдағы балансар мен есептерде қайта есепке алынады. Қаражат тиісті шоттарға/қорларға ${verb}.`,
    restoreTxCredited: 'есептеледі',
    restoreTxDebited: 'шегеріледі',
    restoreAccFundDesc: (name) => `"${name}" шоты/қоры операцияларда таңдауға қайта қолжетімді болады және негізгі экранда көрсетіледі. Баланс өзгермейді.`,
    restoreContractorDesc: (name) => `"${name}" контрагенті жаңа операцияларда таңдауға қайта қолжетімді болады.`,
    restoreCategoryDesc: (name) => `"${name}" санаты қаражатты бөлу үшін қайта қолжетімді болады.`,
    noArchived: (entity) => `Мұрағатталған ${entity} жоқ`,
    emptyAccounts: 'Шоттар',
    emptyFunds: 'Қорлар',
    emptyContractors: 'Контрагенттер',
    emptyCategories: 'Санаттар',
    emptyTx: 'Операциялар',
    balance: 'Баланс:',
    restore: 'Қалпына келтіру',
    income: 'Кіріс',
    expense: 'Шығыс',
    transfer: 'Аударым',
    incomePrefix: '[Кіріс] ',
    expensePrefix: '[Шығыс] ',
  },

  // ─── Заңды құжаттар (LegalDocsModal) ───
  legal: {
    tosTitle: 'Пайдалану келісімі (Жария оферта)',
    privacyTitle: 'Құпиялылық саясаты және деректерді қорғау',
    consentTitle: 'Деректерді жинау және өңдеуге келісім',
    agreeBtn: 'Мен таныстым және келісемін',
  },

  // ─── Анықтамалар ───
  help: {
    contractor: `Бұл не үшін керек? Контрагент — бұл сіз есеп жүргізетін тұлға.

Оның рөлін таңдамайынша, қалған өрістер құлыпталады.
• Клиент (Сізге төлейді): Сіздің Шоттарыңызға (кірістерге) және Кіріс санаттарына байланысты.
• Жеткізуші (Сіз төлейсіз): Қорларға және Шығыс санаттарына байланысты.

Бұл әмбебап серіктес болса (оған да сатасыз, одан да сатып аласыз), екі белгіні де бірден таңдауға болады.`,

    category: `Санаттар не үшін керек?
Санаттар кірістер мен шығыстардың құрылымын талдауға көмектеседі.

• Кіріс санаттары: клиенттер не үшін төлейтінін көрсетеді. Нақты Шотқа байланыстыруға болады.
• Шығыс санаттары: ақша неге жұмсалатынын көрсетеді. Нақты Қорға байланыстыруға болады.

Санатты қорға немесе шотқа байланыстырмай жасауға болады — сонда ол «Жалпы» болады және кез келген операцияда қолжетімді.
Сондай-ақ санатқа контрагенттерді байланыстырып, жұмысты жылдамдатуға болады (олар бірінші ұсынылады).`,

    uncategorized: `Бұл санат неге «Санатсыз» деп аталады?

Мұнда жасау кезінде нақты санат берілмеген барлық операциялар автоматты түрде түседі.

Тәртіпке келтіру қалай?
Төмендегі тізімдегі кез келген операцияны басыңыз — өңдеу терезесі ашылады. Қажетті санатты таңдап, сақтаңыз, сонда операция автоматты түрде дұрыс бөлімге ауысады!`,

    debtReceivable: `Дебиторлық берешек (Бізге тиесілі)

Мұнда клиенттер немесе серіктестер сізге төлеуге уәде берген сомалар тіркеледі.
• Жоспарланған кірісті қажетті Шот пен Санатқа бірден байланыстыра аласыз.
• Борышкер ақшаны қайтарғанда, «Төледі» батырмасын басыңыз — жүйе автоматты түрде толықтыру транзакциясын жасап, бұл борышты жабады.`,

    debtPayable: `Кредиторлық берешек (Біз тиеспіз)

Мұнда жеткізушілер, қызметкерлер немесе серіктестер алдындағы міндеттемелеріңіз тіркеледі.
• Борышты қайтаруды жоспарлаған Қорды және Шығыс санатын көрсетіңіз.
• Борышты өтегенде, «Төледік» батырмасын басыңыз — жүйе автоматты түрде қордан ақша алып, борышты тарихқа жібереді.`,

    primaryAccount: `«Негізгі» не дегенді білдіреді?

Бұл шот немесе қор тізімдерде әрқашан бірінші көрсетіледі. Жұмысты жылдамдату үшін жаңа кірістер немесе шығыстар қосқанда дәл осы ұсынылады.`,

    hiddenAccount: `«Жасырын шот» дегеніміз не?

Бұл сіздің жеке «қорыңыз» немесе резервіңіз.
• Ол компанияның жалпы балансында ескерілмейді (басқа қойындыларда).
• Оған түсімдер қорларға бөлінбейді.
• Оған кіріс және шығыс санаттары қажет емес.

Құпиялылықты баптай аласыз: оны тек өзіңізге немесе компаниядағы белгілі рөлдерге көрінетін етіңіз.`,

    quickIncome: `Жылдам түсім қалай жұмыс істейді?

1. Борышты қайтару: Бұрын жасалған борышты төлесе, «Борышты өтеу» тізімінен оны таңдаңыз. Жүйе барлық өрістерді (сома, клиент, санат) өзі толтырады және Міндеттемелерде борышты автоматты жабады.

2. Жаңа төлем: Бұл жаңа түсім болса, борыш таңдауды өткізіңіз. Соманы енгізіп, санатты (не үшін төленді) және клиентті (кім төледі) таңдаңыз.

💡 Маңызды: Ақша берілген пайыздарға сәйкес қорларыңызға (қоржындарға) автоматты бөлінеді!`,

    setupAccount: `INMONEX-тегі «Шот» дегеніміз не?

Шот — бұл сіздің нақты банк шотыңыздың немесе кассаңыздың көрінісі. Мұнда клиенттерден ақша түседі, осы жерден шығыстарды төлейсіз.

• Әр шот нақты уақыттағы балансты көрсетеді
• Шоттар арасындағы аударымдар да бақыланады
• Қалағанша шот қосуға болады

Типтік мысалдар:
💳 Kaspi Бизнес — төлемдер қабылдаудың негізгісі
🏦 Halyk — ірі есеп айырысулар үшін
💵 Қолма-қол ақша — кеңседегі касса`,

    setupFund: `Қор — бұл сіздің бизнесіңіз үшін «ақылды қоржын».

Клиент сізге төлеген сайын, ақша берілген пропорцияларға сәйкес қорларға автоматты «тарайды».

Не үшін? «Шотта ақша бар, бірақ салықтарға / жалақыларға жетпейді» тұзағына түспеу үшін.

• Жалақы (50%) — қанша төлей алатыныңызды әрқашан білесіз
• Салықтар (20%) — салық органынан тосын жағдай болмайды
• Пайда (30%) — бизнестен жеке табысыңыз

💡 Қорлар ақшаны бұғаттамайды — бұл виртуалды есеп. Әр бағытқа қанша «тиесілі» екенін әрқашан көресіз.`,

    setupFundLink: `Шоттар мен Қорлар қалай байланысты?

📥 Шот — ақша ҚАЙДА жатыр (Kaspi, Halyk, касса)
📊 Қор — бұл ақша НЕ ҮШІН арналған (жалақы, салықтар, пайда)

Мысал:
→ Клиент Kaspi-ге 1 000 000 ₸ аударды
→ INMONEX автоматты түрде жазады:
   • 500 000 ₸ «Жалақы» қорына
   • 200 000 ₸ «Салықтар» қорына
   • 300 000 ₸ «Пайда» қорына

Ақша физикалық түрде Kaspi-де қалады, бірақ сіз әр қордан қанша жұмсауға болатынын анық көресіз.`,

    debtPayment: `Міндеттемені төлеу қалай жұмыс істейді?

Клиент борышты төлегенде (дебиторлық) немесе сіз борышты төлегенде (кредиторлық), жүйе автоматты түрде:

📥 Дебиторлық (Бізге төледі):
• Таңдалған шотқа түсім транзакциясын жасайды
• Соманы қорларға бөледі (пайыздарға сәйкес)
• Міндеттемеден түсініктеме мен санатты жазады

📤 Кредиторлық (Біз төледік):
• Таңдалған шоттан шығыс транзакциясын жасайды
• Көрсетілген қордан есептен шығарады
• Шығыс санатын жазады

💡 Барлық деректер (контрагент, санат, түсініктеме) міндеттемеден автоматты толтырылады. Сіз тек растап, қажет болса шотты таңдауыңыз керек.`,

    debtPartial: `Ішінара төлем — бұл не?

Клиент бүкіл соманы емес, бір бөлігін ғана төлесе, «Ішінара төлемді» пайдаланыңыз:

1. Нақты қанша төленгенін енгізіңіз
2. Жүйе осы сомаға транзакция жасайды
3. Борыш қалдығы автоматты қайта есептеледі
4. Төлем тарихында әр төлем көрінеді

Мысал:
• Борыш: 1 000 000 ₸
• Бірінші төлем: 400 000 ₸ → қалдық 600 000 ₸
• Екінші төлем: 600 000 ₸ → борыш жабылды ✅

💡 Прогресс-бар қанша төленгенін көрсетеді. Төлемдер сомасы борыштың толық сомасына жеткенде, міндеттеме автоматты жабылады.`,

    debtViewInfo: `Міндеттеме туралы толық ақпарат.

Мұнда толық суретті көресіз: кім тиесілі, қанша, не үшін және төлем мәртебесі қандай.

• «Жалпы сома» — міндеттеменің бастапқы сомасы
• «Төленді» — қанша төленді (барлық төлемдер сомасы)
• «Қалдық» — тағы қанша төлеу керек
• Байланыстар — қай шотқа, қорға және санатқа төлем байланысты

Міндеттемені төлеу, өңдеу немесе мұрағаттау үшін төмендегі батырмаларды пайдаланыңыз.`,

    perm_manageAccounts: `Шоттарға қолжетімділік:
Шоттар балансын қарауға, жаңа шоттар жасауға, олардың деректемелерін (IBAN) өңдеуге және мұрағатқа жіберуге мүмкіндік береді.`,

    perm_manageFunds: `Қорларға қолжетімділік:
Виртуалды қоржындар (қорлар) жасауға, бөлу пайыздары мен мақсатты сомаларды баптауға, сондай-ақ пайдаланылмайтын қорларды мұрағаттауға мүмкіндік береді.`,

    perm_manageCategories: `Санаттарды басқару:
Кіріс және шығыс санаттарын жасауға және өңдеуге, оларды есепті автоматтандыру үшін нақты шоттарға немесе қорларға байланыстыруға мүмкіндік береді.`,

    perm_manageContractors: `Контрагенттермен жұмыс:
Клиенттер мен жеткізушілер базасын жасауға және өңдеуге, олардың қаржылық тарихын қарауға және санаттармен байланыстарды басқаруға құқық береді.`,

    perm_manageOperations: `Операциялармен әрекеттер:
Жаңа түсімдер, шығыстар мен аударымдарды жүргізуге, сондай-ақ соңғы әрекеттерді болдырмауға және транзакцияларды өңдеуге мүмкіндік береді.`,

    perm_manageDebts: `Міндеттемелерді басқару:
Борыштарды (дебиторлық және кредиторлық) тіркеуге, шот-фактуралар шығаруға және оларды төленген деп белгілеуге, проводкаларды автоматты жасауға мүмкіндік береді.`,
  },

  // ─── Ұйым таңдау (жаңа кілттер) ───
  orgSelect: {
    welcome: (name) => `Қош келдіңіз, ${name}`,
    choose: 'Ұйымды таңдаңыз',
    create: 'Ұйым құру',
    noOrgs: 'Сізде әлі ұйымдарға қол жетімділік жоқ. Қол жетімділік алу үшін ұйым иесіне хабарласыңыз.',
  },

  // ─── Шоттар мен Қорлар қойындысы (AccountsFundsTab) ───
  af: {
    // Қойындылар
    tabSummary: 'Жиынтық',
    tabAccounts: 'Шоттар',
    tabFunds: 'Қорлар',
    tabArchive: (count) => `Мұрағат (${count})`,
    // Стат-карточкалар
    onAccounts: 'Шоттарда',
    inFunds: 'Қорларда',
    incomeMonth: 'Кіріс (ай үшін)',
    expenseMonth: 'Шығыс (ай үшін)',
    // Операция түрлері
    transferFundShort: 'Аударым қ.',
    // Кассалық алшақтық
    cashGapTitle: 'Кассалық алшақтық (тапшылық)!',
    cashGapDesc: (fundTotal, accTotal) => `Қорлардағы сома (${fundTotal}) шоттардағы нақты ақшадан (${accTotal}) асып кетеді. Балансты тексеріңіз.`,
    // Күйлер
    accountsState: 'Шоттар күйі',
    fundsState: 'Қорлар күйі',
    adjustFundsTitle: 'Баланстарды түзету',
    adjustTitle: 'Қорлар баланстарын түзету',
    adjustSubtitle: 'Әр қор балансын қолмен өзгерту',
    adjustTotalAccounts: 'Шоттардағы жалпы сома',
    adjustTotalFunds: 'Қорлардағы жиыны',
    adjustBalanced: '✓ Баланстар сәйкес келеді',
    adjustNeedAdd: 'Тағы бөлу керек',
    adjustNeedRemove: 'Қорларда артық',
    adjustSave: 'Сақтау',
    adjustAuditDesc: 'Қорлар баланстарын қолмен түзету',
    // FundManager
    fmTitle: 'Қорларды басқару',
    fmSubtitle: 'Пайыздар, баланстар, тәртіп, қосу және жою',
    fmHelp: 'Қорларды басқарыңыз: пайыздарды, баланстарды, тәртіпті өзгертіңіз. Қайта бөлумен қорларды қосыңыз және жойыңыз.',
    fmTogglePercent: 'Пайыздарды өзгерту',
    fmToggleBalance: 'Балансты өзгерту',
    fmTogglePctHint: 'Пайыздар тек жаңа түсімдерді бөлуге әсер етеді',
    fmToggleBalHint: 'Әр қордың ағымдағы балансын өзгерту',
    fmFundName: 'Атауы',
    fmBalance: 'Баланс',
    fmDeleteFund: 'Қорды жою',
    fmAddFund: 'Қор қосу',
    fmInfoAccounts: 'Шоттардағы сома',
    fmInfoFunds: 'Қорлардағы сома',
    fmInfoBalanced: '✓ Сәйкес келеді',
    fmInfoDiff: 'Айырмашылық',
    fmPctTotal: 'Пайыздар',
    fmAddStep1: 'Жаңа қор',
    fmAddStep2: 'Пайыздарды қайдан алу керек?',
    fmAddStep2Hint: 'Әр бар қордан қанша пайыз алу керектігін көрсетіңіз',
    fmAddNeedTake: 'Алу керек',
    fmAddTaken: 'Көрсетілген',
    fmAddBack: 'Артқа',
    fmAddConfirm: 'Қосу',
    fmAddNext: 'Келесі',
    fmAddPctMismatch: (need, taken) => `${need}% бөлу керек, ${taken}% көрсетілген`,
    fmDelTitle: name => `«${name}» қорын жою`,
    fmDelStep1Hint: pct => `${pct}% қалған қорлар арасында бөліңіз`,
    fmDelAutoDistribute: 'Пропорционалды бөлу',
    fmDelStep2Hint: bal => `${bal} қайда аудару керек?`,
    fmDelWriteOff: 'Есептен шығару (нөлдеу)',
    fmDelConfirm: 'Жою',
    fmDelPctMismatch: (need, taken) => `${need}% бөлу керек, ${taken}% көрсетілген`,
    fmValPctNot100: pct => `Пайыздар сомасы: ${pct}%. 100% болуы керек.`,
    fmValPctAction: d => `${d}%-ға жаңа қор қосыңыз немесе ${d}%-ды бар қорларға бөліңіз.`,
    fmValPctOverAction: d => `Бар қорлардан артық ${d}%-ды алыңыз.`,
    fmValFundsExceed: 'Қорлар сомасы шоттардағы сомадан асады. Баланстарды азайтыңыз.',
    fmValBalMismatch: diff => `Шоттар мен қорлар арасындағы айырмашылық: ${diff}. Қорлар балансы шоттар балансына сәйкес болуы керек.`,
    fmAddFreePct: pct => `Бос ${pct}% — қорларға бөлінбеген`,
    fmAddNoFreePct: 'Барлық 100% бөлінген. Жаңа қор пайыздары бар қорлардан алынады.',
    fmAddHasFree: pct => `Сізде бос ${pct}% бар (қорсыз).`,
    fmAddNeedFromOthers: (total, need) => `Жаңа қор: ${total}%. Басқа қорлардан алу керек: ${need}%.`,
    fmAuditAdjust: 'Қорларды түзету',
    fmSave: 'Сақтау',
    fundNamePlaceholder: 'Қор атауы',
    // Қайталанулар
    dupAccName: 'Мұндай атаумен шот бұрыннан бар',
    dupAccIban: 'Мұндай IBAN-мен шот бұрыннан бар',
    dupFundName: 'Мұндай атаумен қор бұрыннан бар',
    dupFundNameSave: 'Бірнеше қор бірдей атаумен. Атауларды өзгертіңіз.',
    // Бос тізімдер
    noActiveAccounts: 'Белсенді шоттар жоқ',
    noActiveFunds: 'Белсенді қорлар жоқ',
    noActiveDebts: 'Белсенді борыштар жоқ',
    noOps: 'Операциялар жоқ',
    // Плейсхолдерлер
    accountNamePlaceholder: 'Шот атауы',
    ibanPlaceholder: 'ИИК / IBAN (Міндетті)',
    fundNamePlaceholder: 'Атауы',
    // Кеңестер
    ibanTooltip: 'ИИК PDF импорты кезінде шоттарды автоматты тану үшін қажет.',
    // Бейджик
    primaryBadge: 'Негізгі',
    hiddenAccountTitle: 'Жасырын шот',
    // Батырма тақырыптары
    addToStash: 'Жинақты толтыру',
    takeFromStash: 'Жинақтан алу',
    quickIncomeTitle: 'Жылдам түсім',
    quickExpenseTitle: 'Жылдам шығыс',
    historyBtn: 'Тарих',
    // Борыштар (панельдер)
    receivableLabel: 'Дебиторлық',
    payableLabel: 'Кредиторлық',
    theyOweUs: 'Бізге тиесілі',
    weOwe: 'Біз тиеспіз',
    dueDate: (date) => `Дейін: ${date}`,
    // «Қорлардың болашақ толықтырылуы» модалы
    futureFundsTitle: 'Қорлардың болашақ толықтырылуы',
    futureFundsHint: 'Бұл ақша әлі түскен жоқ — дебиторлар толық төлеген жағдайда әр қорға қанша түсетіні есептеледі',
    futureFundsNoDebts: 'Белсенді міндеттемелер жоқ',
    futureFundsNoFunds: 'Белсенді қорлар жоқ',
    // Соңғылар
    recentOps: 'Соңғы операциялар',
    // Ескертулер
    enterAccountName: 'Шот атауын енгізіңіз',
    enterIban: 'ИИК / IBAN енгізіңіз. Бұл PDF импорты кезінде шоттарды автоматты тану үшін қажет.',
    exceedsHundred: '100%-дан асып кетеді',
    // Растау
    archiveAccountMsg: (name) => `"${name}" шотын мұрағатқа жасыру керек пе?`,
    archiveAccountExtra: 'Бұл шот таңдау тізімдерінен жоғалады, бірақ онымен байланысты барлық өткен операциялар есептерде өзгеріссіз қалады. Оны Бірыңғай мұрағаттан қалпына келтіруге болады.',
    archiveFundMsg: (name) => `"${name}" қорын мұрағатқа жіберу керек пе?`,
    archiveFundExtra: 'Қор жасырылады, бірақ оның бойынша барлық операциялар тарихта сақталады. Оны Бірыңғай мұрағаттан қалпына келтіруге болады.',
    defaultCompanyName: 'Біздің компания',
    // Шот модалкасы (AccountModal)
    accModalEdit: 'Өзгерту',
    accModalArchive: 'Мұрағатқа',
    accModalPrimaryLabel: 'Негізгі шот',
    accModalHiddenLabel: 'Жасырын шот (Жеке)',
    addAccPrimaryHint: 'Негізгі шот операцияларды құру кезінде әдепкі бойынша қолданылады. Тек біреу болуы мүмкін.',
    addAccHiddenHint: 'Жасырын шот жалпы балансқа және статистикаға кірмейді. Жеке жинақтар үшін қолайлы.',
    accModalVisibilityLabel: 'Бұл шотты кім көре алады?',
    accModalVisMe: 'Тек мен (иесі)',
    accModalVisMeAccountant: 'Мен және бухгалтер',
    accModalVisAdmins: 'Мен, бухгалтер және әкімші',
    accModalVisAll: 'Бүкіл команда',
    accModalIbanLabel: 'ИИК / IBAN (Міндетті)',
    accModalIbanDisplay: 'ИИК / IBAN:',
    accModalBalance: (bal) => `Баланс: ${bal}`,
    accModalVisibilityDisplay: 'Көрінуі:',
    accModalEnterName: 'Шот атауын енгізіңіз',
    accModalEnterIban: 'ИИК / IBAN енгізіңіз. Бұл шоттарды автоматты тану үшін қажет.',
    accModalIncomeCategories: 'Кіріс санаттары',
    accModalAddBtn: 'Қосу',
    accModalNoFreeCategories: 'Бос санаттар жоқ',
    accModalNoLinkedCategories: 'Байланысты санаттар жоқ',
    accModalRemoveBtn: 'Алып тастау',
    accModalClients: 'Клиенттер',
    accModalAllClientsLinked: 'Барлық клиенттер байланысты',
    accModalNoLinkedClients: 'Байланысты клиенттер жоқ',
    // Қор модалкасы (FundModal)
    fundModalExceedsHundred: 'Қорлар пайыздарының сомасы 100%-дан асып кетеді',
    fundModalBelowHundred: (total, missing) => `Пайыздар сомасы = ${total}%. 100% болуы керек. ${missing}% жетіспейді`,
    fundModalInFund: (bal) => `Қорда: ${bal}`,
    fundModalExpenseCategories: 'Шығыс санаттары',
    fundModalNoFreeCategories: 'Бос санаттар жоқ',
    fundModalNoLinkedCategories: 'Байланысты санаттар жоқ',
    fundModalRemoveBtn: 'Алып тастау',
    // Жылдам түсім (QuickIncomeModal)
    qIncTitle: 'Жылдам түсім',
    qIncAccountLabel: 'Есептесу шоты',
    qIncCurrentBalance: 'Ағымдағы баланс',
    qIncDebtLabel: '1. Борышты өтеу (Міндетті емес)',
    qIncNewRevenue: '-- Бұл жаңа түсім (борыш емес) --',
    qIncNoDebtors: 'Белсенді борышкерлер жоқ',
    qIncAmountLabel: 'Түсім сомасы *',
    qIncCategoryLabel: 'Санат (Не үшін?)',
    qIncNoCategory: 'Санатсыз',
    qIncClientLabel: 'Клиент (Кім төлейді?)',
    qIncClientNone: 'Көрсетілмеген',
    qIncCommentLabel: 'Түсініктеме',
    qIncCommentPlaceholder: 'Төлем мәліметтері...',
    qIncSubmit: 'Есепке алу',
    qIncDebtPayComment: 'Борыш төлемі',
    qIncEnterAmount: 'Дұрыс соманы енгізіңіз',
    qIncRemainder: (amount) => `қалдық: ${amount}`,
    // Жылдам шығыс (QuickExpenseModal)
    qExpTitle: 'Жылдам шығыс',
    qExpFundLabel: 'Есептен шығару қоры',
    qExpFundAvailable: 'Қорда қолжетімді',
    qExpPayDebtLabel: 'Міндеттемені өтеу',
    qExpNoDebtBinding: '— Міндеттемеге байланыссыз —',
    qExpDebtDefault: 'Міндеттеме',
    qExpDebtRemainder: (amount) => `қалдық: ${amount}`,
    qExpAccountLabel: 'Есептен шығару шоты (нақты ақша қайдан кетеді)',
    qExpCategoryLabel: 'Шығыс санаты',
    qExpNoCategory: 'Санатсыз',
    qExpAmountLabel: 'Шығыс сомасы',
    qExpCommentLabel: 'Түсініктеме',
    qExpCommentPlaceholder: 'Кімге, не үшін...',
    qExpSubmit: 'Есептен шығару',
    qExpEnterAmount: 'Соманы енгізіңіз',
    qExpFundInsufficient: (fundName, available) => `"${fundName}" қорында қаражат жеткіліксіз. Қолжетімді: ${available}`,
    qExpAccountInsufficient: (available) => `Таңдалған шотта қаражат жеткіліксіз. Қолжетімді: ${available}`,
    qExpPayDebtComment: 'Міндеттемені өтеу',
    // Жасырын шот (QuickHiddenAdjustModal)
    qHidAddTitle: 'Жинақты толтыру',
    qHidTakeTitle: 'Жинақтан алу',
    qHidAccountLabel: 'Жасырын шот',
    qHidCurrentBalance: 'Жинақта қазір',
    qHidAddAmountLabel: 'Толтыру сомасы',
    qHidTakeAmountLabel: 'Алу сомасы',
    qHidCommentLabel: 'Түсініктеме (өзіңіз үшін)',
    qHidAddPlaceholder: 'Сыйлықтан салдым...',
    qHidTakePlaceholder: 'Сыйлыққа жұмсадым...',
    qHidSubmitAdd: 'Толтыру',
    qHidSubmitTake: 'Алу',
    qHidEnterAmount: 'Соманы енгізіңіз',
    qHidInsufficient: (available) => `Қаражат жеткіліксіз. Жинақта: ${available}`,
    qHidDefaultCommentAdd: 'Жинақты толтыру',
    qHidDefaultCommentTake: 'Жинақтан алу',
    // Қордан жинаққа аудару (FundToHiddenTransferModal)
    qFTHTitle: 'Жинаққа аудару',
    qFTHFundLabel: 'Қордан',
    qFTHAccountLabel: 'Шоттан',
    qFTHHiddenLabel: 'Жинаққа (жасырын шот)',
    qFTHAmountLabel: 'Сома',
    qFTHCommentLabel: 'Түсініктеме (өзіңіз үшін)',
    qFTHCommentPlaceholder: 'Жеке шығындарға...',
    qFTHSubmit: 'Аудару',
    qFTHEnterAmount: 'Соманы енгізіңіз',
    qFTHFundInsufficient: (fundName, available) => `"${fundName}" қорында қаражат жеткіліксіз. Қолжетімді: ${available}`,
    qFTHAccountInsufficient: (available) => `Таңдалған шотта қаражат жеткіліксіз. Қолжетімді: ${available}`,
    qFTHNoHidden: 'Алдымен жасырын шот құрыңыз',
    qFTHButtonTitle: 'Жинаққа аудару',
    qFTHDefaultComment: 'Жинаққа аудару',
    qFTHHint: 'Қор үшін бұл шығыс, сома кіріс есептеріне түспейді.',
    // Көмек кеңестері
    helpAccount: 'Шот — сіздің банк шотыңыз, кассаңыз немесе әмияныңыз. Оған ақша түседі және одан шығыстар жасалады. Кіріс санаттары мен контрагенттерді шотқа байланыстыруға болады.',
    helpFund: 'Қор — бұл виртуалды жинақ. Әрбір түсім кезінде ақша пайыздарға сәйкес қорларға автоматты түрде бөлінеді. Шығыс санаттарын қорға байланыстыруға болады.',
  },

  // ─── PeriodFilter (жалпы компонент) ───
  periodFilter: {
    label: 'Кезең:',
    d30: '30 күн',
    m3: '3 ай',
    m6: '6 ай',
    y1: 'Жыл',
    all: 'Барлық уақыт',
    custom: 'Өз кезеңі',
    from: 'бастап',
    to: 'дейін',
    contractorsLabel: 'Контрагенттер бойынша сома:',
    categoriesLabel: 'Санаттар бойынша сома:',
  },

  // ─── Ұйымдар бойынша жиынтық (жаңа кілттер) ───
  multiOrg: {
    title: 'Ұйымдар бойынша жиынтық',
    periodLabel: (period, count) => `${period} · ${count} ұйым`,
    totalIncome: 'ЖАЛПЫ КІРІС',
    totalExpense: 'ЖАЛПЫ ШЫҒЫС',
    netProfit: 'ТАЗА ПАЙДА',
    onAccounts: 'ШОТТАРДА',
    comparison: 'Ұйымдарды салыстыру',
    thOrg: 'ҰЙЫМ',
    thIncome: 'Кіріс',
    thExpense: 'Шығыс',
    thProfit: 'Пайда',
    thBalance: 'Баланс',
    thOps: 'Операциялар',
    incomeVsExpense: 'Кіріс vs Шығыс',
    incomeShare: 'Кіріс үлесі',
    topExpenses: 'Шығыс санаттарының топы',
    noExpenses: 'Шығыстар жоқ',
    periodMonth: 'Ай',
    periodQuarter: 'Тоқсан',
    periodYear: 'Жыл',
    needMinOrgs: 'Салыстыру үшін кем дегенде 2 ұйым қажет',
    addOneMoreOrg: 'Салыстырмалы талдауды көру үшін тағы бір ұйым қосыңыз',
    currentMonth: 'Ағымдағы ай үшін',
    currentQuarter: 'Ағымдағы тоқсан үшін',
    currentYear: 'Ағымдағы жыл үшін',
    noIncomeForPeriod: 'Кезеңдегі кіріс туралы деректер жоқ',
    incomeBarName: 'Кіріс',
    expenseBarName: 'Шығыс',
  },

  subscription: {
    useBonuses: 'Бонустарды қолдану',
    bonusAvailable: (amount) => `Қолжетімді: ${amount}`,
    basePrice: 'Негізгі баға',
    bonusDiscount: 'Бонустар',
    finalPrice: 'Төлеуге',
    title: 'Жазылым',
    subtitle: 'Жазылым мен төлемді басқару',
    planTab: 'Тариф',
    historyTab: 'Төлем тарихы',
    statusTrial: 'Сынақ кезеңі',
    statusActive: 'Жазылым белсенді',
    statusExpired: 'Жазылым мерзімі аяқталды',
    daysLeft: (n) => `${n} күн қалды`,
    expiresAt: 'дейін',
    expiredHint: 'Деректерді жасау мен өңдеу бұғатталған. Жазылымды рәсімдеңіз.',
    pricingTitle: 'Жазылым құны',
    firstOrg: 'Бірінші ұйым',
    extraOrg: 'Әрбір қосымша',
    month: 'ай',
    yourOrgs: (n) => `Сіздің ұйымдар (${n})`,
    choosePeriod: 'Кезеңді таңдаңыз',
    month1: '1 ай',
    month3: '3 ай',
    month6: '6 ай',
    month12: '12 ай',
    monthN: (n) => `${n} ай`,
    totalFor: (m) => `${m} айға`,
    payBtn: 'Төлеу',
    payDisabledHint: 'Төлем уақытша қолжетімсіз. Белсендіру үшін әкімшіге хабарласыңыз: inmonex@support.kz',
    historyEmpty: 'Төлемдер жоқ',
    monthsLabel: 'ай',
    paymentSuccess: 'Төленді',
    paymentPending: 'Күту',
    paymentFailed: 'Қате',
    expiringBanner: (days) => `Сынақ кезеңі ${days} күнде аяқталады. Жазылымды рәсімдеңіз!`,
    expiredBanner: 'Жазылым мерзімі аяқталды. Жұмысты жалғастыру үшін жазылымды рәсімдеңіз.',
    renewBtn: 'Жазылым',
    modalTitle: 'Жазылым мерзімі аяқталды',
    modalText: 'Деректерді жасау мен өңдеу үшін белсенді жазылым қажет. Барлық деректеріңіз сақталған.',
    modalBtn: 'Жазылымды рәсімдеу',
    selectOrgs: 'Жазылым үшін ұйымдарды таңдаңыз',
    selectOrgsHint: 'Бірінші ұйым — 5 500 ₸/ай. Әрбір келесі — 3 500 ₸/ай.',
    selectedOrgs: (n) => `Таңдалған ұйымдар: ${n}`,
    savings: (s) => `Үнемдеу: ${s}`,
    selectAtLeast1: 'Кемінде 1 ұйым таңдаңыз',
  },
  deletion: {
    deleteOrg: 'Ұйымды жою',
    deleteOrgConfirm: (name) => `«${name}» ұйымын шынымен жойғыңыз келе ме? Барлық деректер қайтарылмайтын түрде жоғалады.`,
    deleteOrgHint: 'Ұйымды жою барлық деректерді қайтарылмайтын түрде жояды: шоттар, қорлар, операциялар, транзакциялар және тарих. Жоюдан бұрын сақтық көшірме жасауды ұсынамыз.',
    deleteAccount: 'Аккаунтты жою',
    deleteAccountConfirm: 'Аккаунтыңызды шынымен жойғыңыз келе ме? Барлық ұйымдар мен деректер жоғалады.',
    backupTitle: 'Сақтық көшірме',
    backupOrgText: 'Жоюдан бұрын ұйым деректерінің сақтық көшірмесін сақтауды ұсынамыз.',
    backupAccountText: 'Аккаунтты жоюдан бұрын барлық деректердің сақтық көшірмесін сақтауды ұсынамыз.',
    downloadBackup: 'Сақтық көшірмені жүктеу',
    backupDownloaded: 'Сақтық көшірме сақталды',
    deleteWithoutBackup: 'Көшірмесіз жою',
    confirmDelete: 'Жоюды растау',
    deleting: 'Жою...',
    orgDeleted: 'Ұйым жойылды',
    accountDeleted: 'Аккаунт жойылды',
    backupOrg: 'Ұйымның сақтық көшірмесі',
    backupAll: 'Барлық деректердің сақтық көшірмесі',
    step1of2: '1-қадам / 2',
    step2of2: '2-қадам / 2',
  },
};


// ══════════════════════════════════════════════════════════
// АНГЛИЙСКИЙ ЯЗЫК
// ══════════════════════════════════════════════════════════
const en = {

  // ─── Common (used across multiple files) ───
  common: {
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    add: 'Add',
    back: 'Back',
    next: 'Next',
    search: 'Search...',
    noData: 'No data',
    loading: 'Loading...',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    archive: 'Archive',
    noRights: 'No access',
    check: 'Check',
    show: 'Show',
    hide: 'Hide',
    reset: 'Reset',
    download: 'Download',
    noCategoryShort: 'Uncategorized',
    noComment: 'No comment',
    noDescription: 'No description',
    unknown: 'Unknown',
    notSpecified: 'Not specified',
    notSpecifiedFem: 'Not specified',
    defaultUser: 'User',
    system: 'System',
    toArchive: 'To archive',
    saving: 'Saving...',
    genericError: 'Error',
    deleteError: 'Error while deleting',
    pdfUploadError: 'PDF upload error',
    unknownError: 'unknown error',
  },

  defaultFunds: {
    salary: 'Salary',
    taxes: 'Taxes',
    profit: 'Profit',
  },

  // ─── User roles ───
  roles: {
    owner: 'Owner',
    admin: 'Administrator',
    accountant: 'Accountant',
    viewer: 'Viewer',
  },

  // ─── Month names ───
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],

  // ─── Filter periods ───
  periods: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    year: 'Year',
    twoYears: '2 years',
    all: 'All',
  },

  // ─── Weekdays ───
  weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],

  // ─── Currencies (labels) ───
  currencies: {
    KZT: 'Tenge (KZT)',
    RUB: 'Rubles (RUB)',
    USD: 'US Dollars (USD)',
    EUR: 'Euros (EUR)',
    CNY: 'Chinese Yuan (CNY)',
    AED: 'UAE Dirhams (AED)',
    GBP: 'British Pounds (GBP)',
    TRY: 'Turkish Lira (TRY)',
    UZS: 'Uzbek Sum (UZS)',
    KGS: 'Kyrgyz Som (KGS)',
    TJS: 'Tajik Somoni (TJS)',
    BYN: 'Belarusian Ruble (BYN)',
    UAH: 'Ukrainian Hryvnia (UAH)',
    GEL: 'Georgian Lari (GEL)',
    AZN: 'Azerbaijani Manat (AZN)',
    AMD: 'Armenian Dram (AMD)',
    MDL: 'Moldovan Leu (MDL)',
    TMT: 'Turkmen Manat (TMT)',
  },

  // ─── Transaction types ───
  txTypes: {
    income: 'Income',
    expense: 'Expense',
    transfer: 'Transfer',
    transferFund: 'Fund transfer',
    transferAccount: 'Account transfer',
    transferFundFull: 'Transfer between funds',
  },

  // ─── HomeScreen (App.js) ───
  home: {
    brand: 'INMONEX',
    subtitle: 'Control your money. Own your decisions',
    yourOrgs: 'Your organizations:',
    createOrg: 'Create organization',
    light: 'Light',
    dark: 'Dark',
    deleteOrgConfirm: 'Delete organization and all data?',
    loginTitle: 'Sign In',
    forgotPassword: 'Forgot password?',
    registerLink: 'Sign up',
    addOrg: 'Create organization',
    passwordHint: 'Account hints:',
    contactOwner: 'contact the organization owner',
    noUsersYet: 'No registered users yet.',
    createFirstOrg: 'Create your first organization to get started',
    loginHint: (login, len) => `Login: ${login} · Hint: password is ${len} characters`,
    cantRememberPassword: 'If you can\'t remember the password, contact the organization owner.',
  },

  // ─── LoginScreen (App.js) ───
  login: {
    loginPlaceholder: 'Login or email',
    passwordPlaceholder: 'Password',
    enter: 'Sign in',
    wrongCredentials: 'Invalid login or password',
    // Stage 11: second login screen for users with 2FA enabled
    twoFA: {
      title: 'Enter the code from your authenticator app',
      desc: 'Open Google Authenticator (or any TOTP app) and enter the 6-digit code. If you lost access to the app, use a backup code in the form XXXX-XXXX.',
      codePlaceholder: '6 digits or XXXX-XXXX',
      verifyBtn: 'Sign in',
      verifying: 'Verifying...',
      backToLogin: '← Back to login',
      expired: '2FA session expired. Please sign in again.',
      invalidCode: 'Invalid code',
      backupCodesLow: (n) => `Warning: only ${n} backup codes left. Generate new ones in security settings.`,
      usedBackup: 'You signed in with a backup code. One code has been used.',
    },
  },

  // ─── "Forgot password" modal ───
  forgotPwd: {
    title: 'Forgot password?',
    desc: 'Enter the email you used during registration. We will send a password reset link.',
    noEmailHint: 'If you have no email or no access to it — contact the company owner. They can reset your password manually.',
    emailPh: 'email@example.com',
    sendBtn: 'Send reset link',
    sentMsg: 'If this email is registered, we have sent a password reset link to it. Please check your inbox (including the Spam folder). The link is valid for 1 hour.',
    close: 'Close',
    failed: 'Failed to send the request. Please try again.',
  },

  // ─── Reset password page ───
  resetPwd: {
    logoSub: 'Money control. Freedom to decide',
    title: 'Create a new password',
    subtitle: 'Set a strong password for your account',
    newPwd: 'New password',
    newPwdPh: 'At least 8 characters',
    confirmPwd: 'Repeat new password',
    confirmPwdPh: 'Enter the password again',
    pwdTooShort: 'Password must be at least 8 characters',
    pwdMismatch: 'Passwords do not match',
    save: 'Save',
    saving: 'Saving...',
    successMsg: 'Password changed successfully! Redirecting to sign in...',
    noToken: 'Reset token not found. Please request a new link.',
    failed: 'Failed to change password. Please try again.',
  },

  // ─── Email change with OTP ───
  emailChange: {
    title: 'Change email',
    step1Desc: 'Enter the new email. We will send a confirmation code to it.',
    newEmailLabel: 'New email',
    newEmailPh: 'new@example.com',
    sendCode: 'Send code',
    step2Desc: 'We have sent a 6-digit code to the new email. Enter it below.',
    codeLabel: 'Code from email',
    codePh: '123456',
    confirm: 'Confirm',
    successMsg: 'Email changed successfully',
    rateLimit: (s) => `Wait ${s} sec.`,
    invalidCode: 'Invalid code',
    expired: 'Code expired, please request a new one',
    changeBtn: 'Change',
    sameEmail: 'New email is the same as current',
    sendError: 'Failed to send code',
  },

  // ─── Phase 10: Optimistic locking (App.js) ───
  sync: {
    conflictTitle: 'Data updated',
    conflictBody: 'Another user saved changes first. Applied the latest version — retry your action if needed.',
  },

  // ─── SetupWizard (App.js) ───
  setup: {
    title: 'INMONEX SETUP',
    stepOf: (step, total) => `Step ${step} of ${total}`,
    orgColorLabel: 'Organization color',
    orgColorTaken: (label) => `${label} (taken)`,
    welcomeText: 'Welcome to INMONEX! Fill in your company details and create an Owner account. This information is used for secure login and in reports.',
    companyName: 'Company or project name',
    companyPlaceholder: 'e.g. Acme Corp LLC',
    yourName: 'Your Name',
    namePlaceholder: 'John Smith',
    loginLabel: 'Login username',
    loginPlaceholder: 'admin',
    passwordLabel: 'Create a strong password',
    passwordMinLength: 'Minimum 8 characters',
    passwordsDoNotMatch: 'Passwords do not match',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat password',
    emailLabel: 'Email (for receipts and recovery)',
    emailPlaceholder: 'director@company.com',
    emailConfirmed: 'Confirmed',
    confirmEmail: 'Confirm',
    codePlaceholder: 'Code from email',
    wrongCode: 'Invalid code. Please try again.',
    codeSent: 'Code sent to your email',
    codeSendError: 'Failed to send code',
    codeSending: 'Sending...',
    verificationError: 'Code verification error',
    phoneLabel: 'Phone',
    phonePlaceholder: '15550001122',
    phoneMinDigits: 'Minimum 7 digits',
    phoneHint: 'Digits only, no spaces or brackets. Spacing is applied automatically.',
    accountLabel: (i) => `Account ${i}`,
    ibanLabel: 'Account IBAN',
    ibanPlaceholder: 'KZ00 0000 0000 0000 0000',
    balanceLabel: 'Current balance (optional)',
    balancePlaceholder: '0',
    balanceHint: 'If you enter a balance, it will be auto-distributed across funds',
    addAccount: 'Add account',
    fundsWhy: 'Why do I need funds?',
    fundsExplain: 'Funds automatically distribute revenue: Salaries, Taxes, Dividends.',
    fundsSkipHint: 'You can set them up now or remove and skip this step. INMONEX will suggest enabling them later.',
    percentHint: (pct) => `Percentages must total 100%. Currently: ${pct}%`,
    fundsPctError: 'Total fund percentages must equal exactly 100%. Adjust the values to continue.',
    duplicateAccountName: 'Account names must be unique',
    duplicateIban: 'Account IBANs must be unique',
    duplicateFundName: 'Fund names must be unique',
    fundDistribution: (amount) => `Initial balance: ${amount} — distribution across funds:`,
    fundNamePlaceholder: 'Fund name',
    addFund: 'Add fund',
    removeAllFunds: 'Remove all and skip',
    skipFunds: 'Skip funds',
    noFundsConfirm: 'You have not added any funds. Expenses will be debited directly from accounts. Continue?',
    ownerLabel: 'Owner:',
    emailLabelShort: 'Email:',
    accountsCount: 'Accounts:',
    fundsCount: 'Funds:',
    agreementText: 'I have read and accept the',
    tosLink: 'Terms of Service',
    privacyLink: 'Privacy Policy',
    consentLink: 'Consent to Personal Data Collection and Processing',
    finishSetup: 'Complete Registration',
    existingUserInfo: 'You are already logged in',
    // ─── Extended step descriptions ───
    step1Title: 'Your company and account',
    step1Desc: 'Let\'s get started! Enter your company details and create an administrator account. You can add other employees with different access levels later.',
    step2Title: 'Your bank accounts',
    step2Desc: 'An account represents your real bank account or cash register. For example, a business account, savings account, or petty cash. You receive payments from clients and pay expenses through them.',
    step2Examples: 'Business Account · Savings · Cash · Checking · PayPal',
    step2Hint: 'Add at least one account. You can add more later in settings.',
    step3Title: 'Funds \u2014 your financial safety net',
    step3Desc: 'Funds are virtual "piggy banks" within the system. Each incoming payment is automatically split among them by assigned percentages. This helps you set aside money for taxes, salaries, and profit in advance.',
    step3Example: 'Example: a client paid $10,000 \u2192 $5,000 goes to the "Salaries" fund, $2,000 to "Taxes", $3,000 to "Profit".',
    step3FlowTitle: 'How does it work?',
    flowClientPays: 'Client pays',
    flowYourAccount: 'Your account',
    flowFundsNotConfigured: 'Funds not configured',
    examplesTitle: 'Name examples:',
    accountExample: 'e.g. Business Account',
    step4Title: 'All set! Review your details',
    step4Desc: 'After creating the organization, you will enter the workspace where you can manage your full financial records.',
    step4Next1: 'Add income and expense categories',
    step4Next2: 'Create counterparties (clients and suppliers)',
    step4Next3: 'Start recording transactions and generating reports',
    step4WhatsNext: 'What\'s next?',
    switchTo: (name) => `Switch to: ${name}`,
  },

  // ─── Welcome Tour ───
  tour: {
    welcome: 'Welcome to INMONEX!',
    welcomeDesc: 'Let us show you the main tools in 30 seconds. You can skip the tour at any time.',
    step1Title: '\uD83D\uDCB3 Accounts and Funds',
    step1Desc: 'Here you can see all your accounts and funds. Click "+" on an account to quickly record an incoming payment. The money will be automatically distributed across your funds.',
    step2Title: '\uD83D\uDCDA Directories',
    step2Desc: 'This is where your counterparties (clients and suppliers) and income/expense categories live. The more accurately you set them up, the more useful your reports will be.',
    step3Title: '\u26A1 Operations',
    step3Desc: 'The main tool! Record income, expenses, and transfers. Each operation is automatically reflected in reports and balances.',
    step4Title: '\uD83D\uDCCA Reports',
    step4Desc: 'This is where P&L and Cash Flow statements are generated \u2014 two key financial reports. You will see how much you earn and where the money goes.',
    skipBtn: 'Skip',
    nextBtn: 'Next',
    prevBtn: 'Back',
    finishBtn: 'Get started!',
    stepOf: (step, total) => `${step} / ${total}`,
  },

  // ─── Sidebar / MainApp (App.js) ───
  sidebar: {
    tabs: {
      af: 'Home',
      data: 'Structure',
      ops: 'Operations',
      debts: 'Liabilities',
      tx: 'Transactions',
      reports: 'Reports',
      history: 'History',
      admin: 'Settings',
      myaccount: 'My account',
      multiorg: 'Summary',
      support: 'Support',
      referral: 'Referrals',
      subscription: 'Subscription',
    },
    cooperation: 'Contact Us',
    logout: 'Log out',
    backToOrgs: '\u2190 Orgs',
    menu: '\u2630 Menu',
    copyright: (year) => `\u00A9 ${year} INMONEX`,
    allRightsReserved: 'All rights reserved.',
    tosLink: 'Terms of Service',
    privacyLink: 'Privacy Policy',
    dataProcessingLink: 'Data Processing',
    addOrg: 'Add organization',
    allOrgs: 'All orgs',
    sync: {
      updated: 'Updated',
      justNow: 'just now',
      secondsAgo: 'N sec ago',
      minutesAgo: 'N min ago',
      hoursAgo: 'N h ago',
    },
  },

  // ─── Referral program (ReferralTab.js) ───
  referral: {
    title: 'Referral Program',
    subtitle: 'Invite friends and earn bonuses',
    myLink: 'My referral link',
    copyLink: 'Copy link',
    copied: 'Copied!',
    myCode: 'My code',
    balance: 'Bonus balance',
    balanceUnit: '₸',
    howItWorks: 'How it works',
    rule1: 'Share your link with friends',
    rule2: 'Friend signs up via your link and pays for 6+ months subscription',
    rule3: 'You get 10% of their payment as bonus balance',
    rule4: 'Bonuses are automatically applied to your next subscription payment',
    subUserNoticeTitle: 'Notice for organization employees',
    subUserNoticeText: 'If your INMONEX subscription is paid by your employer, you may transfer your accumulated bonuses to the owner of your organization. They will use them to pay for the subscription and save company funds. Under an internal arrangement with your employer, you may receive monetary compensation for the transferred bonuses — the amount, form and timing of such compensation are determined solely between you and your employer. INMONEX is not a party to any such agreement and does not participate in settlements between employee and employer.',
    invitedTitle: 'Invited friends',
    invitedEmpty: 'Nobody has signed up via your link yet',
    invitedTotal: (n) => `Total invited: ${n}`,
    invitedRewarded: (n) => `Bonuses received: ${n}`,
    colDate: 'Date',
    colLogin: 'Login',
    colEmail: 'Email',
    colStatus: 'Status',
    statusRewarded: '✓ Bonus granted',
    statusPending: 'Awaiting payment',
    historyTitle: 'Bonus history',
    historyEmpty: 'History is empty',
    typeReferralReward: 'Referral bonus',
    typeTransferIn: 'Transfer received',
    typeTransferOut: 'Transfer sent',
    typeSpend: 'Used for payment',
    typeAdminAdjust: 'Admin adjustment',
    transferButton: 'Transfer bonuses',
    transferTitle: 'Transfer bonuses',
    transferHint: 'Find recipient by login or email',
    transferSearch: 'Start typing login or email (min. 3 characters)',
    transferAmount: 'Transfer amount',
    transferNote: 'Note (optional)',
    transferSend: 'Send',
    transferCancel: 'Cancel',
    transferSuccess: (amount, login) => `Transferred ${amount} ₸ to ${login}`,
    transferSelectUser: 'Select recipient',
    transferNoResults: 'No users found',
    transferConfirm: (amount, login) => `Transfer ${amount} ₸ to ${login}?`,
    errorInvalidAmount: 'Enter a valid amount',
    errorNoRecipient: 'Select a recipient',
    errorTransferFailed: 'Transfer failed',
  },

  // ─── Contacts modal (shared.js) ───
  contacts: {
    title: 'Contact Us',
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
  },

  // ─── Confirmation component (shared.js) ───
  confirmDialog: {
    cancel: 'Cancel',
    confirm: 'Confirm',
  },

  // ─── Empty component (shared.js) ───
  empty: 'No data',

  // ─── NumInput component (shared.js) ───
  numInputPlaceholder: 'Enter amount',

  // ─── GlobalStats component (shared.js) ───
  globalStats: {
    totalOnAccounts: 'Total on accounts:',
  },

  // ─── Audit (shared.js) ───
  audit: {
    headers: 'Date\tType\tAmount\tAccount\tFund\tCategory\tComment\tUser',
  },

  // ─── Default categories (shared.js) ───
  defaultCategories: ['Advance', 'Salary', 'Zircon', 'Materials', 'Advertising', 'Logistics', 'Other'],

  // ─── OpsTab ───
  ops: {
    title: 'Operations',
    subtitle: 'Income, expenses, and transfers',
    income: 'Income',
    expense: 'Expense',
    transfer: 'Transfer',
    betweenAccounts: 'Between accounts',
    betweenFunds: 'Between funds',
    fromAccount: 'From account',
    toAccount: 'To account',
    account: 'Account',
    fromFund: 'From fund',
    toFund: 'To fund',
    fundDebit: 'Debit fund',
    expenseCategory: 'Expense category',
    amount: 'Amount',
    comment: 'Comment',
    incomeDescPlaceholder: 'Income description',
    expenseDescPlaceholder: 'Expense description',
    transferDescPlaceholder: 'Transfer description',
    submitIncome: 'Record income',
    submitExpense: 'Record expense',
    submitTransfer: 'Record transfer',
    undoLast: 'Undo last',
    undoConfirm: 'Undo the last operation?',
    undoSuccess: 'Operation undone',
    enterAmount: 'Enter amount',
    differentAccounts: 'Select different accounts',
    differentFunds: 'Select different funds',
    fundInsufficient: (fundName, balance, deficit) =>
      `Fund "${fundName}" has only ${balance}. Short by: ${deficit}`,
    accountInsufficient: (balance, deficit) =>
      `Account has only ${balance}. Short by: ${deficit}`,
    fundOnlyInsufficient: (balance, deficit) =>
      `Fund has only ${balance}. Short by: ${deficit}`,
    distributionTitle: 'Distribution across funds:',
    lastOps: 'Recent operations',
    noOpsYet: 'No operations yet',
    transferBetweenAccounts: 'Transfer between accounts',
    transferBetweenFunds: 'Transfer between funds',
    // Transfer to stash (hidden account)
    transferToHidden: 'To stash',
    toHiddenAccount: 'To stash (hidden account)',
    transferToHiddenComment: 'Transfer to stash',
    transferToHiddenHint: 'For the fund this is a debit (expense), the amount will not appear in income reports.',
    // Debt links
    closeDebtLabel: 'Close debt (Payment received)',
    simpleIncome: '-- Simple income --',
    payDebtLabel: 'Pay our debt / recurring payment',
    simpleExpense: '-- Simple expense --',
    debtPaymentComment: (desc) => `Payment for liability: ${desc}`,
    freeOperation: 'Free operation',
    debtPaymentIncoming: 'Liability payment (To us)',
    debtPaymentOutgoing: 'Liability payment (By us)',
    selectDebtReceivable: 'Select debt (Owed to us)',
    selectDebtPlaceholder: '-- Select debt --',
    remainLabel: (amount) => `Remaining: ${amount}`,
    contractorOptional: 'Counterparty (optional)',
    noContractor: 'No counterparty',
    addContractorQuick: 'Quick add counterparty',
    addCategoryQuick: 'Quick add category',
    // Categories
    expenseCategories: 'Expense categories',
    categoriesLinkedToFund: 'Categories are linked to the selected fund.',
    categoriesForFund: 'Categories for fund:',
    newCategory: 'New category',
    noCategories: 'No categories',
    deleteCategoryConfirm: 'Delete expense category?',
  },

  // ─── DebtsTab ───
  debts: {
    title: 'Liabilities',
    subtitle: 'Manage accounts receivable and payable',
    receivableTitle: 'Owed to Us (Receivables)',
    receivableSubtitle: 'Issued invoices, client debts',
    payableTitle: 'We Owe (Payables)',
    payableSubtitle: 'Supplier invoices, recurring payments',
    totalExpected: 'Total expected:',
    totalOwed: 'Total owed:',
    addDebtor: 'Add debtor',
    addOurDebt: 'Add our debt',
    noActiveDebts: 'No active debts',
    whoOwesUs: 'Who owes us?',
    weOweTo: 'Whom do we owe?',
    // View modal
    viewReceivable: 'Details: Owed to Us',
    viewPayable: 'Details: We Owe',
    contractor: 'Counterparty:',
    debtAmount: 'Debt amount:',
    date: 'Date:',
    commentLabel: 'Comment:',
    paymentBindings: 'Payment bindings:',
    accountCredit: 'Credit account: ',
    fundDebit: 'Debit fund: ',
    category: 'Category: ',
    paid: 'Paid',
    wePaid: 'We paid',
    paidAlert: 'Button works! Next step: we will attach a transaction modal here.',
    // Create modal
    contractorLabel: 'Counterparty*',
    newContractor: '+ New',
    selectPlaceholder: '-- Select --',
    accountCreditLabel: 'Credit account',
    incomeCategoryLabel: 'Income category',
    fundDebitLabel: 'Debit fund',
    expenseCategoryLabel: 'Expense category',
    noBinding: 'No binding',
    noCategoryOption: 'Uncategorized',
    amountLabel: 'Amount*',
    issueDateLabel: 'Issue date',
    expectedPaymentLabel: 'Expected payment',
    descriptionLabel: 'Description (What for?)',
    descriptionPlaceholder: 'e.g. For goods delivery',
    regularPayment: 'Recurring payment (Rent, etc.)',
    repeatLabel: 'Repeat:',
    frequencyMonth: 'Monthly',
    frequencyQuarter: 'Quarterly',
    frequencyYear: 'Yearly',
    saveDebt: 'Save liability',
    fillContractorAndAmount: 'Fill in the counterparty and amount',
    deleteConfirm: 'Are you sure you want to delete this entry?',
    daysPassed: (days) => `${days} days ago`,
    // Contractor from DebtsTab
    newContractorTitle: 'New counterparty',
    saveContractor: 'Save counterparty',
    enterContractorName: 'Enter counterparty name',
    selectContractorType: 'Select at least one counterparty type',
    clientLabel: 'Client (Pays us)',
    supplierLabel: 'Supplier (We pay)',
    namePlaceholder: 'John Smith LLC',
    legalNameLabel: 'Legal name',
    legalNamePlaceholder: 'Acme Corp LLC',
    binLabel: 'BIN / IIN',
    binPlaceholder: '123456789012',
    phoneLabel: 'Phone',
    phonePlaceholder: '+1 555 000 0000',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Address',
    addressPlaceholder: '123 Main Street, New York',
    availableAccounts: 'Available accounts (for Client)',
    accountsHint: 'If nothing is selected, the counterparty is available for all accounts.',
    incomeCategories: 'Income categories:',
    noIncomeCategories: 'No income categories',
    availableFunds: 'Available funds and categories (for Supplier)',
    fundsHint: 'If nothing is selected, the counterparty is available everywhere.',
    fundsLabel: 'Funds:',
    expenseCategories: 'Expense categories:',
    // Validations and errors
    enterLegalName: 'Enter legal name',
    enterBin: 'Enter BIN/IIN',
    selectContractorRole: 'Select counterparty role',
    duplicateContractor: (bin, name) => `A counterparty with this BIN (${bin}) or legal name already exists: ${name}`,
    selectAccountForDebt: 'Select an account to bind the liability',
    selectFundForPayable: 'Select a debit fund for the payable',
    correctAmountAlert: 'Enter a valid amount',
    selectDebtType: 'Select liability type (receivable / payable)',
    selectAccount: 'Select an account',
    specifyContractor: 'Specify or create a counterparty',
    paymentExceedsRemaining: (payAmt, remAmt) => `Payment amount (${payAmt}) exceeds remaining balance (${remAmt})`,
    noCategoryConfirm: 'No category selected. The transaction will be created without a category.\n\nContinue without a category?',
    archiveConfirm: 'Archive this liability?',
    // Counterparty creation labels
    nameForYouLabel: 'Name (for your reference)*',
    legalNameLabelReq: 'Legal name*',
    binLabelReq: 'BIN / IIN*',
    // Debt creation modal labels
    accountDebitLabel: 'Debit account*',
    accountCreditLabelReq: 'Credit account*',
    fundDebitLabelReq: 'Debit fund*',
    noActiveAccounts: 'No active accounts',
    noActiveFunds: 'No active funds',
    monthLabel: 'Month:',
    quarterMonthLabel: 'Quarter month:',
    quarterFirst: 'First',
    quarterSecond: 'Second',
    quarterThird: 'Third',
    dayOfMonthLabel: 'Day of month:',
    // Edit modal
    editDebtTitle: 'Edit liability',
    editContractorLabel: 'Counterparty',
    editContractorPlaceholder: 'Select counterparty',
    expectedPaymentDate: 'Expected payment date',
    editAccountCreditLabel: 'Credit account',
    editFundDebitLabel: 'Debit fund',
    // View modal (extended)
    viewWeOweUs: 'Owed to us',
    viewWeOwe: 'We owe',
    editBtnTitle: 'Edit',
    archiveBtnTitle: 'Archive',
    deleteBtnTitle: 'Delete',
    closeBtnTitle: 'Close',
    dateLabel: 'Date',
    totalAmount: 'Total amount',
    paidLabelUpper: 'Paid',
    remainingLabel: 'Remaining',
    paidPercent: (pct) => `${pct}% paid`,
    paymentHistory: (count) => `Payment history (${count})`,
    attachmentLabel: 'Attachment',
    downloadBtn: 'Download',
    fixPayment: 'Record payment',
    toWhichAccount: 'To which account?',
    fromWhichAccount: 'From which account?',
    categoryIncomeLabel: 'Income category',
    categoryExpenseLabel: 'Expense category',
    recommendSelect: 'Recommended',
    fromWhichFund: 'From which fund?',
    fullPayment: (amount) => `Full payment (${amount})`,
    partialPayment: 'Partial payment',
    enterPaymentAmount: 'Enter payment amount:',
    payBtn: 'Pay',
    maxPaymentHint: (amount) => `Maximum: ${amount} · A transaction will be created for the entered amount`,
    fullyPaid: 'Fully paid',
    allPaymentsFixed: 'All payments for this liability have been recorded',
    ofAmount: 'of',
    debtPaymentComment: (desc) => `Debt payment: ${desc}`,
    debtPaymentPartial: (paid, total) => ` (partial ${paid} of ${total})`,
    systemUser: 'System',
    // Invoice import from PDF
    uploadInvoice: 'Upload invoice',
    importFromPdf: 'Import from PDF',
    dragDropPdf: 'Drag and drop a PDF file or click to browse',
    invoiceOrPaymentOrder: 'Invoice or Payment order',
    recognizingDoc: 'Recognizing document...',
    onlyPdfSupported: 'Only PDF files are supported',
    pdfReadError: 'Could not read PDF. Make sure the file is not password-protected.',
    tryAgain: 'Try again',
    invoiceDocType: 'Invoice',
    paymentOrderDocType: 'Payment order',
    documentDocType: 'Document',
    debtTypeLabel: 'Liability type*',
    receivableShort: 'Receivable',
    payableShort: 'Payable',
    autoDetectFailed: 'Could not detect automatically. Check the IBAN in account settings.',
    typeUncertain: 'Type detected approximately — please verify “They owe us / We owe”.',
    foundLabel: 'Found',
    foundByBin: 'Found by BIN',
    contractorNotFound: 'Counterparty not found. A new one will be created:',
    nameFieldLabel: 'Name',
    binFieldLabel: 'BIN/IIN',
    orSelectExisting: 'Or select an existing one:',
    searchPlaceholder: '-- Search --',
    descriptionImport: 'Description',
    forWhatPlaceholder: 'What for?',
    createDebt: 'Create liability',
    invoiceDesc: (num) => `Invoice #${num}`,
    paymentOrderDesc: (num) => `Payment order #${num}`,
    categoryDynamic: (isPayable) => isPayable ? 'Expense category' : 'Income category',
    categoryHelpTooltip: 'A category helps track what the amount was received or spent for.\n\n💡 We recommend always specifying a category for accurate accounting.',
    auditDeleted: (desc) => `Liability deleted: ${desc}`,
    auditTypeReceivable: 'Receivable',
    auditTypePayable: 'Payable',
    auditTypeAmount: (type, amount) => `Type: ${type}. Amount: ${amount}`,
    auditNewDebt: (direction, amount) => `New liability: ${direction} ${amount}`,
    auditImportDebt: (direction, amount) => `Import from document: ${direction} ${amount}`,
    auditWeOwe: 'We owe',
    auditTheyOwe: 'They owe us',
  },

  // ─── TxTab ───
  tx: {
    title: 'Transactions',
    totalLabel: (total, shown) =>
      `Total: ${total}${shown !== total ? ` (showing: ${shown})` : ''}`,
    viewGrid: 'View: grid',
    viewTable: 'View: table',
    allTypes: 'All types',
    allAccountsFunds: 'All accounts and funds',
    fund: 'Fund:',
    archivedSuffix: ' (archived)',
    dateFrom: 'Date from',
    dateTo: 'Date to',
    toLatest: 'To latest',
    toLatestOps: 'To latest operations',
    toLatestOpsFull: 'Go to latest operations',
    futureMonthTitle: 'This month has not arrived yet',
    futureMonthHint: 'The grid is not displayed for future months. Switch to the current or a past month.',
    noTxInMonth: 'No transactions this month',
    noTxInMonthHint: 'Only days that have already passed in the selected month are shown.',
    noTransactions: 'No transactions',
    opsCount: (n) => `${n} ops`,
    opsCountShort: (n) => `${n} ops`,
    moreItems: (n) => `+ ${n} more`,
    // Table
    thDate: 'Date',
    thType: 'Type',
    thRoute: 'Route',
    thCategory: 'Category',
    thFund: 'Fund',
    thAmount: 'Amount',
    thComment: 'Comment',
    thContractor: 'Contractor',
    thWho: 'User',
    thActions: '\u2699',
    edited: '(edited)',
    deleteTxConfirm: (amount) => `Delete transaction for ${amount}?`,
    editLogComment: (oldAmt, newAmt, comment) =>
      `Was: ${oldAmt}, now: ${newAmt}. ${comment}`,
    // Day
    dayOps: 'Operations:',
    dayIncome: 'Income',
    dayExpense: 'Expenses',
    dayTransfers: 'Transfers',
  },

  // ─── ReportsTab ───
  reports: {
    title: 'Reports',
    charts: 'Charts',
    opu: 'P&L',
    dds: 'Cash Flow',
    allReport: 'Full report',
    onlyOpu: 'P&L only',
    onlyDds: 'Cash Flow only',
    allAccounts: 'All accounts',
    allFunds: 'All funds',
    // Stat cards
    incomeLabel: 'Income',
    expenseLabel: 'Expenses',
    onAccounts: 'On accounts',
    inFunds: 'In funds',
    // Charts
    chartIncomeExpense: 'Income / Expense',
    chartExpenseByFunds: 'Expense breakdown by funds',
    chartIncomeByAccounts: 'Income by accounts',
    chartCumulativeResult: 'Cumulative period result',
    // P&L table
    opuTitle: 'P&L',
    opuDescription: 'The P&L statement shows business results: how much was earned and how much was spent. Internal transfers between accounts and funds are not included.',
    opuPeriodResult: 'Period result',
    opuIncome: 'INCOME',
    opuExpense: 'EXPENSES',
    opuProfit: 'PROFIT / LOSS',
    opuArticle: 'Line item',
    opuTotal: 'Total',
    noFund: 'No fund',
    // Cash Flow table
    ddsTitle: 'Cash Flow',
    ddsDescription: 'The Cash Flow statement shows the actual movement of money across accounts: opening balance, inflows, outflows, internal transfers between accounts, and closing balance. This is different from the P&L statement.',
    ddsOpening: 'Opening balance',
    ddsInflow: 'Cash inflow',
    ddsOutflow: 'Cash outflow',
    ddsInternal: 'Internal transfers between accounts',
    ddsNetFlow: 'NET CASH FLOW',
    ddsClosing: 'Closing balance',
    ddsNetFlowLabel: 'Net flow',
    ddsFilterHint: 'When filtering by a specific account, internal transfers count as money movement within the selected account. When filtering by "All accounts", such transfers do not affect net flow.',
    // Export
    exportSummary: 'SUMMARY',
    exportIncomes: 'Income',
    exportExpenses: 'Expenses',
    exportTransfers: 'Transfers',
    exportReportName: { all: 'Full', opu: 'P&L', dds: 'Cash Flow' },
    // Export - additional types
    exportCategoriesName: 'Categories',
    exportContractorsName: 'Counterparties',
    exportTimelinessName: 'Timeliness',
    exportLiquidityName: 'Liquidity',
    // Report tabs
    categoriesTab: 'Categories',
    contractorsTab: 'Counterparties',
    timelinessTab: 'Timeliness',
    liquidityTab: 'Liquidity',
    onlyCategoriesExport: 'Categories only',
    onlyContractorsExport: 'Counterparties only',
    onlyTimelinessExport: 'Timeliness only',
    onlyLiquidityExport: 'Liquidity only',
    // Additional charts
    obligationsCalendar: 'Obligations Calendar (Payment Forecast)',
    noUpcomingPayments: 'No upcoming debt payments',
    weWillReceive: 'We will receive (+)',
    weWillPay: 'We will pay (-)',
    // Categories view
    incomeByCategories: 'Income analysis by categories',
    expenseByCategories: 'Expense analysis by categories',
    categoryColumn: 'Category',
    amountColumn: 'Amount',
    // Counterparties view
    contractorActivity: 'Counterparty activity',
    contractorColumn: 'Counterparty',
    revenueColumn: 'Revenue (+)',
    costsColumn: 'Costs (-)',
    balanceColumn: 'Balance',
    debtBalanceColumn: 'Debt / Balance',
    contractorFilterAll: 'All',
    contractorFilterClients: 'Clients',
    contractorFilterSuppliers: 'Suppliers',
    // Timeliness view
    paymentDiscipline: 'Payment discipline (Debtors)',
    paymentDisciplineHint: 'Average delay or lead relative to the payment schedule for completed deals. Lifetime statistics.',
    clientsOnlyNote: 'This report shows clients only: the metric is calculated for those who pay you. It is not applicable to suppliers (those whom you pay).',
    clientColumn: 'Client',
    dealsColumn: 'Deals',
    punctualityColumn: 'Punctuality (%)',
    avgDelayColumn: 'Avg. delay',
    onTime: 'On time',
    noPaymentHistory: 'No payment history for analysis',
    daysShort: 'days',
    // Liquidity view
    liquidityPosition: 'Liquidity position',
    cashOnAccounts: 'Cash on accounts:',
    receivablesLabel: 'Receivables:',
    payablesLabel: 'Payables:',
    totalLiquidity: 'Total liquidity:',
    liquidityHint: 'This report shows your financial safety margin by combining actual cash and upcoming payments.',
    capitalStructure: 'Capital structure',
    cashName: 'Cash',
    receivablesName: 'Receivables',
    resultChartName: 'Result',
    resetFilters: 'Reset',
    dateFrom: 'Date from',
    dateTo: 'Date to',
    noDataAvailable: 'No income data for the period',
  },

  // ─── AdminTab ───
  admin: {
    title: 'Settings and Profile',
    subtitle: 'Manage company, staff, and system',
    profileTab: 'Company Profile',
    usersTab: 'Staff',
    systemTab: 'System',
    securityTab: 'Security',
    // Security (Stage 11): UI strings for SessionsCard and SecurityEventsCard
    security: {
      sectionSubtitle: 'Manage sessions and view account action history',
      devicesTitle: 'My devices',
      refresh: 'Refresh',
      loading: 'Loading...',
      noSessions: 'No active sessions.',
      currentBadge: 'Current',
      thisDevice: 'This device',
      terminate: 'End',
      logoutAll: 'Sign out from all devices',
      logoutAllInProgress: 'Signing out...',
      logoutAllHint: 'After this, you will need to sign in again on each device.',
      confirmRevoke: 'End this session? If this is your current device, you will need to sign in again.',
      confirmLogoutAll: 'End ALL sessions? You will be signed out from this device immediately.',
      errorLoad: 'Failed to load',
      errorPrefix: 'Error: ',
      unknownDevice: 'Unknown device',
      ipLabel: 'IP',
      loggedInAt: 'signed in',
      // Stage 11: two-factor authentication UI block (TwoFactorCard + setup wizard)
      twoFactor: {
        title: 'Two-factor authentication',
        subtitle: 'An extra code at sign-in — even if someone learns your password, they cannot sign in without your authenticator app.',
        statusEnabled: 'Enabled',
        statusDisabled: 'Not configured',
        enabledSince: (date) => `Enabled since ${date}`,
        backupRemaining: (n) => `Backup codes remaining: ${n}`,
        backupLowWarning: 'Few backup codes left. We recommend generating new ones.',
        enableBtn: 'Enable 2FA',
        disableBtn: 'Disable 2FA',
        loading: 'Loading...',
        // Setup wizard (3 steps)
        setupTitle: 'Set up 2FA',
        step1Title: 'Step 1 of 3 — Scan the QR code',
        step1Desc: 'Install a 2FA authenticator app (Google Authenticator, Microsoft Authenticator, Authy, or 1Password) and scan the QR code.',
        manualSecretLabel: "Can't scan?",
        manualSecretHint: 'Enter this secret manually in the app:',
        copyBtn: 'Copy',
        copied: 'Copied',
        nextBtn: 'Next',
        cancelBtn: 'Cancel',
        backBtn: '← Back',
        step2Title: 'Step 2 of 3 — Confirm',
        step2Desc: 'Enter the 6-digit code currently shown by your authenticator app.',
        codeInputPh: '6 digits',
        verifyBtn: 'Verify',
        verifying: 'Verifying...',
        codeInvalid: 'Invalid code. Check the time on your device.',
        step3Title: 'Step 3 of 3 — Backup codes',
        step3Desc: 'Save these 10 one-time codes in a safe place. They will help you sign in if you lose access to your authenticator app. Each code can be used only ONCE.',
        step3Notice: '⚠️ These codes are shown only now. They cannot be retrieved again — only hashes are stored on the server.',
        copyAllBtn: 'Copy all',
        downloadBtn: 'Download .txt',
        confirmSavedLabel: 'I have saved the backup codes in a safe place',
        finishBtn: 'Done',
        setupSuccess: '2FA enabled successfully',
        // Disable flow
        disableTitle: 'Disable 2FA',
        disableDesc: 'Enter your current password and a 2FA code (or one of the backup codes) to disable two-factor authentication.',
        passwordPh: 'Current password',
        codeOrBackupPh: '6 digits or XXXX-XXXX',
        disableConfirmBtn: 'Disable',
        disablingBtn: 'Disabling...',
        disableSuccess: '2FA disabled',
        confirmDisable: 'Are you sure you want to disable 2FA? This will reduce your account security.',
        // Errors
        errorPrefix: 'Error: ',
        errorLoad: 'Failed to load 2FA status',
        errorInit: 'Failed to start setup',
        currentPasswordInvalid: 'Current password is incorrect',
      },
      events: {
        title: 'Security activity',
        empty: 'No events recently.',
        showingLast: 'Showing last 50 events',
        criticalWarning: 'If this was not you — change your password immediately and review devices above.',
        warningBanner: 'Suspicious events detected — review the list below',
        actions: {
          change_password: 'Password change',
          delete_account: 'Account deletion',
          delete_org: 'Organization deletion',
        },
        types: {
          password_changed: 'Password changed',
          password_reset: 'Password reset via email',
          email_changed: 'Email changed',
          '2fa_enabled': 'Two-factor authentication enabled',
          '2fa_disabled': 'Two-factor authentication disabled',
          org_deleted: 'Organization deleted',
          account_deleted: 'Account deleted',
          reuse_detected: 'Stolen session token detected',
          logout_all: 'All sessions ended',
          session_revoked: 'Session ended',
          admin_revoke: 'Session revoked by administrator',
          sensitive_action_locked: 'Suspicious password attempts',
        },
      },
    },
    // Profile
    companyInfo: 'Company information',
    ownerOnly: 'Only the Owner can modify this data.',
    baseCurrency: 'Base accounting currency',
    currencyHint: 'This currency will be displayed in all reports, operations, and exported files.',
    companyName: 'Company name (Displayed in reports)',
    companyPlaceholder: 'Acme Corp LLC',
    emailLabel: 'Email for notifications',
    emailConfirmed: 'Confirmed',
    confirmEmail: 'Confirm email',
    emailPlaceholder: 'director@company.com',
    codePlaceholder: 'Code from email',
    wrongCode: 'Invalid code. Please try again.',
    codeSent: 'Code sent to your email',
    codeSendError: 'Failed to send code',
    codeSending: 'Sending...',
    verificationError: 'Code verification error',
    phoneLabel: 'Contact phone',
    phonePlaceholder: '15550001122',
    phoneHint: 'Digits only, minimum 7. Spacing is applied automatically.',
    phoneMinDigits: 'Minimum 7 digits',
    phoneSaved: 'Phone saved',
    phoneSaveErr: 'Failed to save',
    phoneNotSet: 'Not set',
    myPhoneLabel: 'My phone',
    filterUsersPlaceholder: 'Search: name, login, email, or phone digits',
    detailsLabel: 'Bank details and additional information',
    detailsPlaceholder: 'BIN/IIN, legal address...',
    // My account
    myAccount: 'My Account',
    // Stage 2: section headers for MyAccountTab
    myAccountSubtitle: 'Manage personal data and security',
    personalDataSection: 'Personal data',
    securitySection: 'Security',
    currentPasswordPh: 'Current password',
    currentPasswordRequired: 'Enter your current password',
    roleLabel: 'Role:',
    changePassword: 'Change my password',
    newPasswordPlaceholder: 'New password',
    passwordHint: 'Leave blank if you do not want to change the current password.',
    saveAll: 'Save all changes',
    saveSuccess: 'Profile settings saved successfully!',
    // Staff
    employees: (count) => `Employees (${count})`,
    viewOnlyHint: 'You are in view-only mode. Only the Owner can manage access.',
    addEmployee: 'Add employee',
    dupLogin: 'A user with this login already exists in this organization',
    addExisting: 'Add existing user',
    addNew: 'Create new',
    searchUserPlaceholder: 'User login or email',
    searchBtn: 'Search',
    searching: 'Searching...',
    userFound: 'User found',
    userNotFound: 'User not found. Check login or email.',
    userAlreadyInOrg: 'This user is already in the organization',
    addToOrg: 'Add to organization',
    addToOrgHint: 'The user will get access to this organization with the selected role',
    userOrgsCount: n => `In ${n} org(s)`,
    suggestedRole: 'Suggested role (from another org)',
    accessRevoked: 'Access to organization revoked',
    accessRevokedHint: 'Your access to this organization has been revoked. Please select another organization.',
    employeeName: 'Employee name',
    employeeNamePlaceholder: 'John Smith',
    roleSelectLabel: 'Role',
    loginLabel: 'Login username',
    loginPlaceholder: 'john_acc',
    initialPassword: 'Initial password',
    initialPasswordPlaceholder: 'pass123',
    deleteEmployeeConfirm: 'Are you sure you want to remove this employee from the organization?',
    permOverrides: 'Individual permissions (override role)',
    permHint: '* Highlighted permissions are set manually. Others are inherited from the role.',
    orgsLabel: 'Organizations:',
    loginDisplayLabel: 'Login:',
    passwordDisplayLabel: 'Password:',
    showPassword: 'Show',
    resetPassword: 'Reset password',
    resetPasswordPlaceholder: 'New password',
    passwordResetSuccess: 'Password changed',
    passwordNotSet: 'Set',
    // Stage 11: change login (owner only)
    changeLogin: 'Change login',
    changeLoginTitle: 'Change employee login',
    changeLoginDesc: 'New login must start with a Latin letter and be 3-32 characters: a-z, 0-9, dot, underscore, hyphen.',
    changeLoginPlaceholder: 'New login',
    changeLoginSubmit: 'Change',
    changeLoginSuccess: 'Login changed',
    loginInvalidFormat: 'Invalid format: only Latin, digits, ".", "_", "-", 3-32 characters, starts with a letter.',
    loginTaken: 'This login is already taken by another user',
    loginUnchanged: 'Login is unchanged',
    // Stage 11: warning when admin resets password
    resetPasswordTitle: 'Reset employee access',
    resetPasswordWarning: 'Will be reset: password, two-factor authentication (if enabled), all active sessions. The employee will be immediately signed out from all devices.\n\nIf this is a transfer of the account to a new person — DO NOT FORGET to also change the employee\'s email! Otherwise the previous employee can use "Forgot password" to get a new reset link.\n\nContinue?',
    resetPasswordSimpleHint: 'New password — minimum 8 characters.',
    // Stage 2 v2: change employee email by owner
    changeEmail: 'Change email',
    changeEmailTitle: 'Employee email',
    changeEmailDesc: 'The employee\'s email is used for self-service password recovery. When terminating the employee, be sure to clear or replace the email — otherwise the former employee can use "Forgot password" to regain access.',
    changeEmailPlaceholder: 'employee@example.com',
    changeEmailSubmit: 'Save',
    changeEmailRemoveBtn: 'Remove email',
    changeEmailSuccess: 'Email updated',
    changeEmailRemoveSuccess: 'Email removed',
    emailInvalid: 'Invalid email format',
    emailTaken: 'This email is already used by another user',
    emailNotSet: 'not set',
    emailVerifiedYes: 'Verified',
    emailVerifiedNo: 'Not verified',
    orgAssignment: 'Organizations',
    orgAssignmentHint: 'Check the organizations this user has access to',
    orgRoleIn: role => `Role: ${role}`,
    // System
    backupTitle: 'Database backup',
    backupHint: 'Regularly download a backup to your computer. If you accidentally delete data in the browser or log in from another device, you can upload this file and restore all your accounting records.',
    saveBackup: 'Save backup (.json)',
    restoreBackup: 'Restore backup',
    restoreOwnerOnly: 'Only the Owner can restore the database!',
    restoreConfirm: 'WARNING! Current data will be deleted and replaced with data from the file. Continue?',
    restoreSuccess: 'Database restored successfully!',
    restoreFormatError: 'Invalid backup file format.',
    restoreReadError: 'An error occurred while reading the file.',
    auditTitle: 'System audit log',
    auditHint: 'The audit log records all user actions (who performed which operation, when, and what was added or deleted). You can export it for review.',
    downloadAudit: 'Download Audit Log (.tsv)',
    statsTitle: 'Usage statistics',
    statsUsers: 'Users',
    statsAccounts: 'Active accounts',
    statsFunds: 'Active funds',
    statsTx: 'Transactions',
    langLabel: 'Interface language',
  },

  // ─── DataTab ───
  data: {
    accountsTab: 'Accounts and Funds',
    contractorsTab: 'Counterparties',
    categoriesTab: 'Categories',
    archiveTab: 'Archive',

    // --- CategoriesTab ---
    linkedToAccount: (name) => `Linked to account: ${name}`,
    linkedToFund: (name) => `Linked to fund: ${name}`,
    deletedLink: 'Deleted',
    notLinkedToAccount: 'Not linked to an account',
    notLinkedToFund: 'Not linked to a fund',
    baseSystemCategory: 'Base system category',
    noTurnover: 'No turnover',
    archiveCategoryConfirm: (name) => `Archive category "${name}"?`,
    archiveCategoryExtra: 'The category will disappear from dropdown lists when creating new operations, but all past operations with it will remain unchanged in financial reports. You can restore it from the Unified Archive.',
    editCategory: 'Edit category',
    newCategory: 'New category',
    incomeType: 'Income',
    expenseType: 'Expense',
    categoryNamePlaceholder: 'Category name',
    linkToAccountLabel: 'Link to Account (optional)',
    noAccountLink: 'Not linked to an account',
    linkToFundLabel: 'Link to Fund (optional)',
    noFundLink: 'Not linked to a fund',
    linkedContractors: 'Linked counterparties',
    noneSelected: 'None selected',
    searchByNameOrBin: 'Search by name or BIN...',
    contractorsNotFound: 'No counterparties found',
    binIinLabel: 'BIN/IIN: ',
    categoriesTitle: 'Category Directory',
    categoriesSubtitle: (count) => `Income and expense analytics (${count})`,
    addCategory: 'Add category',
    searchCategory: 'Search category...',
    sortRecent: 'Most recent first',
    sortVolDesc: 'By turnover (descending)',
    sortVolAsc: 'By turnover (ascending)',
    sortNameAsc: 'Alphabetical (A-Z)',
    sortNameDesc: 'Alphabetical (Z-A)',
    categoriesNotFound: 'No categories found',
    categoriesForAccounts: 'Categories for Accounts',
    categoriesForFunds: 'Categories for Funds',

    // --- CategoryModal ---
    enterName: 'Enter name',
    archiveCategoryMinOne: 'Cannot archive: at least one active category must remain.',
    archiveCategoryQuestion: 'Archive this category?',
    noLinkGeneral: 'No link (General)',
    linkToAccountEdit: 'Link to account (optional)',
    linkToFundEdit: 'Link to fund (optional)',
    noLinkGeneralCategory: 'No link (General category)',
    categoryNameLabel: 'Category name',
    notFoundShort: 'Not found',
    saveChanges: 'Save changes',
    linkSection: 'Linking',
    accountPrefix: 'Account: ',
    fundPrefix: 'Fund: ',
    categoryOperations: 'Operations by category',
    periodAll: 'All time',
    periodMonth: 'Last month',
    period6months: 'Last 6 months',
    periodYear: 'Last year',
    periodCustom: 'Custom',
    totalTurnover: 'Total turnover for the selected period:',
    operationsList: 'Operations list (Clickable)',
    noOpsInPeriod: 'No operations in this period',

    // --- TxEditModal ---
    moveToArchiveTitle: 'Move to archive?',
    moveToArchiveDesc: 'Account and fund balances will be recalculated: the operation amount will be reversed. The operation can be restored from the archive.',
    yesToArchive: 'Yes, archive',
    viewOperation: 'View operation',
    txEditHelp: 'You can change the category, counterparty, and comment. The amount is not editable to ensure accounting accuracy. To cancel an operation, move it to the archive.',
    txTypeIncome: 'Income',
    txTypeExpense: 'Expense',
    txTypeTransfer: 'Transfer',
    contractor: 'Counterparty',
    noContractor: 'No counterparty',
    categorySelectLabel: 'Category',
    noCategory: 'Uncategorized',
    commentLabel: 'Comment',
    descPlaceholder: 'Description...',

    // --- AccountsFundsTab ---
    enterAccountName: 'Enter account name',
    enterFundName: 'Enter fund name',
    newAccount: 'New account',
    accountNameLabel: 'Account name',
    accountNamePlaceholder: 'Checking, Cash...',
    makePrimary: 'Make primary',
    hiddenAccountLabel: 'Hidden account (Personal)',
    whoCanSee: 'Who can see this account?',
    visibleOnlyMe: 'Only me (owner)',
    visibleMeAccountant: 'Me and accountant',
    visibleAdmins: 'Me, accountant and administrator',
    visibleAll: 'Entire team',
    ibanLabel: 'IBAN (Required)',
    ibanRequired: 'Enter the account IBAN. This is required for invoice import.',
    colorLabel: 'Label color',
    saveAccount: 'Save account',
    newFund: 'New fund',
    fundNameLabel: 'Fund name',
    fundNamePlaceholder: 'Payroll, Reserve...',
    fundPercentLabel: 'Allocation percentage (%)',
    fundTargetLabel: 'Target amount',
    saveFund: 'Save fund',
    yourAccounts: 'Your accounts',
    sortLabel: 'Sort...',
    sortAlpha: 'Alphabetical (A-Z)',
    sortBalDesc: 'By balance (Descending)',
    sortBalAsc: 'By balance (Ascending)',
    sortPctDesc: 'By percentage (Descending)',
    addBtn: 'Add',
    noActiveAccounts: 'No active accounts',
    primaryBadge: 'Primary',
    fundsTitle: 'Funds (Savings)',
    noActiveFunds: 'No active funds',

    // --- AccountModal ---
    primaryAccount: 'Primary account',
    hiddenAccountEdit: 'Hidden account (Personal)',
    visibilityPrefix: 'Visibility: ',
    balancePrefix: (bal) => `Balance: ${bal}`,
    historyBtn: 'History',
    ibanShort: 'IBAN:',
    incomeCategories: 'Income categories',
    noFreeCategories: 'No available categories',
    noLinkedCategories: 'No linked categories',
    removeLink: 'Remove',
    clientsTitle: 'Clients',
    allClientsLinked: 'All clients are linked',
    noLinkedClients: 'No linked clients',
    enterIban: 'Enter the IBAN. This is required for automatic account recognition.',

    // --- FundModal ---
    fundPercentExceeds: 'Total fund percentages will exceed 100%',
    inFundPrefix: (bal) => `In fund: ${bal}`,
    expenseCategories: 'Expense categories',
    noLinkedExpenseCategories: 'No linked categories',
    noFreeExpenseCategories: 'No available categories',

    // --- QuickHiddenAdjustModal ---
    topUpStash: 'Top up stash',
    withdrawStash: 'Withdraw from stash',
    hiddenAccountName: 'Hidden account',
    stashNow: 'Currently in stash',
    topUpAmount: 'Top-up amount',
    withdrawAmount: 'Withdrawal amount',
    commentForSelf: 'Comment (for yourself)',
    topUpPlaceholder: 'Saved from bonus...',
    withdrawPlaceholder: 'Spent on a gift...',
    enterAmount: 'Enter amount',
    insufficientStash: (balance) => `Insufficient funds. In stash: ${balance}`,
    topUpComment: 'Stash top-up',
    withdrawComment: 'Stash withdrawal',
    topUpBtn: 'Top up',
    withdrawBtn: 'Withdraw',

    // --- Tooltips (title attributes) ---
    editTooltip: 'Edit',
    toArchiveTooltip: 'Archive',
    closeTooltip: 'Close',
    hiddenAccountTooltip: 'Hidden account',
    quickIncomeTooltip: 'Quick income',
    quickExpenseTooltip: 'Quick expense',
    accountHelpFallback: 'An account is your bank account or cash register. Money flows in and expenses are made from it.',
    fundHelpFallback: 'A fund is a virtual savings jar. Money is allocated to funds according to percentages.',
    ibanHelpTooltip: 'IBAN is required for automatic account recognition during PDF import.',
  },

  // ─── ContractorsTab ───
  contractors: {
    title: 'Counterparty Directory',
    subtitle: (count) => `Client and supplier database (${count})`,
    addContractor: 'Add counterparty',
    searchPlaceholder: 'Search by name or BIN...',
    sortRecent: 'Most recent first',
    sortVolDesc: 'By volume (descending)',
    sortVolAsc: 'By volume (ascending)',
    sortNameAsc: 'Alphabetical (A-Z)',
    sortNameDesc: 'Alphabetical (Z-A)',
    notFound: 'No counterparties found',
    clientsTitle: 'Clients (Pay us)',
    suppliersTitle: 'Suppliers (We pay)',
    emptyList: 'List is empty',
    partner: '\u2194 Partner',
    oweUs: (amount) => `Owed to us: ${amount}`,
    weOwe: (amount) => `We owe: ${amount}`,
    zeroBalance: 'Settled',
    noExtraData: 'No additional data',
    legalNotSpecified: 'Legal entity not specified',
    client: 'Client',
    supplier: 'Supplier',
    // Modal
    editTitle: 'Edit',
    newTitle: 'New counterparty',
    roleRequired: 'Counterparty role (Required)',
    clientRole: 'Client (Pays us)',
    supplierRole: 'Supplier (We pay)',
    nameLabel: 'Name (for your reference)*',
    namePlaceholder: 'e.g. Smith & Co LLC',
    legalNameLabel: 'Legal name',
    legalNamePlaceholder: 'Acme Corp LLC',
    binLabel: 'BIN / IIN',
    binPlaceholder: '123456789012',
    phoneLabel: 'Phone',
    phonePlaceholder: '+1 555 000 0000',
    emailLabel: 'Email',
    emailPlaceholder: 'mail@example.com',
    addressLabel: 'Physical address',
    addressPlaceholder: '123 Main Street, New York',
    saveContractor: 'Save counterparty',
    saveChanges: 'Save changes',
    enterName: 'Enter counterparty name',
    selectType: 'Select at least one counterparty type (Client or Supplier)',
    archiveConfirm: 'Archive this counterparty?',
    enterNameShort: 'Enter name',
    // Financial summary
    financeSummary: 'Financial summary',
    periodAll: 'All time',
    periodMonth: 'This month',
    period6months: 'Last 6 months',
    periodYear: 'This year',
    periodCustom: 'Custom',
    fromClient: 'From client (Pays us)',
    toSupplier: 'To supplier (We pay)',
    alreadyPaidUs: 'Already paid us:',
    stillOwesUs: 'Still owes us:',
    alreadyPaidThem: 'Already paid by us:',
    ourDebt: 'Our debt (Credit):',
    oweNow: 'Currently owed:',
    totalPaid: 'Total paid:',
    opsForPeriod: 'Operations for period',
    noOpsInPeriod: 'No operations in this period',
    ourDebtLabel: 'Our debt',
    clientDebtLabel: 'Client debt',
    paidStatus: '\u2713 Paid',
    awaitingPayment: 'Awaiting payment',
    linkedCategories: 'Linked categories',
    // Bindings
    availableAccounts: 'Available accounts (for Client)',
    accountsHint: 'If nothing is selected, the counterparty is available for all accounts.',
    incomeCategories: 'Income categories:',
    noIncomeCategories: 'No income categories',
    availableFundsAndCats: 'Available funds and categories (for Supplier)',
    fundsHint: 'If nothing is selected, the counterparty is available everywhere.',
    fundsLabel: 'Funds:',
    expenseCategories: 'Expense categories:',
    editTitleFull: 'Editing',
    archiveConfirmDetailed: (name) => `Archive counterparty "${name}"?`,
    archiveHint: 'The counterparty will disappear from dropdown lists when creating new operations, but all past operations will remain unchanged. You can restore it from the Unified Archive.',
    accountsLabelClient: 'Available accounts (Client)',
    fundsAndCatsSupplier: 'Funds and categories (Supplier)',
    titleLabel: 'Name (title)*',
    legalNameRequired: 'Legal name*',
    binRequired: 'BIN / IIN*',
    enterLegalNameError: 'Enter the counterparty legal name',
    enterBinError: 'Enter the counterparty BIN/IIN',
    enterLegalNameShort: 'Enter legal name',
    enterBinShort: 'Enter BIN/IIN',
    duplicateError: (bin, name) => `A counterparty with this BIN (${bin}) or legal name already exists: ${name}`,
    selectTypeError: 'Select at least one role (Client or Supplier)',
    phoneUpperLabel: 'PHONE',
    emailUpperLabel: 'EMAIL',
    addressUpperLabel: 'ADDRESS',
    clientPaysUs: 'Client (Pays us)',
    supplierWePay: 'Supplier (We pay)',
    debtToUsLabel: 'Owed to us:',
    ourDebtLabel2: 'Our debt:',
    currentBindings: 'Current bindings',
    accountsAndIncomeCategories: 'Accounts and Income categories',
    fundsAndExpenseCategories: 'Funds and Expense categories',
    noBindings: 'No bindings',
    lastOperations: 'Recent operations',
    historyEmpty: 'History is empty',
    noDescriptionShort: 'No description',
    paidStatusUpper: 'PAID',
    awaitingStatusUpper: 'PENDING',
    clientTypeLabel: 'Client',
    supplierTypeLabel: 'Supplier',
  },

  // ─── HistoryModal ───
  history: {
    title: (name) => `History: ${name}`,
    noOps: 'No operations',
    pageTitle: 'Activity Log',
    pageSubtitle: 'System activity journal and change audit',
    exportTsv: 'Export .TSV',
    allActions: 'All actions',
    accountantsTab: 'Accountants',
    adminsTab: 'Admins',
    searchPlaceholder: 'Search by name or action...',
    eventDetails: 'Event details',
    dateTime: 'Date and time',
    executor: 'Performed by',
    action: 'Action',
    stateChanges: 'STATE CHANGES',
    wasBefore: 'BEFORE',
    becameAfter: 'AFTER',
    understood: 'Got it',
    adminRole: 'Admin',
    actionTypes: {
      all: 'All actions',
      income: 'Income',
      expense: 'Expenses',
      transfer: 'Transfers',
      accounts: 'Accounts/Funds',
      categories: 'Categories',
      users: 'Employees',
      debts: 'Liabilities',
      contractors: 'Counterparties',
      system: 'System',
    },
    colDateTime: 'Date / Time',
    colUser: 'User',
    colType: 'Type',
    colAction: 'Action',
    colChanges: 'Changes',
    colDetails: 'Details',
    filterActionType: 'Action type',
    filterUser: 'User',
    filterDateFrom: 'Date from',
    filterDateTo: 'Date to',
    filterAll: 'All',
    resetFilters: 'Reset filters',
  },

  // ─── Archive (ArchiveTab.js) ───
  archive: {
    tabAccounts: 'Accounts',
    tabFunds: 'Funds',
    tabContractors: 'Counterparties',
    tabCategories: 'Categories',
    tabTx: 'Transactions',
    deleteForever: 'Delete permanently?',
    deleteForeverDesc: 'This action is irreversible. The record will be permanently deleted. The financial impact of transactions was already reversed upon archiving.',
    restoreFromArchive: 'Restore from archive?',
    restoreTxDesc: (amount, verb) => `The amount of ${amount} will be accounted for in current balances and reports again. Funds will be ${verb} the corresponding accounts/funds.`,
    restoreTxCredited: 'credited to',
    restoreTxDebited: 'debited from',
    restoreAccFundDesc: (name) => `Account/fund "${name}" will become available for selection in transactions again and will be displayed on the main screen (if not hidden). Balance will remain unchanged.`,
    restoreContractorDesc: (name) => `Counterparty "${name}" will become available for selection in new transactions again.`,
    restoreCategoryDesc: (name) => `Category "${name}" will become available for fund distribution again.`,
    noArchived: (entity) => `No archived ${entity}`,
    emptyAccounts: 'accounts',
    emptyFunds: 'funds',
    emptyContractors: 'counterparties',
    emptyCategories: 'categories',
    emptyTx: 'transactions',
    balance: 'Balance:',
    restore: 'Restore',
    income: 'Income',
    expense: 'Expense',
    transfer: 'Transfer',
    incomePrefix: '[Income] ',
    expensePrefix: '[Expense] ',
  },

  // ─── Legal documents (LegalDocsModal) ───
  legal: {
    tosTitle: 'Terms of Service (Public Offer)',
    privacyTitle: 'Privacy and Data Protection Policy',
    consentTitle: 'Consent to Data Collection and Processing',
    agreeBtn: 'I have read and agree',
  },

  // ─── Help texts ───
  help: {
    contractor: `What is this for? A counterparty is anyone you have financial dealings with.

Until you select their role, the other fields will be locked.
\u2022 Client (Pays you): Linked to your Accounts (income) and Income Categories.
\u2022 Supplier (You pay): Linked to Funds and Expense Categories.

If this is a universal partner (you both buy from and sell to them), you can select both checkboxes at once.`,

    category: `What are categories for?
Categories help analyze the structure of your income and expenses.

\u2022 Income categories: show what exactly clients pay for. Can be linked to a specific Account.
\u2022 Expense categories: show where money goes. Can be linked to a specific Fund.

You can create a category without linking it to a fund or account \u2014 it will then be "General" and available for any operation.
You can also link counterparties to a category to speed up your workflow (they will be suggested first).`,

    uncategorized: `Why is this category called "Uncategorized"?

All operations that were not assigned a specific category at creation are automatically placed here.

How to organize?
Simply click on any operation in the list below \u2014 an editing window will open. Select the desired category there, save, and the operation will automatically move to the correct section!`,

    debtReceivable: `Accounts Receivable (Owed to Us)

Here you record amounts that clients or partners have promised to pay you.
\u2022 You can immediately link the expected income to a specific Account and Category.
\u2022 When the debtor pays, click "Paid" \u2014 the system will automatically create a deposit transaction and close this debt.`,

    debtPayable: `Accounts Payable (We Owe)

Here you record your obligations to suppliers, employees, or partners.
\u2022 Specify the Fund from which you plan to repay the debt, and the Expense Category.
\u2022 When you pay off the debt, click "We paid" \u2014 the system will automatically debit the fund and move the debt to history.`,

    primaryAccount: `What does "Primary" mean?

This account or fund will always be displayed first in lists. It will be suggested by default when adding new income or expenses, to speed up your workflow.`,

    hiddenAccount: `What is a "Hidden account"?

This is your personal stash or reserve.
\u2022 It is not included in the company's total balance (in other tabs).
\u2022 Income deposited to it is not distributed across funds.
\u2022 It does not need income or expense categories.

You can configure privacy: make it visible only to you or to specific roles in the company.`,

    quickIncome: `How does Quick Income work?

1. Debt repayment: If someone is paying a previously created debt, simply select it from the "Close debt" list. The system will automatically fill in all fields (amount, client, category) and close this debt in Liabilities.

2. New payment: If this is new revenue, skip the debt selection. Simply enter the amount, select a category (what was paid for) and a client (who paid).

Important: Money will be automatically distributed across your funds (piggy banks) based on the percentages set for them!`,

    setupAccount: `What is an "Account" in INMONEX?

An account reflects your real bank account or cash register. Money from clients comes in here, and you pay expenses from here.

\u2022 Each account shows a real-time balance
\u2022 Transfers between accounts are also tracked
\u2022 You can add as many accounts as you need

Typical examples:
Business Account \u2014 main account for receiving payments
Savings Account \u2014 for large transactions
Cash \u2014 office petty cash`,

    setupFund: `A fund is a "smart piggy bank" for your business.

Every time a client pays you, the money is automatically split across funds in set proportions.

Why? So you don't fall into the trap of "there's money in the account, but not enough for taxes or salaries."

\u2022 Salaries (50%) \u2014 always know how much you can pay
\u2022 Taxes (20%) \u2014 no surprises from the tax authority
\u2022 Profit (30%) \u2014 your personal business income

Funds don't lock money \u2014 this is virtual accounting. You always see how much "belongs" to each direction.`,

    setupFundLink: `How are Accounts and Funds related?

Account \u2014 WHERE the money is (bank account, cash register)
Fund \u2014 WHAT the money is designated for (salaries, taxes, profit)

Example:
\u2192 A client transferred $10,000 to your account
\u2192 INMONEX automatically records:
   \u2022 $5,000 to the "Salaries" fund
   \u2022 $2,000 to the "Taxes" fund
   \u2022 $3,000 to the "Profit" fund

The money physically stays in the account, but you clearly see how much can be spent from each fund.`,

    debtPayment: `How does liability payment work?

When a client pays you a debt (receivable) or you pay your debt (payable), the system automatically:

Receivable (Payment received):
\u2022 Creates a deposit transaction to the selected account
\u2022 Distributes the amount across funds (according to percentages)
\u2022 Records the comment and category from the liability

Payable (We paid):
\u2022 Creates an expense transaction from the selected account
\u2022 Debits from the specified fund
\u2022 Records the expense category

All data (counterparty, category, comment) is filled automatically from the liability. You only need to confirm and, if necessary, select the account.`,

    debtPartial: `Partial payment \u2014 what is it?

If a client did not pay the full amount but only a part, use "Partial payment":

1. Enter the exact amount paid
2. The system will create a transaction for that amount
3. The remaining debt will be automatically recalculated
4. The payment history will show each payment

Example:
\u2022 Debt: $10,000
\u2022 First payment: $4,000 \u2192 remaining $6,000
\u2022 Second payment: $6,000 \u2192 debt closed

The progress bar will show how much has already been paid. When the total payments reach the full debt amount, the liability will automatically close.`,

    debtViewInfo: `Detailed liability information.

Here you see the full picture: who owes, how much, for what, and the payment status.

\u2022 "Total amount" \u2014 the original liability amount
\u2022 "Paid" \u2014 how much has already been paid (sum of all payments)
\u2022 "Remaining" \u2014 how much still needs to be paid
\u2022 Bindings \u2014 to which account, fund, and category the payment is linked

Use the buttons below to pay, edit, or archive the liability.`,

    perm_manageAccounts: `Account Access:
Allows viewing account balances, creating new accounts, editing their details (IBAN), and archiving them.`,

    perm_manageFunds: `Fund Access:
Allows creating virtual piggy banks (funds), setting distribution percentages and target amounts, and archiving unused funds.`,

    perm_manageCategories: `Category Management:
Allows creating and editing income and expense categories, linking them to specific accounts or funds for accounting automation.`,

    perm_manageContractors: `Counterparty Management:
Grants access to create and edit the client and supplier database, view their financial history, and manage category links.`,

    perm_manageOperations: `Operation Actions:
Allows recording new income, expenses, and transfers, as well as undoing recent actions and editing transactions.`,

    perm_manageDebts: `Liability Management:
Allows recording debts (receivables and payables), issuing invoices, and marking them as paid with automatic transaction creation.`,
  },

  // ─── Organization selection (NEW) ───
  orgSelect: {
    welcome: (name) => `Welcome, ${name}`,
    choose: 'Select organization',
    create: 'Create organization',
    noOrgs: 'You don\'t have access to any organizations yet. Contact the organization owner to get access.',
  },

  // ─── Accounts & Funds Tab (AccountsFundsTab) ───
  af: {
    // Tabs
    tabSummary: 'Summary',
    tabAccounts: 'Accounts',
    tabFunds: 'Funds',
    tabArchive: (count) => `Archive (${count})`,
    // Stat cards
    onAccounts: 'On accounts',
    inFunds: 'In funds',
    incomeMonth: 'Income (monthly)',
    expenseMonth: 'Expense (monthly)',
    // Operation types
    transferFundShort: 'Fund transfer',
    // Cash gap
    cashGapTitle: 'Cash gap (deficit)!',
    cashGapDesc: (fundTotal, accTotal) => `Fund total (${fundTotal}) exceeds actual cash on accounts (${accTotal}). Please verify balances.`,
    // States
    accountsState: 'Account status',
    fundsState: 'Fund status',
    adjustFundsTitle: 'Adjust balances',
    adjustTitle: 'Fund balance adjustment',
    adjustSubtitle: 'Manually change each fund balance',
    adjustTotalAccounts: 'Total in accounts',
    adjustTotalFunds: 'Total in funds',
    adjustBalanced: '✓ Balances match',
    adjustNeedAdd: 'Still need to distribute',
    adjustNeedRemove: 'Funds exceed accounts',
    adjustSave: 'Save',
    adjustAuditDesc: 'Manual fund balance adjustment',
    // FundManager
    fmTitle: 'Fund Management',
    fmSubtitle: 'Percentages, balances, order, adding and removing',
    fmHelp: 'Manage funds: change percentages, balances, order. Add and remove funds with redistribution.',
    fmTogglePercent: 'Edit percentages',
    fmToggleBalance: 'Edit balances',
    fmTogglePctHint: 'Percentages only affect distribution of new income',
    fmToggleBalHint: 'Change current balance of each fund',
    fmFundName: 'Name',
    fmBalance: 'Balance',
    fmDeleteFund: 'Delete fund',
    fmAddFund: 'Add fund',
    fmInfoAccounts: 'Total in accounts',
    fmInfoFunds: 'Total in funds',
    fmInfoBalanced: '✓ Balanced',
    fmInfoDiff: 'Difference',
    fmPctTotal: 'Percentages',
    fmAddStep1: 'New fund',
    fmAddStep2: 'Where to take percentages from?',
    fmAddStep2Hint: 'Specify how many percent to take from each existing fund',
    fmAddNeedTake: 'Need to take',
    fmAddTaken: 'Specified',
    fmAddBack: 'Back',
    fmAddConfirm: 'Add',
    fmAddNext: 'Next',
    fmAddPctMismatch: (need, taken) => `Need to distribute ${need}%, specified ${taken}%`,
    fmDelTitle: name => `Delete fund "${name}"`,
    fmDelStep1Hint: pct => `Distribute ${pct}% among remaining funds`,
    fmDelAutoDistribute: 'Distribute proportionally',
    fmDelStep2Hint: bal => `Where to transfer ${bal}?`,
    fmDelWriteOff: 'Write off (zero out)',
    fmDelConfirm: 'Delete',
    fmDelPctMismatch: (need, taken) => `Need to distribute ${need}%, specified ${taken}%`,
    fmValPctNot100: pct => `Total percentages: ${pct}%. Must be 100%.`,
    fmValPctAction: d => `Add a new fund for ${d}% or distribute ${d}% among existing funds.`,
    fmValPctOverAction: d => `Remove excess ${d}% from existing funds.`,
    fmValFundsExceed: 'Fund total exceeds account total. Reduce balances.',
    fmValBalMismatch: diff => `Difference between accounts and funds: ${diff}. Fund balances must equal account balances.`,
    fmAddFreePct: pct => `${pct}% free — not allocated to any fund`,
    fmAddNoFreePct: 'All 100% allocated. Percentages for the new fund will be taken from existing ones.',
    fmAddHasFree: pct => `You have ${pct}% free (unallocated).`,
    fmAddNeedFromOthers: (total, need) => `New fund: ${total}%. Need to take from other funds: ${need}%.`,
    fmAuditAdjust: 'Fund adjustment',
    fmSave: 'Save',
    fundNamePlaceholder: 'Fund name',
    // Duplicates
    dupAccName: 'An account with this name already exists',
    dupAccIban: 'An account with this IBAN already exists',
    dupFundName: 'A fund with this name already exists',
    dupFundNameSave: 'Multiple funds with the same name. Please rename.',
    // Empty lists
    noActiveAccounts: 'No active accounts',
    noActiveFunds: 'No active funds',
    noActiveDebts: 'No active debts',
    noOps: 'No operations',
    // Placeholders
    accountNamePlaceholder: 'Account name',
    ibanPlaceholder: 'IBAN (Required)',
    fundNamePlaceholder: 'Name',
    // Tooltips
    ibanTooltip: 'IBAN is required for automatic account recognition during PDF import.',
    // Badges
    primaryBadge: 'Primary',
    hiddenAccountTitle: 'Hidden account',
    // Button titles
    addToStash: 'Top up stash',
    takeFromStash: 'Take from stash',
    quickIncomeTitle: 'Quick income',
    quickExpenseTitle: 'Quick expense',
    historyBtn: 'History',
    // Debts (panels)
    receivableLabel: 'Receivables',
    payableLabel: 'Payables',
    theyOweUs: 'Owed to us',
    weOwe: 'We owe',
    dueDate: (date) => `Due: ${date}`,
    // "Future fund replenishment" modal (click on the receivable total)
    futureFundsTitle: 'Future fund replenishment',
    futureFundsHint: 'This money hasn\u2019t arrived yet \u2014 projection of how it would be split across funds if debtors pay in full',
    futureFundsNoDebts: 'No active debts',
    futureFundsNoFunds: 'No active funds',
    // Recent
    recentOps: 'Recent operations',
    // Alerts
    enterAccountName: 'Enter account name',
    enterIban: 'Enter the IBAN. This is required for automatic account recognition during PDF import.',
    exceedsHundred: 'Will exceed 100%',
    // Confirm
    archiveAccountMsg: (name) => `Hide account "${name}" to archive?`,
    archiveAccountExtra: 'This account will disappear from selection lists, but all associated past operations will remain unchanged in reports. You can restore it from the Unified Archive.',
    archiveFundMsg: (name) => `Archive fund "${name}"?`,
    archiveFundExtra: 'The fund will be hidden, but all its operations will remain in history. You can restore it from the Unified Archive.',
    defaultCompanyName: 'Our company',
    // Account modal (AccountModal)
    accModalEdit: 'Edit',
    accModalArchive: 'Archive',
    accModalPrimaryLabel: 'Primary account',
    accModalHiddenLabel: 'Hidden account (Personal)',
    addAccPrimaryHint: 'The primary account is used by default when creating operations. Only one can be primary.',
    addAccHiddenHint: 'Hidden account is excluded from total balance and statistics. Suitable for personal savings or stash.',
    accModalVisibilityLabel: 'Who can see this account?',
    accModalVisMe: 'Only me (owner)',
    accModalVisMeAccountant: 'Me and accountant',
    accModalVisAdmins: 'Me, accountant and administrator',
    accModalVisAll: 'Entire team',
    accModalIbanLabel: 'IBAN (Required)',
    accModalIbanDisplay: 'IBAN:',
    accModalBalance: (bal) => `Balance: ${bal}`,
    accModalVisibilityDisplay: 'Visibility:',
    accModalEnterName: 'Enter account name',
    accModalEnterIban: 'Enter the IBAN. This is required for automatic account recognition.',
    accModalIncomeCategories: 'Income categories',
    accModalAddBtn: 'Add',
    accModalNoFreeCategories: 'No available categories',
    accModalNoLinkedCategories: 'No linked categories',
    accModalRemoveBtn: 'Remove',
    accModalClients: 'Clients',
    accModalAllClientsLinked: 'All clients are linked',
    accModalNoLinkedClients: 'No linked clients',
    // Fund modal (FundModal)
    fundModalExceedsHundred: 'Total fund percentages will exceed 100%',
    fundModalBelowHundred: (total, missing) => `Total percentages = ${total}%. Must be exactly 100%. Missing ${missing}%`,
    fundModalInFund: (bal) => `In fund: ${bal}`,
    fundModalExpenseCategories: 'Expense categories',
    fundModalNoFreeCategories: 'No available categories',
    fundModalNoLinkedCategories: 'No linked categories',
    fundModalRemoveBtn: 'Remove',
    // Quick income (QuickIncomeModal)
    qIncTitle: 'Quick income',
    qIncAccountLabel: 'Credit account',
    qIncCurrentBalance: 'Current balance',
    qIncDebtLabel: '1. Debt repayment (Optional)',
    qIncNewRevenue: '-- This is new revenue (not a debt) --',
    qIncNoDebtors: 'No active debtors',
    qIncAmountLabel: 'Income amount *',
    qIncCategoryLabel: 'Category (What for?)',
    qIncNoCategory: 'Uncategorized',
    qIncClientLabel: 'Client (Who is paying?)',
    qIncClientNone: 'Not specified',
    qIncCommentLabel: 'Comment',
    qIncCommentPlaceholder: 'Payment details...',
    qIncSubmit: 'Credit',
    qIncDebtPayComment: 'Debt payment',
    qIncEnterAmount: 'Enter a valid amount',
    qIncRemainder: (amount) => `remaining: ${amount}`,
    // Quick expense (QuickExpenseModal)
    qExpTitle: 'Quick expense',
    qExpFundLabel: 'Debit fund',
    qExpFundAvailable: 'Available in fund',
    qExpPayDebtLabel: 'Pay liability',
    qExpNoDebtBinding: '— No liability binding —',
    qExpDebtDefault: 'Liability',
    qExpDebtRemainder: (amount) => `remaining: ${amount}`,
    qExpAccountLabel: 'Debit account (where the actual money leaves)',
    qExpCategoryLabel: 'Expense category',
    qExpNoCategory: 'Uncategorized',
    qExpAmountLabel: 'Expense amount',
    qExpCommentLabel: 'Comment',
    qExpCommentPlaceholder: 'To whom, what for...',
    qExpSubmit: 'Debit',
    qExpEnterAmount: 'Enter amount',
    qExpFundInsufficient: (fundName, available) => `Insufficient funds in "${fundName}". Available: ${available}`,
    qExpAccountInsufficient: (available) => `Insufficient funds on the selected account. Available: ${available}`,
    qExpPayDebtComment: 'Liability payment',
    // Hidden account (QuickHiddenAdjustModal)
    qHidAddTitle: 'Top up stash',
    qHidTakeTitle: 'Take from stash',
    qHidAccountLabel: 'Hidden account',
    qHidCurrentBalance: 'Currently in stash',
    qHidAddAmountLabel: 'Top-up amount',
    qHidTakeAmountLabel: 'Withdrawal amount',
    qHidCommentLabel: 'Comment (for yourself)',
    qHidAddPlaceholder: 'Saved from bonus...',
    qHidTakePlaceholder: 'Spent on a gift...',
    qHidSubmitAdd: 'Top up',
    qHidSubmitTake: 'Withdraw',
    qHidEnterAmount: 'Enter amount',
    qHidInsufficient: (available) => `Insufficient funds. In stash: ${available}`,
    qHidDefaultCommentAdd: 'Stash top-up',
    qHidDefaultCommentTake: 'Stash withdrawal',
    // Fund → Hidden transfer (FundToHiddenTransferModal)
    qFTHTitle: 'Transfer to stash',
    qFTHFundLabel: 'From fund',
    qFTHAccountLabel: 'From account',
    qFTHHiddenLabel: 'To stash (hidden account)',
    qFTHAmountLabel: 'Amount',
    qFTHCommentLabel: 'Comment (for yourself)',
    qFTHCommentPlaceholder: 'For personal expenses...',
    qFTHSubmit: 'Transfer',
    qFTHEnterAmount: 'Enter amount',
    qFTHFundInsufficient: (fundName, available) => `Insufficient funds in "${fundName}". Available: ${available}`,
    qFTHAccountInsufficient: (available) => `Insufficient funds on the selected account. Available: ${available}`,
    qFTHNoHidden: 'Create a hidden account first',
    qFTHButtonTitle: 'Transfer to stash',
    qFTHDefaultComment: 'Transfer to stash',
    qFTHHint: 'For the fund this is a debit (expense), the amount will not appear in income reports.',
    // Help tooltips
    helpAccount: 'An account is your bank account, cash register, or wallet. Money flows into it and expenses are made from it. You can link income categories and counterparties to an account.',
    helpFund: 'A fund is a virtual savings jar. With each income, money is automatically distributed to funds according to percentages. You can link expense categories to a fund.',
  },

  // ─── PeriodFilter (shared component) ───
  periodFilter: {
    label: 'Period:',
    d30: '30 days',
    m3: '3 months',
    m6: '6 months',
    y1: '1 year',
    all: 'All time',
    custom: 'Custom range',
    from: 'from',
    to: 'to',
    contractorsLabel: 'Contractor totals for:',
    categoriesLabel: 'Category totals for:',
  },

  // ─── Multi-org reports (NEW) ───
  multiOrg: {
    title: 'Organizations Summary',
    periodLabel: (period, count) => `${period} \u00B7 ${count} organizations`,
    totalIncome: 'TOTAL INCOME',
    totalExpense: 'TOTAL EXPENSE',
    netProfit: 'NET PROFIT',
    onAccounts: 'ON ACCOUNTS',
    comparison: 'Organization Comparison',
    thOrg: 'ORGANIZATION',
    thIncome: 'Income',
    thExpense: 'Expense',
    thProfit: 'Profit',
    thBalance: 'Balance',
    thOps: 'Operations',
    incomeVsExpense: 'Income vs Expense',
    incomeShare: 'Income share',
    topExpenses: 'Top expense categories',
    noExpenses: 'No expenses',
    periodMonth: 'Month',
    periodQuarter: 'Quarter',
    periodYear: 'Year',
    needMinOrgs: 'At least 2 organizations are needed for comparison',
    addOneMoreOrg: 'Add one more organization to view comparative analytics',
    currentMonth: 'Current month',
    currentQuarter: 'Current quarter',
    currentYear: 'Current year',
    noIncomeForPeriod: 'No income data for the period',
    incomeBarName: 'Income',
    expenseBarName: 'Expense',
  },

  subscription: {
    useBonuses: 'Use bonuses',
    bonusAvailable: (amount) => `Available: ${amount}`,
    basePrice: 'Base price',
    bonusDiscount: 'Bonuses',
    finalPrice: 'To pay',
    title: 'Subscription',
    subtitle: 'Manage your subscription and payments',
    planTab: 'Plan',
    historyTab: 'Payment History',
    statusTrial: 'Trial Period',
    statusActive: 'Subscription Active',
    statusExpired: 'Subscription Expired',
    daysLeft: (n) => `${n} days left`,
    expiresAt: 'until',
    expiredHint: 'Creating and editing data is blocked. Please subscribe.',
    pricingTitle: 'Subscription Pricing',
    firstOrg: 'First organization',
    extraOrg: 'Each additional',
    month: 'mo',
    yourOrgs: (n) => `Your organizations (${n})`,
    choosePeriod: 'Choose period',
    month1: '1 month',
    month3: '3 months',
    month6: '6 months',
    month12: '12 months',
    monthN: (n) => `${n} mo.`,
    totalFor: (m) => `for ${m} mo.`,
    payBtn: 'Pay',
    payDisabledHint: 'Payment is temporarily unavailable. To activate, contact the administrator: inmonex@support.kz',
    historyEmpty: 'No payments',
    monthsLabel: 'mo.',
    paymentSuccess: 'Paid',
    paymentPending: 'Pending',
    paymentFailed: 'Failed',
    expiringBanner: (days) => `Trial period ends in ${days} days. Subscribe now!`,
    expiredBanner: 'Subscription expired. Subscribe to continue working.',
    renewBtn: 'Subscribe',
    modalTitle: 'Subscription Expired',
    modalText: 'An active subscription is required to create and edit data. All your data is saved and available for viewing.',
    modalBtn: 'Subscribe',
    selectOrgs: 'Select organizations for subscription',
    selectOrgsHint: 'First organization — 5,500 ₸/mo. Each additional — 3,500 ₸/mo.',
    selectedOrgs: (n) => `Selected organizations: ${n}`,
    savings: (s) => `Savings: ${s}`,
    selectAtLeast1: 'Select at least 1 organization',
  },
  deletion: {
    deleteOrg: 'Delete organization',
    deleteOrgConfirm: (name) => `Are you sure you want to delete "${name}"? All data will be permanently lost.`,
    deleteOrgHint: 'Deleting the organization will permanently erase all data: accounts, funds, operations, transactions and history. We recommend creating a backup before deletion.',
    deleteAccount: 'Delete account',
    deleteAccountConfirm: 'Are you sure you want to delete your account? All organizations and data will be permanently lost.',
    backupTitle: 'Backup',
    backupOrgText: 'We recommend saving a backup of the organization data before deleting.',
    backupAccountText: 'We recommend saving a backup of all your data before deleting your account.',
    downloadBackup: 'Download backup',
    backupDownloaded: 'Backup saved',
    deleteWithoutBackup: 'Delete without backup',
    confirmDelete: 'Confirm deletion',
    deleting: 'Deleting...',
    orgDeleted: 'Organization deleted',
    accountDeleted: 'Account deleted',
    backupOrg: 'Organization backup',
    backupAll: 'Full data backup',
    step1of2: 'Step 1 of 2',
    step2of2: 'Step 2 of 2',
  },
};


// ══════════════════════════════════════════════════════════
// Реестр всех языков
// ══════════════════════════════════════════════════════════
const STRINGS = { ru, kz, en };

export default STRINGS;
